// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * Liveness answer (M0 shape, 2026-08-02): `status` is `ok` by construction — the process answered — enriched with the serving node, the platform version and the server clock so `tvstor system-info` and the dev rig have one honest probe. PROPOSAL: a `degraded` value may join the enum with the M1 readiness round, as in the TVirt sibling.
 *
 */
export const zSystemHealth = z.object({
    status: z.enum([
        'ok'
    ]),
    node: z.string(),
    version: z.string(),
    time: z.iso.datetime()
});

export const zIdentifyRequest = z.object({
    identifier: z.string().max(320)
});

/**
 * Step 1 of the identifier-first login (TVirt pattern, adopted 2026-08-02): the realm is READ FROM THE IDENTIFIER the user typed and the client is told HOW to authenticate against it — the candidate set is the cluster realm plus the two reserved realms `local` and `files`. Since #118 NAS-QUALNAMES (ratifica Luca 2026-08-14 §2) the realm is never DERIVED: the identifier must carry it (`ROMA-1\mario.rossi`, `mario.rossi@roma-1`, `local\root`, `mario@files`) and a bare name answers `422 validation` teaching the two forms. It deliberately says NOTHING about whether an account exists — an unknown user gets the very same answer, and only the submit in step 2 can fail. The DECLARED DEVIATION of 2026-08-06 (a bare identifier resolving to `files` necessarily revealed that a file-services account by that name existed) DIED WITH THE PRECEDENCE: step 1 is uninformative again for every realm.
 *
 */
export const zIdentifyResult = z.object({
    realm: z.string(),
    method: z.enum([
        'password'
    ]),
    realmKind: z.optional(z.enum([
        'local',
        'cluster',
        'files',
        'directory'
    ]))
});

export const zFieldError = z.object({
    field: z.string(),
    message: z.string()
});

/**
 * RFC 9457 problem document with TVStor extensions.
 */
export const zProblem = z.object({
    type: z.optional(z.string()),
    title: z.string(),
    status: z.int(),
    detail: z.optional(z.string()),
    code: z.enum([
        'invalid_argument',
        'unauthenticated',
        'permission_denied',
        'sudo_required',
        'password_change_required',
        'not_found',
        'conflict',
        'in_use',
        'metadata_lease_held',
        'last_admin_protection',
        'retention_locked',
        'retention_active',
        'retention_frozen',
        'qos_not_supported',
        'quota_scope_not_supported',
        'previous_versions_not_supported',
        'id_range_conflict',
        'domain_identity_conflict',
        'protocol_model_mismatch',
        'acl_not_representable',
        'acl_not_readable',
        'node_unreachable',
        'precondition_failed',
        'validation',
        'immutable_field',
        'rate_limited',
        'internal',
        'store_unavailable',
        'task_backlog',
        'insufficient_storage',
        'replica_promoted',
        'capacity_exceeded',
        'capacity_not_observed',
        'volume_not_materialized',
        'mds_standby_exhausted',
        'pg_budget_exhausted',
        'fs_timeout',
        'mfa_required',
        'mfa_unavailable',
        'mfa_not_configured',
        'mfa_enrollment_required',
        'directory_unreachable',
        'directory_login_not_supported',
        'worm_not_supported',
        'mirror_peer_conflict',
        'mirror_not_supported',
        'mirror_owned',
        'catalog_disabled',
        'catalog_unavailable',
        'catalog_scan_running',
        'catalog_mode_not_supported',
        'catalog_cursor_invalid',
        'catalog_source_not_mounted',
        'catalog_source_path_reserved',
        'audit_retention_backwards',
        'metrics_token_missing',
        'syslog_sink_unverifiable',
        'audit_tail_unavailable',
        'scope_quota_exceeded',
        'maintenance_refused',
        'volume_exported',
        'enclosure_head_alive',
        'enclosure_not_owner',
        'step_failed'
    ]),
    retryable: z.optional(z.boolean()),
    leaseHolder: z.optional(z.object({
        task: z.string(),
        family: z.union([
            z.string(),
            z.null()
        ]),
        type: z.string()
    })),
    errors: z.optional(z.array(zFieldError))
});

export const zSessionUser = z.object({
    username: z.string(),
    realm: z.string(),
    displayName: z.string()
});

/**
 * One share of a kiosk session's perimeter (WEBEXP-KIOSK, ratifica Luca 2026-08-05). `writable` mirrors the server-side write rule (PROPOSAL 2026-08-06): the fileUser may write iff AT LEAST ONE protocol block listing it is not readOnly and carries no per-user readOnly mark.
 *
 */
export const zKioskShare = z.object({
    id: z.uuid(),
    name: z.string(),
    writable: z.boolean()
});

/**
 * A session on the answering node (sessions are node-local — TVirt API spec §3 heritage, adopted 2026-08-02). Scoped semantics (TVirt #24/#25, adopted 2026-08-02): a session inherits the SCOPE of its owner — the caller always reaches their own sessions, and reaches another principal's only with `user.sessions.revoke` granted on that principal's scope.
 *
 */
export const zSession = z.object({
    id: z.uuid().readonly(),
    user: zSessionUser,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    active: z.boolean().readonly(),
    mustChangePassword: z.optional(z.boolean()),
    mfaEnrollmentDeadline: z.optional(z.iso.datetime()),
    mfaEnrollmentDaysLeft: z.optional(z.int()),
    roles: z.optional(z.array(z.string()).readonly()),
    mode: z.optional(z.enum([
        'console',
        'kiosk'
    ])),
    kioskShares: z.optional(z.array(zKioskShare))
});

export const zSessionList = z.object({
    items: z.array(zSession),
    nextCursor: z.optional(z.string())
});

export const zSessionCreated = zSession.and(z.object({
    token: z.string()
}));

export const zSudoGrant = z.object({
    sudoToken: z.string(),
    expiresInSeconds: z.int()
});

/**
 * One entry of the hardcoded permission catalog.
 */
export const zPermissionInfo = z.object({
    name: z.string(),
    description: z.optional(z.string())
});

export const zPermissionList = z.object({
    items: z.array(zPermissionInfo)
});

/**
 * One tag value attached to this resource. `tag` is the BARE name and `scope` the tag's scope: together they are the qualified form `/scope/name` that disambiguates homonyms living in different scopes. `id` is the assignment handle — pass it to the tag-assignments delete to detach without a lookup.
 *
 */
export const zResourceTag = z.object({
    id: z.uuid(),
    tag: z.string(),
    scope: z.string(),
    value: z.string(),
    tagId: z.optional(z.uuid()),
    valueId: z.optional(z.uuid())
});

/**
 * Fields shared by every flat resource (TVirt ADR-22, adopted 2026-08-02). The kind's identifier field (`name`, `username` for users) is declared per kind. Writes are last-write-wins (no client-side optimistic locking). RESPONSE-REQUIRED RULE (#195, 2026-08-21 — reperti R1/R5 of #183): resource schemas declare `id` and — where every render carries it — `scope` as `required`, because the wire ALWAYS sends them on responses (every backend model renders `id: r.id` and `scope: r.scopePath`); before this the generated client types said `id?: string` about a field that never misses, and api-js had to re-strengthen 25 schemas at runtime. Flat resources compose the `RenderedResource` schema below next to this one; kinds that declare their own `id` (Grant, ClusterNode, NodeRaid, NodeVdisk, VolumeSnapshot, Bucket, S3Account) list it in their own `required` directly. On REQUESTS those entries do not bind: `id` is `readOnly` (the OpenAPI 3.0 semantics of readOnly+required, kept deliberately under 3.1) and `scope` is marked `x-tvstor-request-optional` (writable at creation but defaulted server-side, so a create may omit it) — the backend's request validator strips both classes from `required` before compiling (http/validation.ts).
 *
 */
export const zResourceCommon = z.object({
    id: z.optional(z.uuid().readonly()),
    scope: z.optional(z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/)),
    tags: z.optional(z.array(zResourceTag).readonly()),
    notes: z.optional(z.string().max(65536)),
    createdAt: z.optional(z.iso.datetime().readonly()),
    updatedAt: z.optional(z.iso.datetime().readonly())
});

/**
 * The response-required half of a rendered flat resource (#195, 2026-08-21 — reperti R1/R5 of #183; see the rule on ResourceCommon): composed via allOf NEXT TO ResourceCommon by every resource whose render always carries `id` and `scope`. A SEPARATE schema, not a `required` on ResourceCommon, because the Cluster singleton and the root Scope legitimately omit them; and `id`/`scope` are REDECLARED here (types only — the full constraints stay on ResourceCommon and still bind, the fields being always present) because every generator in the toolchain honors `required` only for properties declared at the same object level.
 *
 */
export const zRenderedResource = z.object({
    id: z.uuid().readonly(),
    scope: z.string()
});

/**
 * A user account. `username` is the kind's identifier field.
 */
export const zUser = zResourceCommon.and(zRenderedResource).and(z.object({
    username: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/),
    displayName: z.optional(z.string()),
    enabled: z.optional(z.boolean()).default(true),
    mustChangePassword: z.optional(z.boolean()).default(false),
    hasPassword: z.optional(z.boolean().readonly()),
    lastLoginAt: z.optional(z.iso.datetime().readonly()),
    lastLoginFrom: z.optional(z.string().readonly()),
    mfaEnrolled: z.optional(z.boolean().readonly()),
    mfaEnrolledAt: z.optional(z.iso.datetime().readonly()),
    mfaExternal: z.optional(z.boolean()).default(false),
    mfaRequired: z.optional(z.boolean()).default(false),
    mfaRequiredAt: z.optional(z.iso.datetime().readonly()),
    failedAttempts: z.optional(z.int().readonly()),
    lockedUntil: z.optional(z.iso.datetime().readonly())
}));

export const zUserList = z.object({
    items: z.array(zUser),
    nextCursor: z.optional(z.string())
});

/**
 * Bulk delete (TVirt heritage, 2026-08-02): the ids to remove. Partial success is normal — see `BatchDeleteResult`; the endpoint applies the SAME per-item guards as the single delete.
 *
 */
export const zBatchDeleteRequest = z.object({
    ids: z.array(z.string()).min(1).max(500)
});

/**
 * Per-item outcome of a bulk delete; the endpoint returns `200` even on partial refusal.
 */
export const zBatchDeleteResult = z.object({
    deleted: z.array(z.string()),
    refused: z.array(z.object({
        id: z.string(),
        code: z.string(),
        detail: z.string()
    }))
});

/**
 * The enrolment material (#74 MFA-1, 2026-08-18), returned EXACTLY ONCE by `actions/enrollTotp` — the server keeps the secret sealed in the keystore (kind `userTotp`, envelope-encrypted like every secret) and the recovery codes only as hashes: nothing here can be fetched again. Losing this response before confirming simply means enrolling again (a pending enrolment is replaced wholesale).
 *
 */
export const zTotpEnrollment = z.object({
    secret: z.string(),
    otpauthUri: z.string(),
    recoveryCodes: z.array(z.string())
});

export const zTotpConfirmRequest = z.object({
    otp: z.string().max(64)
});

/**
 * A node of the namespace. Creating `{scope: "/roma-1", name: "tenant-web"}` makes `/roma-1/tenant-web` available for resources, RBAC bindings and delegation.
 *
 */
export const zScope = zResourceCommon.and(z.object({
    id: z.uuid().readonly(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    quota: z.optional(z.union([
        z.object({
            capacityBytes: z.optional(z.int().gte(1073741824))
        }),
        z.null()
    ])),
    promisedBytes: z.optional(z.int().readonly()),
    promisedNetBytes: z.optional(z.int().readonly()),
    admittableBytes: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    resourceCounts: z.optional(z.object({
        volumes: z.optional(z.int()),
        fileShares: z.optional(z.int()),
        objectStores: z.optional(z.int()),
        subScopes: z.optional(z.int())
    }).readonly())
}));

export const zScopeList = z.object({
    items: z.array(zScope),
    nextCursor: z.optional(z.string())
});

/**
 * ENEA-B-R15: one address the tenant is served on — a floating gateway address assigned to the scope (ceph, `kind: gateway`, with its current holder) or the dedicated service address of one of its shares/stores (multiscsi, `kind: service`).
 *
 */
export const zScopeReportAddress = z.object({
    address: z.string(),
    kind: z.enum([
        'gateway',
        'service'
    ]),
    clusterId: z.uuid(),
    clusterName: z.string(),
    scope: z.union([
        z.string(),
        z.null()
    ]),
    holder: z.union([
        z.string(),
        z.null()
    ]),
    resourceKind: z.nullable(z.enum([
        'volume',
        'fileShare',
        'objectStore'
    ])),
    resourceId: z.union([
        z.string(),
        z.null()
    ]),
    qos: z.union([
        z.record(z.string(), z.union([
            z.int(),
            z.null()
        ])),
        z.null()
    ])
});

/**
 * ENEA-B-R15 «tenant» (2026-10-24, disegno §2.3): one resource with capacity in the scope's subtree — a volume, a file share or an object store — with its raw and net promise, its observed usage (null = not observed: block volumes have no usage observation), its QoS (ENEA-B-R14) when set, its audit switch when the kind has one (null on a volume), and the TENANT addresses that announce it. The CSV and JSONL exports stream exactly these rows.
 *
 */
export const zScopeReportRow = z.object({
    kind: z.enum([
        'volume',
        'fileShare',
        'objectStore'
    ]),
    id: z.uuid(),
    name: z.string(),
    scope: z.string(),
    clusterId: z.uuid(),
    clusterName: z.string(),
    clusterKind: z.enum([
        'ceph',
        'multiscsi'
    ]),
    sizeGiB: z.int(),
    redundancy: z.string(),
    promisedBytes: z.int(),
    promisedNetBytes: z.int(),
    usedBytes: z.union([
        z.int(),
        z.null()
    ]),
    qos: z.union([
        z.record(z.string(), z.union([
            z.int(),
            z.null()
        ])),
        z.null()
    ]),
    audit: z.union([
        z.boolean(),
        z.null()
    ]),
    addresses: z.array(z.string()),
    phase: z.string()
});

/**
 * ENEA-B-R15 «tenant» (2026-10-24, disegno §2.3): THE REPORT OF A SCOPE AND ITS DESCENDANTS — the quota and the promises (raw weighted and net), the observed usage (summed where observed; `unobserved` counts the rows without one), the counts by kind, the tenant's addresses, the QoS and audit tallies and one row per resource. Read from the store and the status rows the renders already use — no call to the nodes.
 *
 */
export const zScopeReport = z.object({
    scope: z.object({
        id: z.uuid(),
        path: z.string(),
        name: z.string()
    }),
    generatedAt: z.iso.datetime(),
    quota: z.union([
        z.object({
            capacityBytes: z.optional(z.int())
        }),
        z.null()
    ]),
    promised: z.object({
        bytes: z.int(),
        netBytes: z.int()
    }),
    admittableBytes: z.union([
        z.int(),
        z.null()
    ]),
    used: z.object({
        bytes: z.union([
            z.int(),
            z.null()
        ]),
        observed: z.int(),
        unobserved: z.int()
    }),
    counts: z.object({
        volumes: z.int(),
        fileShares: z.int(),
        objectStores: z.int(),
        buckets: z.int(),
        subScopes: z.int(),
        clusters: z.int()
    }),
    addresses: z.array(zScopeReportAddress),
    qos: z.object({
        fileSharesWithQos: z.int(),
        objectStoresWithQos: z.int(),
        gatewayAddressesWithQos: z.int()
    }),
    audit: z.object({
        fileSharesAudited: z.int(),
        objectStoresAudited: z.int()
    }),
    resources: z.array(zScopeReportRow)
});

/**
 * A named, scoped aggregate of permissions and/or other roles (hierarchy): the effective permission set is the transitive union. At least one of `permissions` and `roles` MUST be present. Names with the reserved `tvstor-` prefix are system roles: present in every deployment, neither creatable nor modifiable nor deletable (mutations return `422 immutable_field`).
 *
 */
export const zRole = zResourceCommon.and(zRenderedResource).and(z.intersection(z.unknown(), z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    permissions: z.optional(z.array(z.string()).min(1)),
    roles: z.optional(z.array(z.string()).min(1))
})));

export const zRoleList = z.object({
    items: z.array(zRole),
    nextCursor: z.optional(z.string())
});

export const zGrant = z.intersection(z.unknown(), z.object({
    id: z.uuid().readonly(),
    user: z.optional(z.string().regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?@[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)),
    group: z.optional(z.string()),
    role: z.string(),
    grantRight: z.optional(z.boolean()).default(false),
    scope: z.string().max(257).regex(/^(\/\*|(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){1,8}(\/\*)?|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/\*)?)$/),
    createdAt: z.optional(z.iso.datetime().readonly()),
    createdBy: z.optional(z.string().readonly()),
    effective: z.optional(z.boolean().readonly())
}));

export const zGrantList = z.object({
    items: z.array(zGrant),
    nextCursor: z.optional(z.string())
});

/**
 * One member of the store (M2f, 2026-08-03): its declared membership plus the replication state observed on the primary. Pure observation for everything but `node`/`role` — never a declaration.
 *
 */
export const zClusterStoreMember = z.object({
    node: z.optional(z.uuid()),
    name: z.optional(z.string()),
    role: z.optional(z.enum([
        'primary',
        'secondary',
        'none'
    ])),
    endpoint: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    slot: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    inAckSet: z.optional(z.union([
        z.boolean().readonly(),
        z.null()
    ]).readonly()),
    fenced: z.optional(z.boolean().readonly()),
    syncState: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    lagBytes: z.optional(z.union([
        z.number(),
        z.null()
    ])),
    lagSeconds: z.optional(z.union([
        z.number(),
        z.null()
    ])),
    observedAt: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ]))
});

/**
 * The CLUSTER STORE's high-availability state (M2f, dettatura Luca 2026-08-03, 8° giro — YugabyteDB DROPPED). The store is PostgreSQL MANAGED BY US at EVERY scale: one primary plus N secondaries in streaming replication, worker-supervised detached instances (Annex M pattern: pidfile + logfile under `<systemPath>/store/`). ACK POLICY (RATIFICATA Luca 2026-08-03, 9° giro — supersedes the M2f `syncPolicy` PROPOSAL): a config commit is confirmed only when replicated on ALL live, non-excluded secondaries — the ACK SET, rendered onto the primary as `FIRST <n>` over every set member. A fallen secondary is FENCED and EXCLUDED (fence-before-degrade on the control plane; see `degradedReason`), and a member re-enters the set only once caught up (`members[].inAckSet` / `members[].fenced`). There is no engine switch any more — growing from 2 to 3 nodes is "add a secondary" (`addStoreMember`), and the quorum deciding a PROMOTION stays the PLATFORM's (witness grant or verified fencing cascade), never the DB's. Everything here is OBSERVED — since the 9° giro the block carries no writable field (the retired `syncPolicy` write took the `cluster.store.update` permission with it). READ GUARD (PROPOSAL 2026-08-03, pending ratification): rendering this block requires `cluster.store.read` on top of the operation's `cluster.read` — endpoints, slots and lag describe the store's topology.
 *
 */
export const zClusterStore = z.object({
    primary: z.optional(z.union([
        z.uuid().readonly(),
        z.null()
    ]).readonly()),
    members: z.optional(z.array(zClusterStoreMember).readonly()),
    synchronousStandbyNames: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    degraded: z.optional(z.boolean().readonly()),
    degradedReason: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly())
});

export const zRollingUpdateNode = z.object({
    name: z.string(),
    state: z.enum([
        'pending',
        'gating',
        'draining',
        'awaitingReturn',
        'undraining',
        'done',
        'failed',
        'skipped'
    ]),
    since: z.iso.datetime(),
    detail: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * ENEA-A-R16 (2026-10-24): the persisted state of a rolling update — the cursor into the plan, every node's step, whether it is paused, and where it stopped (the first red gate, a failed drain/undrain, an expired wait, or the operator's abort).
 *
 */
export const zRollingUpdateState = z.object({
    taskId: z.uuid(),
    phase: z.string(),
    cursor: z.int(),
    nodes: z.array(zRollingUpdateNode),
    paused: z.boolean(),
    stoppedAt: z.union([
        z.object({
            node: z.optional(z.string()),
            gate: z.optional(z.string()),
            detail: z.optional(z.string())
        }),
        z.null()
    ]),
    plan: z.object({
        nodes: z.array(z.string()),
        expectedVersion: z.union([
            z.string(),
            z.null()
        ]),
        awaitReturnTimeoutMinutes: z.int(),
        drainTimeoutMinutes: z.int()
    })
});

/**
 * The node's cluster — a singleton, install-born (TVirt heritage, adopted 2026-08-02). Not an ordinary scoped resource: no `scope` field (it OWNS the single tree's root, named after it), never listed, never deletable at runtime. Cluster renders expose `revision`: it is the store generation clients watch for convergence.
 *
 */
export const zCluster = z.object({
    id: z.optional(z.uuid().readonly()),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    phase: z.optional(z.enum([
        'forming',
        'ready'
    ])),
    security: z.optional(z.object({
        passwordPolicy: z.optional(z.object({
            minLength: z.optional(z.int().gte(8).lte(128)).default(12)
        })),
        sessionMaxIdleMinutes: z.optional(z.int().gte(5).lte(43200)).default(480),
        lockout: z.optional(z.object({
            threshold: z.optional(z.int().gte(1)).default(5),
            durationMinutes: z.optional(z.int().gte(1)).default(15)
        }))
    })),
    monitoring: z.optional(z.object({
        anomaly: z.optional(z.object({
            windowHours: z.optional(z.int().gte(1).lte(168)).default(24),
            minSamples: z.optional(z.int().gte(6).lte(2016)).default(24),
            alpha: z.optional(z.union([
                z.number().gt(0).lte(1),
                z.null()
            ])).default(null)
        })),
        retentionDays: z.optional(z.int().gte(7).lte(3650)).default(90)
    })),
    audit: z.optional(z.object({
        retentionDays: z.optional(z.int().gte(90).lte(3650)).default(365),
        sensitiveReads: z.optional(z.object({
            enabled: z.boolean().default(false),
            scopes: z.optional(z.array(z.string().max(255)).max(64)).default(['/*'])
        }))
    })),
    logging: z.optional(z.object({
        rotation: z.optional(z.object({
            maxSizeMiB: z.optional(z.int().gte(1).lte(10240)).default(100),
            maxAgeDays: z.optional(z.int().gte(1).lte(3650)).default(30),
            keep: z.optional(z.int().gte(1).lte(365)).default(10),
            compress: z.optional(z.boolean()).default(true)
        })),
        journal: z.optional(z.object({
            maxUseMiB: z.optional(z.int().gte(16).lte(1048576)).default(1024),
            maxRetentionDays: z.optional(z.int().gte(1).lte(3650)).default(30)
        })),
        audit: z.optional(z.object({
            maxSizeMiB: z.optional(z.int().gte(16).lte(4096)).default(256),
            keep: z.optional(z.int().gte(1).lte(64)).default(8)
        }))
    })),
    metrics: z.optional(z.object({
        tokenConfigured: z.boolean(),
        tokenIssuedAt: z.union([
            z.iso.datetime(),
            z.null()
        ])
    }).readonly()),
    transportSecurity: z.optional(z.object({
        plainHttp: z.optional(z.enum([
            'allowed',
            'refused'
        ]))
    })),
    nfsEngine: z.optional(z.object({
        ceph: z.optional(z.enum([
            'ganesha',
            'knfsd'
        ])),
        rdma: z.optional(z.object({
            enabled: z.optional(z.boolean()).default(false),
            port: z.optional(z.int().gte(1).lte(65535)).default(20049)
        }))
    })),
    gatewayFailover: z.optional(z.object({
        rebalance: z.optional(z.enum([
            'automatic',
            'manual'
        ])),
        rebalanceAfterMs: z.optional(z.int().gte(0).lte(3600000)).default(60000)
    })),
    documentEditor: z.optional(z.union([
        z.object({
            serverUrl: z.string().min(1).max(1024),
            wopiBaseUrl: z.optional(z.string().min(1).max(1024)),
            certFingerprint: z.optional(z.union([
                z.string().max(95),
                z.null()
            ]))
        }),
        z.null()
    ])),
    externalMfa: z.optional(z.union([
        z.object({
            serverUrl: z.string().min(1).max(1024),
            realm: z.optional(z.union([
                z.string().max(255),
                z.null()
            ])),
            certFingerprint: z.optional(z.union([
                z.string().max(95),
                z.null()
            ]))
        }),
        z.null()
    ])),
    mfaGraceDays: z.optional(z.int().gte(0).lte(365)).default(14),
    mfaIssuer: z.optional(z.union([
        z.string().min(1).max(64),
        z.null()
    ])),
    replicaEncryption: z.optional(z.object({
        enabled: z.optional(z.boolean()).default(false),
        listenPort: z.optional(z.int().gte(1).lte(65535)).default(51820),
        mtu: z.optional(z.union([
            z.int().gte(1280).lte(9216),
            z.null()
        ])).default(null),
        persistentKeepaliveSec: z.optional(z.int().gte(0).lte(3600)).default(25),
        nodes: z.optional(z.array(z.object({
            node: z.optional(z.uuid()),
            name: z.optional(z.string()),
            index: z.optional(z.int().gte(1).lte(254)),
            overlayAddress: z.optional(z.string()),
            publicKey: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            credential: z.optional(z.uuid())
        })).readonly())
    })),
    quorum: z.optional(z.object({
        state: z.optional(z.enum([
            'single',
            'replicated'
        ])),
        storeMembers: z.optional(z.array(z.string()))
    }).readonly()),
    store: z.optional(zClusterStore),
    storeDegraded: z.optional(z.boolean().readonly()),
    witness: z.optional(z.union([
        z.object({
            endpoints: z.array(z.string().min(1).max(255)).min(1),
            order: z.optional(z.enum([
                'witnessFirst',
                'fencingFirst'
            ])),
            leaseTtlMs: z.optional(z.int().gte(1000)).default(15000),
            renewMs: z.optional(z.int().gte(250)).default(5000),
            marginMs: z.optional(z.int().gte(0)).default(5000)
        }),
        z.null()
    ])),
    witnessStatus: z.optional(z.union([
        z.array(z.object({
            endpoint: z.optional(z.string()),
            reachable: z.optional(z.boolean()),
            observedAt: z.optional(z.iso.datetime())
        })).readonly(),
        z.null()
    ]).readonly()),
    failover: z.optional(z.object({
        mode: z.optional(z.enum([
            'auto',
            'quorum',
            'fencing'
        ])),
        effectiveMode: z.optional(z.enum([
            'quorum',
            'fencing'
        ])),
        survivalLevel: z.optional(z.enum([
            'n-1',
            'n-2'
        ])),
        witnessNode: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        degraded: z.optional(z.boolean().readonly()),
        degradedReason: z.optional(z.union([
            z.string().readonly(),
            z.null()
        ]).readonly())
    })),
    networkQos: z.optional(z.object({
        enabled: z.optional(z.boolean()).default(false),
        classes: z.optional(z.record(z.string(), z.object({
            weight: z.int().gte(1).lte(1000),
            floorMbps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            ceilingMbps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ]))
        })))
    })),
    configGeneration: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    epoch: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    minReplicatedGeneration: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    stale: z.optional(z.boolean().readonly()),
    rollingUpdate: z.optional(z.union([
        zRollingUpdateState,
        z.null()
    ])),
    notes: z.optional(z.string().max(65536)),
    revision: z.optional(z.int().readonly()),
    createdAt: z.optional(z.iso.datetime().readonly()),
    updatedAt: z.optional(z.iso.datetime().readonly())
});

/**
 * Body of the `promoteStore` action (M2f, 2026-08-03) — MANUAL entry into the same machinery the automatic failover engine drives when it holds a witness grant or a verified fence. Sudo-protected AND confirm-typed: a promotion re-mints the cluster epoch and every other member converges down onto the new primary.
 *
 */
export const zClusterPromoteStoreRequest = z.object({
    node: z.uuid(),
    confirm: z.string()
});

/**
 * Body of the `switchoverStore` action (STORE-HA-3 F, ratifica Luca 2026-11-03 §35.3) — a PLANNED change of store primary while the current primary is alive: nothing is lost, so the action is sudo-protected but NOT confirm-typed. `node` names the candidate; absent, the most aligned secondary of the ack set is chosen.
 *
 */
export const zClusterSwitchoverStoreRequest = z.object({
    node: z.optional(z.uuid())
});

/**
 * Body of the `addStoreMember` / `removeStoreMember` actions (M2f, 2026-08-03). SCOPE FENCE (2026-08-03): there is no node JOIN flow yet — the node row must already exist in the registry (`404` otherwise); adding a member is a store operation, not a membership one.
 *
 */
export const zClusterStoreMemberRequest = z.object({
    node: z.uuid()
});

/**
 * Body of `rollingUpdate` (ENEA-A-R16 §2.3). Sudo-protected AND confirm-typed (`confirm` = the cluster's `name`). The product does NOT update itself in this round: between the drain and the undrain of each node the orchestration WAITS for the operator (or the ISO) to update and reboot it, and recognises the return by the worker's `software.startedAt` being newer than the maintenance entry and, when `expectedVersion` is given, by that version.
 *
 */
export const zRollingUpdateRequest = z.object({
    confirm: z.string(),
    nodes: z.optional(z.union([
        z.array(z.string()),
        z.null()
    ])),
    expectedVersion: z.optional(z.union([
        z.string().max(64),
        z.null()
    ])),
    awaitReturnTimeoutMinutes: z.optional(z.union([
        z.int().gte(1).lte(1440),
        z.null()
    ])),
    drainTimeoutMinutes: z.optional(z.union([
        z.int().gte(1).lte(120),
        z.null()
    ]))
});

export const zRollingUpdateAbortRequest = z.object({
    confirm: z.string()
});

/**
 * One recorded API action (#73 AUDIT-1, dettatura Luca 2026-08-06 refactor 3): a row is born for EVERY API mutation (POST/PUT/PATCH/DELETE, actions included) — whoever made it and whatever it answered, failed attempts included (a 401 login, a 403 refusal and a 5xx outage are outcomes, not omissions). GETs are never recorded (a read registry is a different thing and a different volume). The request BODY is never recorded — bodies carry secrets — and the query string in `path` is redacted with the request-log redactor (`?access_token=`). Rows are immutable through the API by construction: no mutation route exists under /cluster/audit, and the only deleter is the retention sweep (`Cluster.audit.retentionDays`).
 *
 */
export const zAuditEvent = z.object({
    id: z.uuid().readonly(),
    occurredAt: z.iso.datetime().readonly(),
    node: z.string().readonly(),
    method: z.enum([
        'POST',
        'PUT',
        'PATCH',
        'DELETE'
    ]),
    route: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    path: z.string().readonly(),
    status: z.int().readonly(),
    sessionId: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    userId: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    username: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    realm: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    remoteAddr: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    kind: z.optional(z.enum([
        'mutation',
        'login',
        'sensitiveRead',
        'prune'
    ])),
    userAgent: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    detail: z.optional(z.union([
        z.record(z.string(), z.unknown()).readonly(),
        z.null()
    ]).readonly()),
    hash: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    prevHash: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly())
});

export const zAuditEventList = z.object({
    items: z.array(zAuditEvent),
    nextCursor: z.optional(z.string())
});

/**
 * Body of `POST /cluster/audit/actions/verify` (all optional).
 */
export const zAuditVerifyRequest = z.object({
    node: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    from: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    to: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ]))
});

/**
 * ENEA-A-R11 (2026-10-23): the result of `POST /cluster/audit/actions/verify` — one verdict per node chain within the requested window. `unchained` rows (born before v46) are counted, never judged; `startsAfterPrune` names the first surviving row when the retention sweep removed older ones and left a `prune` row attesting it; `firstBreak` is the first row whose `prevHash` or `hash` does not recompute — a tamper-evidence verdict, not a signature.
 *
 */
export const zAuditChainVerification = z.object({
    verifiedAt: z.iso.datetime(),
    nodes: z.array(z.object({
        node: z.string(),
        rows: z.int(),
        chained: z.int(),
        unchained: z.int(),
        verified: z.boolean(),
        startsAfterPrune: z.union([
            z.object({
                id: z.optional(z.string()),
                occurredAt: z.optional(z.iso.datetime()),
                prunedAt: z.optional(z.iso.datetime()),
                removedRows: z.optional(z.int())
            }),
            z.null()
        ]),
        firstBreak: z.union([
            z.object({
                id: z.optional(z.string()),
                occurredAt: z.optional(z.iso.datetime()),
                reason: z.optional(z.string())
            }),
            z.null()
        ])
    }))
});

export const zBlockDevicePath = z.object({
    device: z.string(),
    state: z.enum([
        'active',
        'failed',
        'unknown'
    ])
});

/**
 * ENEA-A-R11 (2026-10-23; hardware §8): a drive's SMART health as the owning node's worker read it (smartctl -j, nvme smart-log as the fallback; every 15 minutes, not on every census). `available: false` + `reason` when the tool could not read (missing, permission denied, SMART unsupported) — never an invented number. `percentageUsed` is the firmware's wear counter (NVMe percentage_used; ATA: 100 − normalized 177/233/169/231 where the vendor exposes one, null otherwise); factory rules tvstor-disk-wear-warning (> 80) and -critical (> 90) ride the node metric `node.diskWearMaxPercent`. Thresholds and the 15-minute cadence are measured on iron in R18. Rendered on ClusterDisk.health (the fleet census) and on ClusterNode.blockDevices[].health (the node's own).
 *
 */
export const zDiskHealth = z.object({
    source: z.nullable(z.enum([
        'smartctl',
        'nvme'
    ])),
    percentageUsed: z.union([
        z.number(),
        z.null()
    ]),
    mediaErrors: z.union([
        z.number(),
        z.null()
    ]),
    criticalWarning: z.union([
        z.number(),
        z.null()
    ]),
    temperatureC: z.union([
        z.number(),
        z.null()
    ]),
    powerOnHours: z.union([
        z.number(),
        z.null()
    ]),
    passed: z.union([
        z.boolean(),
        z.null()
    ]),
    available: z.boolean(),
    reason: z.union([
        z.string(),
        z.null()
    ]),
    observedAt: z.string()
});

export const zClusterDisk = z.object({
    wwid: z.union([
        z.string(),
        z.null()
    ]),
    serial: z.union([
        z.string(),
        z.null()
    ]),
    sizeBytes: z.number(),
    model: z.union([
        z.string(),
        z.null()
    ]),
    shared: z.boolean(),
    seenBy: z.array(z.object({
        node: z.string(),
        device: z.string(),
        multipath: z.boolean(),
        paths: z.array(zBlockDevicePath)
    })),
    marked: z.array(z.object({
        node: z.string(),
        serial: z.string()
    })),
    health: z.optional(z.union([
        zDiskHealth,
        z.null()
    ])),
    observedAt: z.union([
        z.iso.datetime(),
        z.null()
    ])
});

export const zClusterDiskList = z.object({
    items: z.array(zClusterDisk)
});

/**
 * One level of a node's fencing cascade (M2e, dettatura Luca 2026-08-03, 5° giro). Verify semantics are DETERMINISTIC per kind: `bmc` = commanded power-off, verified by reading the power state back (readable through the degraded fence account during a disarm lock); `switch` = port-down of ALL the node's host-facing ports — ISOLATION fencing: the node stays up but can serve no client, replicate nothing, reach no store; its own lease-loss self-fence is the extra belt — verified oper-down from the switch, and the cut MUST SPARE the target's BMC port (hardware requirement: dedicated BMC port, no shared-LOM); `pdu` = outlet-off of ALL mapped outlets (dual PSU on dual PDU ⇒ the PSU→outlet map is part of this config and BOTH go off), verified by outlet state. CREDENTIALS (M2g, 2026-08-03 — the M2e "keystore-fenced" seam is CLOSED): a real device declares `credential`, a reference to a `bmcAccount` keystore entry (`credentials.yaml`) the worker resolves AT USE TIME from its replica — for power/port/outlet READS, for the kill actuation, AND as the self-disarm anchor (the credential's username names the account whose role gets flipped; power state stays readable through the degraded account — ratified 4° giro semantics unchanged). Secrets never on argv: ipmitool takes a transient 0600 secretfile (`-f`), Redfish is spoken in-process over HTTPS; task params and `fencingStatus` carry the credential ID at most, never secret material. `mock` remains the rig alternative; a device with NEITHER `credential` NOR `mock` is unusable and reported unreachable in `fencingStatus` (rule carried from M2e, re-dated 2026-08-03).
 *
 */
export const zNodeFencingDevice = z.object({
    kind: z.enum([
        'bmc',
        'switch',
        'pdu'
    ]),
    endpoint: z.string().min(1).max(255),
    credential: z.optional(z.uuid()),
    ports: z.optional(z.array(z.string().min(1))),
    outlets: z.optional(z.array(z.object({
        psu: z.string(),
        outlet: z.string()
    }))),
    roleCapable: z.optional(z.boolean()).default(true),
    escalationOnly: z.optional(z.boolean()).default(false),
    mock: z.optional(z.union([
        z.record(z.string(), z.unknown()),
        z.null()
    ]))
});

/**
 * ENEA-A-R16: what a completed drain CEDED, as the `node.drain` task recorded it — addresses with the gateway that took them, volumes with the peer that took them, the control roles that moved, and whether `noout` was set.
 *
 */
export const zNodeMaintenanceCeded = z.object({
    addresses: z.array(z.object({
        clusterId: z.uuid(),
        address: z.string(),
        to: z.union([
            z.string(),
            z.null()
        ])
    })),
    volumes: z.array(z.object({
        volumeId: z.uuid(),
        to: z.union([
            z.string(),
            z.null()
        ])
    })),
    roles: z.array(z.object({
        kind: z.enum([
            'control',
            'catalog',
            'mirror',
            'forwarder'
        ]),
        datapoolId: z.uuid()
    })),
    noout: z.boolean()
});

/**
 * ENEA-A-R16 (2026-10-24): the maintenance state machine of a node — `draining` (the drain task runs: the node cedes), `maintenance` (all ceded: the operator may stop, update and reboot it), `undraining` (the undrain task runs: `noout` unset, the beats claim again). A failed drain leaves `draining` with the task's `failedStep` visible on the task and `maintenancePending` saying what is still held.
 *
 */
export const zNodeMaintenance = z.object({
    state: z.enum([
        'draining',
        'maintenance',
        'undraining'
    ]),
    requestedAt: z.iso.datetime(),
    requestedBy: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    reason: z.optional(z.union([
        z.string().max(1024),
        z.null()
    ])),
    taskId: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    enteredAt: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    ceded: z.optional(z.union([
        zNodeMaintenanceCeded,
        z.null()
    ]))
});

export const zNodeMaintenancePending = z.object({
    addresses: z.array(z.object({
        clusterId: z.uuid(),
        address: z.string()
    })),
    volumes: z.array(z.uuid()),
    roles: z.array(z.object({
        kind: z.enum([
            'control',
            'catalog',
            'mirror',
            'forwarder'
        ]),
        datapoolId: z.uuid()
    }))
});

export const zNodeSoftware = z.object({
    version: z.string(),
    startedAt: z.iso.datetime()
});

/**
 * A cluster member (TVirt heritage, adopted 2026-08-02) — nodes are OBJECTS of this registry, not scopes. In M1 the registry holds exactly the creating node, registered `member` by install-time seeding (`db:init-cluster` in dev). Exposes `revision`. Since M2a (2026-08-02) the node item accepts PATCH for the first per-node declaration, `dataDisks`, and renders the observed `blockDevices` inventory. ROADMAP (2026-08-02): the joiner lifecycle (`pending-join`/`joining`, `tvsj_` tokens) and the remaining declarations (settings, storageNetworks, BMC) join with their milestones.
 *
 */
export const zClusterNode = z.object({
    id: z.uuid().readonly(),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ip: z.string().max(45),
    site: z.optional(z.union([
        z.string().max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
        z.null()
    ])),
    phase: z.optional(z.enum([
        'pending-join',
        'joining',
        'joinPending',
        'member'
    ])),
    publicKey: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    storeMember: z.optional(z.boolean().readonly()),
    storeRole: z.optional(z.enum([
        'primary',
        'secondary',
        'none'
    ])),
    storeEndpoint: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    storeSlot: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    storeLastPromotedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    storeLastPromotedAtUnknown: z.optional(z.boolean().readonly()),
    replicatedGeneration: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    services: z.optional(z.union([
        z.array(z.object({
            name: z.optional(z.string()),
            enabled: z.optional(z.boolean()),
            active: z.optional(z.boolean()),
            observedAt: z.optional(z.iso.datetime())
        })).readonly(),
        z.null()
    ]).readonly()),
    dataDisks: z.optional(z.array(z.object({
        name: z.string(),
        serial: z.string().min(1),
        wwid: z.optional(z.union([
            z.string().readonly(),
            z.null()
        ]).readonly())
    }))),
    blockDevices: z.optional(z.union([
        z.array(z.object({
            name: z.optional(z.string()),
            serial: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            sizeBytes: z.optional(z.number()),
            model: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            inUse: z.optional(z.boolean()),
            observedAt: z.optional(z.iso.datetime()),
            wwid: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            multipath: z.optional(z.boolean()),
            paths: z.optional(z.array(zBlockDevicePath)),
            seenBy: z.optional(z.array(z.string())),
            health: z.optional(z.union([
                zDiskHealth,
                z.null()
            ]))
        })).readonly(),
        z.null()
    ]).readonly()),
    fencingDevices: z.optional(z.array(zNodeFencingDevice)),
    fencingStatus: z.optional(z.union([
        z.array(z.object({
            kind: z.optional(z.string()),
            endpoint: z.optional(z.string()),
            reachable: z.optional(z.boolean()),
            observedAt: z.optional(z.iso.datetime())
        })).readonly(),
        z.null()
    ]).readonly()),
    failoverState: z.optional(z.union([
        z.object({
            effectiveMode: z.optional(z.enum([
                'quorum',
                'fencing'
            ])),
            witnessRole: z.optional(z.boolean()),
            fencingArmed: z.optional(z.boolean())
        }).readonly(),
        z.null()
    ]).readonly()),
    replicaTunnel: z.optional(z.union([
        z.object({
            enabled: z.optional(z.boolean()),
            index: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            overlayAddress: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            peers: z.optional(z.array(z.object({
                node: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                publicKey: z.optional(z.string()),
                endpoint: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                handshakeAgeSec: z.optional(z.union([
                    z.number(),
                    z.null()
                ])),
                rxBytes: z.optional(z.union([
                    z.number(),
                    z.null()
                ])),
                txBytes: z.optional(z.union([
                    z.number(),
                    z.null()
                ]))
            }))),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    lastSeenAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    keystore: z.optional(z.object({
        wrappingKeyRegistered: z.boolean(),
        wrappingMechanism: z.union([
            z.string(),
            z.null()
        ]),
        custodian: z.union([
            z.object({
                kind: z.optional(z.enum([
                    'file',
                    'pkcs11'
                ])),
                token: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                residence: z.optional(z.string()),
                declaredAt: z.optional(z.iso.datetime())
            }),
            z.null()
        ]),
        revokedAt: z.union([
            z.iso.datetime(),
            z.null()
        ]),
        envelopes: z.union([
            z.object({
                held: z.optional(z.int()),
                total: z.optional(z.int())
            }),
            z.null()
        ])
    }).readonly()),
    maintenance: z.optional(z.union([
        zNodeMaintenance,
        z.null()
    ])),
    maintenancePending: z.optional(z.union([
        zNodeMaintenancePending,
        z.null()
    ])),
    software: z.optional(z.union([
        zNodeSoftware,
        z.null()
    ])),
    notes: z.optional(z.string().max(65536)),
    revision: z.optional(z.int().readonly()),
    createdAt: z.optional(z.iso.datetime().readonly()),
    updatedAt: z.optional(z.iso.datetime().readonly())
});

export const zClusterNodeList = z.object({
    items: z.array(zClusterNode),
    nextCursor: z.optional(z.string())
});

/**
 * Body of the pre-registration POST /cluster/nodes (M5a, 2026-08-04 — the TVirt §3.4 join model): a MEMBER registers the joiner before the joiner exists, so the joiner's installer can authenticate with nothing but the minted token. `address` seeds the node row's `ip` (the management address the M1 registry render carries — one concept, the request speaks the ratified M5a field name).
 *
 */
export const zClusterNodeCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    address: z.string().max(45),
    site: z.optional(z.string().max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/))
});

/**
 * The ONE-TIME join token (M5a, 2026-08-04 — TVirt `tvjt_` mechanism with the TVStor prefix): `tvsj_<secret>.<caFingerprint>` — 32 bytes of entropy base64url-encoded, a dot, and the cluster CA fingerprint (lowercase sha-256 hex of the CA certificate DER). Single use, TTL 30 minutes, stored HASHED only (sha-256, the session-token discipline): this render — inside the pre-registration `201` — is the ONLY time the secret ever crosses the API (keystore write-only doctrine); no read path re-renders it, and losing it means revoke + re-register. The embedded fingerprint is the joiner's OUT-OF-BAND CA pin: the installer verifies the CA delivered by /internal/join against it before trusting anything else.
 *
 */
export const zJoinToken = z.object({
    token: z.string(),
    caFingerprint: z.string().regex(/^[0-9a-f]{64}$/),
    expiresAt: z.iso.datetime()
});

/**
 * Answer of the pre-registration POST (M5a, 2026-08-04): the created `joining` node row plus the one-time token. The `joinToken` half exists ONLY in this response body — the write-once render.
 *
 */
export const zClusterNodeRegistration = z.object({
    node: zClusterNode,
    joinToken: zJoinToken
});

/**
 * Answer of the certificate revocation POST (PKI-2, 2026-08-10).
 *
 */
export const zClusterNodeRevocation = z.object({
    node: z.string(),
    revoked: z.int().gte(0),
    crlNumber: z.int().gte(1)
});

/**
 * Answer of the certificate reinstatement POST (PKI-2, 2026-08-10).
 *
 */
export const zClusterNodeReinstatement = z.object({
    node: z.string(),
    reinstated: z.boolean()
});

/**
 * Body of the node `drain` / `undrain` actions (ENEA-A-R16). Sudo-protected AND confirm-typed (`confirm` = the node's `name`): a drain moves every floating address and every served volume of the node — an operator act, typed by hand.
 *
 */
export const zNodeDrainRequest = z.object({
    confirm: z.string(),
    reason: z.optional(z.union([
        z.string().max(1024),
        z.null()
    ]))
});

/**
 * Body of the wrapping-key enroll POST (#59 S3.2, 2026-08-21). The typed confirm (the promoteStore/destroy precedent) is the spoken danger: on a RE-registration the node's old envelopes die with the old key.
 *
 */
export const zClusterNodeWrappingKeyEnrollRequest = z.object({
    confirm: z.string()
});

/**
 * Answer of the wrapping-key enroll POST (#59 S3.2, 2026-08-21).
 *
 */
export const zClusterNodeWrappingKeyEnrollment = z.object({
    node: z.string(),
    custodian: z.object({
        kind: z.enum([
            'file',
            'pkcs11'
        ]),
        token: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }),
    mechanism: z.enum([
        'rsa-oaep-sha256',
        'rsa-oaep-sha1'
    ]),
    residence: z.string(),
    rotated: z.boolean(),
    envelopesDied: z.int(),
    reseeded: z.int(),
    reseedFailures: z.array(z.string())
});

/**
 * Answer of the wrapping-key revoke POST (#59 S3.2, 2026-08-21).
 *
 */
export const zClusterNodeWrappingKeyRevocation = z.object({
    node: z.string(),
    envelopesRevoked: z.int(),
    revokedAt: z.iso.datetime()
});

/**
 * The CHUNK CENSUS of ONE marked disk (DISK-1 L2, 2026-08-05 — dettature e ratifiche Luca 2026-08-04, docs/vdisk-partial-disk-2026-08-04.md): the disk is a PV in ITS OWN VG (`tvstor-<serial>` — topologia (a) ratificata: blast radius = the disk; a disk dies alone) carved into ANONYMOUS, FUNGIBLE chunks, all of the SAME per-disk size. `create-chunks` decides that size PER DISK (rettifica 2026-08-04: /dev/sda at 1TiB chunks and /dev/sdb at 512GiB chunks coexist on one node); no chunk is individually addressable — no names, no ids, only COUNTERS (ratifica finale 2026-08-04: "i chunk sono ANONIMI... conta solo la QUANTITÀ"). Consumers — raids (per-disk aggregates) and jbod vdisks (same-disk concat) — allocate by QUANTITY and the allocator takes free chunks of the disk. BINDING INTERPRETATION (this round, shared domain `common/src/multiscsi/chunks.ts`): a chunk is the ALLOCATION GRANULE of its disk — carving creates NO LVs; the physical LVs are the per-disk AGGREGATES and jbod vdisks, materialized at create-raid/create-vdisk time (LVM extent allocation IS the ratified same-disk concat). Config-class row keyed by the disk serial (replicated, born by `node.chunks.create`); `chunksAllocated` is config (the sum of live claims, moved at admission in the SAME store transaction as the consuming row), `chunksTotal` is computed at admission from the observed inventory and confirmed by the executor's VG census, and the observed VG state composes from the worker sweep — staleness visible, never hidden. HONESTY (manuale operatore, ratifica 2026-08-04, the SHR lesson): consumers sharing a disk via chunks contend for its IOPS — a rebuild of one array reads/writes a disk that serves others; the M5b metrics make the contention visible.
 *
 */
export const zNodeDiskChunks = z.object({
    disk: z.optional(z.object({
        name: z.optional(z.string()),
        serial: z.optional(z.string().min(1))
    })),
    chunkSizeGib: z.optional(z.int().gte(1).readonly()),
    chunksTotal: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    chunksAllocated: z.optional(z.int().readonly()),
    chunksFree: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    phase: z.optional(z.enum([
        'pending',
        'creating',
        'ready',
        'error',
        'destroying'
    ])),
    vgState: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    createdAt: z.optional(z.iso.datetime().readonly()),
    updatedAt: z.optional(z.iso.datetime().readonly())
});

/**
 * The node's whole chunk census — one entry per CHUNKED disk. Unpaginated by design (bounded by the node's marked disks; the census IS the list the CLI renders).
 *
 */
export const zNodeDiskChunksList = z.object({
    items: z.array(zNodeDiskChunks)
});

/**
 * Body of `POST /api/v1/cluster/nodes/{node}/chunks` — the create-chunks verb (CLI ratificata 2026-08-04: `config-node <node> create-chunks --disks /dev/sda,/dev/sdb --size 1T` — PLURALE: several disks in one call, each becoming its own VG carved at THIS size). Admission (`422 validation` unless stated): every named disk must be marked in the node's `dataDisks` (unmarked or unobserved paths are refused — the serial-pin doctrine), must NOT already be chunked (`409 conflict` naming the disk — the per-disk size is decided ONCE; there is no re-chunking without dismantling), duplicates in `disks` are refused, and `sizeGib` must fit at least one whole chunk on every named disk (error speaking GiB against the disk's observed size).
 *
 */
export const zNodeChunksCreateRequest = z.object({
    disks: z.array(z.string()).min(1),
    sizeGib: z.int().gte(1)
});

/**
 * One member of a node raid — a PER-DISK AGGREGATE (nota ratificata Luca 2026-08-04): the `count` chunks asked of `disk` concatenate into ONE LV, and md sees exactly one device per disk. The aggregate's byte size is `count` × the disk's chunk size — the quantity the redundant levels' byte-wise admission compares.
 *
 */
export const zNodeRaidMember = z.object({
    disk: z.object({
        name: z.string(),
        serial: z.string().min(1)
    }),
    count: z.int().gte(1),
    sizeBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly())
});

/**
 * A RAID — a node-local md array over PER-DISK CHUNK AGGREGATES (DISK-1 L3, 2026-08-05 — CLI ratificata 2026-08-04: `config-node <node> create-raid --name rvol1 --level 0 --members /dev/sda:1x,/dev/sdb:2x`). For each member the allocator takes `count` free chunks of that disk and concatenates them into ONE aggregate LV; md is built over the aggregates — one device per disk BY CONSTRUCTION. Levels 0/1/5/6/10 ONLY: linear is ratified OUT (rettifica finale 2026-08-04 — no cross-disk concatenation in any form; growing past one disk is raid0's job, zone striping absorbing unequal aggregates). ADMISSION IN BYTES (nota ratificata 2026-08-04): for the redundant levels (1/5/6/10) every aggregate must have the SAME total size in BYTES, counting each disk's OWN chunk size — `/dev/sda:1x` of 1TiB chunks mirrors `/dev/sdb:2x` of 512GiB chunks and MUST be admitted; a mismatch is `422 validation` SPEAKING GiB per aggregate, never bare chunk counts. raid0 admits differing aggregates (md zone striping, no waste). DISTINCT DISKS are the redundancy rule and hold by construction (one aggregate = one disk): a disk may appear ONCE in `members` — a duplicate is `422 validation` on every level (more capacity from one disk is a higher `count`, never a second entry). Subresource of the node registry like the vdisks (no scope/tags/notes); `name` unique PER NODE. Config-class row (replicated); observed fields compose from the worker sweep through the node observation outbox (staleness visible, never hidden). HONESTY (manuale operatore, ratifica 2026-08-04): a raid0 loses the WHOLE device with ANY one member disk, and its reconstruction after disk replacement is the ENTIRE mirror resync over the replica network; arrays sharing disks via chunks contend under rebuild (the SHR lesson) — declared, not hidden.
 *
 */
export const zNodeRaid = z.object({
    id: z.uuid().readonly(),
    node: z.optional(z.uuid().readonly()),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    level: z.enum([
        'raid0',
        'raid1',
        'raid5',
        'raid6',
        'raid10'
    ]),
    members: z.array(zNodeRaidMember).min(2),
    phase: z.optional(z.enum([
        'pending',
        'creating',
        'ready',
        'degraded',
        'error',
        'destroying'
    ])),
    vdisk: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    mdState: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    sizeBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    createdAt: z.optional(z.iso.datetime().readonly()),
    updatedAt: z.optional(z.iso.datetime().readonly()),
    revision: z.optional(z.int().readonly())
});

export const zNodeRaidList = z.object({
    items: z.array(zNodeRaid),
    nextCursor: z.optional(z.string())
});

/**
 * Creation body of `POST /api/v1/cluster/nodes/{node}/raids` — the create-raid verb (CLI ratificata 2026-08-04, forma finale: `--members /dev/sda:1x,/dev/sdb:2x` — QUANTITY ONLY, the per-name form died with the chunk names). Members name dev paths; admission maps each to the node's `dataDisks` declaration, pins the serial, and checks the disk's chunk census. Admission (`422 validation` unless stated): every member disk marked AND chunked (an unchunked disk names `create-chunks` in the error), `count` within the disk's free counter (the error names the free count), a disk appearing twice refused, the level's minimum DISK count met (2 for raid0/raid1, 3 for raid5, 4 for raid6/raid10), and for the redundant levels the BYTE-WISE aggregate equality (error speaking GiB per aggregate — see `NodeRaid`); a duplicate raid name on the same node is `409 conflict`. The readOnly fields of `NodeRaid` stay known-and-ignored on input (round #149 strictness rule).
 *
 */
export const zNodeRaidCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    level: z.enum([
        'raid0',
        'raid1',
        'raid5',
        'raid6',
        'raid10'
    ]),
    members: z.array(z.object({
        disk: z.string(),
        count: z.int().gte(1)
    })).min(2)
});

/**
 * A VDISK — the node-local block device an engine consumes, LEVEL FOUR of the chunk stack (DISK-1, 2026-08-05 — the M2d whole-disk md model is RETIRED; pre-GA break ratified 2026-08-04: existing vdisks migrate by RECREATION, the old `--type/--members` surface is GONE). THREE FORMS, exactly one per vdisk: the RAID form (`create-vdisk vdisk1 rvol1`) — the vdisk wraps one of the node's raids, whose md device IS the vdisk's device (the raid is claimed single-owner); the JBOD form (`create-vdisk vdisk1 --disk /dev/sda --chunks 2` — shape PROPOSAL fixed this round per the ratified same-disk concat) — the vdisk is ONE LV concatenating K chunks of ONE disk. A jbod vdisk NEVER crosses disks (rettifica finale 2026-08-04: no cross-disk concatenation in any form — growing past one disk is raid0's job); the EXTERNAL form (#201 EXT-1, 2026-08-21 — ratifica docs/ratifiche-2026-08-21.md §9, uscita B of docs/ext-disegno-2026-08-21.md) — the vdisk's device is the LUN of a FOREIGN SAN (iSCSI or NVMe-oF): no local chunks, no local md — disk-fault tolerance is the foreign SAN's business (the honesty manual), while node/site redundancy stays with the EXISTING models on top (the vdisk external is a FULL datapool member: RAID1 multiscsi mirror across two external vdisks, ceph 3/2 at three or more). The LOGIN FOLLOWS THE VOLUME AUTHORITY (addendum ratificato): only the volume's primary connects and assembles; on failover the NEW primary re-connects — the same choreography as today's internal fabric md. Downstream NOTHING changes: the vdisk remains the internal-transport export unit (ratifica D-C: one nvmet subsystem per vdisk, NQN `nqn.2026-08.it.trantor.tvstor:<cluster>:<node>:vdisk:<vdisk8hex>` — exported when a datapool using it is engine-bound, not at vdisk creation) and the contribution unit of datapools (`datapools.yaml`, UNTOUCHED). Vdisks are SUBRESOURCES of the node registry, not scoped objects — no scope/tags/notes; `name` is unique PER NODE (dettatura 2026-08-03: `node1:vdisk1` and `node2:vdisk1` coexist). Config-class row (`vdisks`, replicated); observed fields compose from `vdisk_status` (worker sweep through the node observation outbox — staleness visible, never hidden). Vdisk RESIZE (lvextend) is ratified first-class (2026-08-04) and LANDED with DISK-1.1 (2026-08-05): the JBOD form grows through `actions/expand` (grow-only, whole chunks of its ONE disk); the RAID form is the dated seam `raid expand arrives with DISK-1.2`.
 *
 */
export const zNodeVdisk = z.object({
    id: z.uuid().readonly(),
    node: z.optional(z.uuid().readonly()),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    raid: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    jbod: z.optional(z.union([
        z.object({
            disk: z.object({
                name: z.string(),
                serial: z.string().min(1)
            }),
            chunks: z.int().gte(1)
        }),
        z.null()
    ])),
    external: z.optional(z.union([
        z.object({
            transport: z.enum([
                'iscsi',
                'nvmeof'
            ]),
            portals: z.array(z.object({
                host: z.string().min(1),
                port: z.int().gte(1).lte(65535)
            })).min(1),
            iqn: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            lun: z.optional(z.union([
                z.int().gte(0),
                z.null()
            ])),
            nqn: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            nsid: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            credential: z.optional(z.union([
                z.uuid(),
                z.null()
            ])),
            wwid: z.optional(z.union([
                z.string(),
                z.null()
            ]))
        }),
        z.null()
    ])),
    encryption: z.optional(z.object({
        mode: z.optional(z.enum([
            'none',
            'luks2'
        ])),
        keyCredential: z.optional(z.union([
            z.uuid().readonly(),
            z.null()
        ]).readonly()),
        state: z.optional(z.nullable(z.enum([
            'open',
            'locked'
        ]))),
        reason: z.optional(z.union([
            z.string().readonly(),
            z.null()
        ]).readonly())
    }).readonly()),
    phase: z.optional(z.enum([
        'pending',
        'creating',
        'ready',
        'degraded',
        'error',
        'destroying'
    ])),
    datapool: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    deviceState: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    rebuildPercent: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    sizeBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    paths: z.optional(z.union([
        z.array(z.object({
            device: z.string(),
            state: z.string()
        })).readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    createdAt: z.optional(z.iso.datetime().readonly()),
    updatedAt: z.optional(z.iso.datetime().readonly()),
    revision: z.optional(z.int().readonly())
});

export const zNodeVdiskList = z.object({
    items: z.array(zNodeVdisk),
    nextCursor: z.optional(z.string())
});

/**
 * Creation body of `POST /api/v1/cluster/nodes/{node}/vdisks` — the DISK-1 create-vdisk verb (CLI ratificata 2026-08-04: `config-node <node> create-vdisk vdisk1 rvol1` for the raid form — the CLI resolves the node-local raid NAME to the `raid` id — or `create-vdisk vdisk1 --disk /dev/sda --chunks 2` for the jbod form, shape PROPOSAL fixed this round), grown the EXTERNAL form by EXT-1 (#201, 2026-08-21: `create-vdisk vdisk1 --external iscsi --portal 10.0.9.1:3260 --iqn iqn.2000-01.com.acme:lun1 --lun 0`). EXACTLY ONE of `raid` / the `disk`+`chunks` pair / `external` — both, neither, or a half-given pair is `422 validation`. The M2d `type`/`members` fields are GONE (pre-GA break, ratified 2026-08-04 — recreation, no compat shim; sending them is refused as any unknown field). Admission — raid form: the raid must belong to THIS node (`404` via its absence from the node), be `ready` (`409 conflict` while creating/deleting/errored) and unclaimed (`409 in_use` naming the claiming vdisk). Jbod form: `disk` must be marked AND chunked (`422 validation` — an unchunked disk names `create-chunks` in the error) and `chunks` within the disk's free counter (`422 validation` naming the free count). External form: the leaf must be transport-coherent (`iqn`+`lun` with `iscsi`, `nqn` with `nvmeof` — the fields of the OTHER transport are `422 validation` naming the mismatch), an unknown `transport` is refused by the enum, `credential` must name an existing `iscsiChap` keystore credential (and is refused on `nvmeof` — DH-HMAC-CHAP is a future kind, D10). There is NO `encryption` field any more (VOL-CRYPT-CLUSTER, 2026-09-04): a vdisk is born plain and is encrypted when it JOINS a `luks2` storage cluster — an external vdisk cannot join one (the dated EXT seam moved to the cluster's join admission). NOTE the external form needs NO marked disk and NO chunk layer: the observed-serial constraint of L1 does not apply to a fabric resource (design §2.1 — declared, then attached, then observed). A duplicate vdisk name on the same node is `409 conflict`. The readOnly fields of `NodeVdisk` stay known-and-ignored on input (round #149 strictness rule).
 *
 */
export const zNodeVdiskCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    raid: z.optional(z.uuid()),
    disk: z.optional(z.string()),
    chunks: z.optional(z.int().gte(1)),
    external: z.optional(z.object({
        transport: z.enum([
            'iscsi',
            'nvmeof'
        ]),
        portals: z.array(z.object({
            host: z.string().min(1),
            port: z.int().gte(1).lte(65535)
        })).min(1),
        iqn: z.optional(z.string().min(1)),
        lun: z.optional(z.int().gte(0)),
        nqn: z.optional(z.string().min(1)),
        nsid: z.optional(z.int().gte(1)),
        credential: z.optional(z.uuid()),
        wwid: z.optional(z.string().min(1))
    }))
});

/**
 * Body of `POST /api/v1/cluster/nodes/{node}/vdisks/{vdisk}/actions/expand` (DISK-1.1, 2026-08-05 — the ratified first-class vdisk resize lands; the volumes `actionExpand` doctrine carried to L4): GROW ONLY — the new size must be STRICTLY GREATER than the vdisk's current size, and shrink does not exist anywhere in this API, by design. JBOD form only this round: the growth allocates MORE free chunks of the vdisk's ONE disk (same-disk concat, as at creation), so `sizeGib` must land EXACTLY on the disk's chunk granule — a target off the granule is `422 validation` speaking the granule and the nearest whole-chunk sizes (the counters are the capacity ledger; partial chunks never exist).
 *
 */
export const zNodeVdiskExpandRequest = z.object({
    sizeGib: z.int().gte(2)
});

/**
 * Body of `POST /api/v1/cluster/nodes/{node}/vdisks/{vdisk}/actions/migrate` (DISK-2 #50, 2026-08-28 — docs/disk2-disegno-2026-08-28.md §2, ratifica 2026-08-28: grana VDISK, forma B): vacate ONE member disk of a raid-form vdisk onto another marked disk of the SAME node, live — `mdadm --add` of a fresh K-chunk aggregate on `toDisk` + `--replace/--with` of the `fromDisk` member (the kernel copies from the LIVE members: no degraded window, MEASURED on the DISK-2 bench; a node crash mid-replace leaves the array CLEAN on its original members and the goal-state re-run re-engages the copy — referto M4). Both disks are named by SERIAL (the marked-disk identity, L1 doctrine).
 *
 */
export const zNodeVdiskMigrateRequest = z.object({
    fromDisk: z.string(),
    toDisk: z.string()
});

/**
 * One mount of a node as its census saw it (one `findmnt` row).
 */
export const zNodeMount = z.object({
    target: z.string(),
    source: z.string(),
    fstype: z.string(),
    options: z.string()
});

/**
 * The mount census of a node (`GET /cluster/nodes/{node}/mounts`): what its worker last published into `system_status.mounts` from `findmnt -J -l -o TARGET,SOURCE,FSTYPE,OPTIONS` (or `/proc/self/mounts` where findmnt is missing — `via` says which), pseudo-filesystems filtered (proc, sysfs, devtmpfs, devpts, cgroup*, pstore, bpf, tracefs, debugfs, securityfs, configfs, hugetlbfs, mqueue, fusectl, selinuxfs, efivarfs, autofs, binfmt_misc, rpc_pipefs, nsfs, ramfs), sorted by target, at most 512 entries (`truncated: true` beyond — a mount beyond the cap is not admissible as a source, declared). `observedAt` null = never published (the node's worker is not sampling): no source can be admitted on it.
 *
 */
export const zNodeMountCensus = z.object({
    node: z.string(),
    observedAt: z.union([
        z.iso.datetime(),
        z.null()
    ]),
    via: z.nullable(z.enum([
        'findmnt',
        'procfs'
    ])),
    truncated: z.boolean(),
    mounts: z.array(zNodeMount)
});

/**
 * ONE NODE'S ATTACHMENT to a network (NET-1, 2026-08-05): which carrier the node uses — a plain `interface` OR a platform-rendered `bond`, EXACTLY ONE of the two (`422 validation` when both or neither) — and the node's `address` inside the network's subnet. Declared via UPSERT PUT keyed by node id (one binding per network per node by construction); the worker reconcile pass renders bond, VLAN sub-interface and address and drift-heals them every pass. The readOnly fields (`node`, `nodeName`, `observed`) are known-and-ignored on input (round #149 strictness).
 *
 */
export const zNetworkNodeBinding = z.object({
    node: z.optional(z.uuid().readonly()),
    nodeName: z.optional(z.string().readonly()),
    interface: z.optional(z.union([
        z.string().max(15).regex(/^[A-Za-z0-9_.-]{1,15}$/),
        z.null()
    ])),
    bond: z.optional(z.union([
        z.object({
            members: z.array(z.string().max(15).regex(/^[A-Za-z0-9_.-]{1,15}$/)).min(2),
            mode: z.enum([
                'active-backup',
                '802.3ad'
            ]),
            lacpRate: z.optional(z.nullable(z.enum([
                'slow',
                'fast'
            ])))
        }),
        z.null()
    ])),
    address: z.string().max(64),
    observed: z.optional(z.union([
        z.object({
            carrier: z.optional(z.union([
                z.boolean(),
                z.null()
            ])),
            operState: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            speedMbps: z.optional(z.union([
                z.number(),
                z.null()
            ])),
            transport: z.optional(z.nullable(z.enum([
                'ethernet',
                'infiniband'
            ]))),
            ipoib: z.optional(z.union([
                z.object({
                    pkey: z.optional(z.string()),
                    mode: z.optional(z.nullable(z.enum([
                        'datagram',
                        'connected'
                    ])))
                }),
                z.null()
            ])),
            guid: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            rdma: z.optional(z.union([
                z.object({
                    device: z.optional(z.string()),
                    state: z.optional(z.string()),
                    physicalState: z.optional(z.union([
                        z.string(),
                        z.null()
                    ]))
                }),
                z.null()
            ])),
            ibRate: z.optional(z.union([
                z.number(),
                z.null()
            ])),
            lid: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            bondMembers: z.optional(z.array(z.object({
                name: z.optional(z.string()),
                carrier: z.optional(z.union([
                    z.boolean(),
                    z.null()
                ])),
                speedMbps: z.optional(z.union([
                    z.number(),
                    z.null()
                ])),
                lacpPartnerSystem: z.optional(z.union([
                    z.string(),
                    z.null()
                ]))
            }))),
            qos: z.optional(z.nullable(z.enum([
                'served',
                'absent',
                'foreign'
            ]))),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly())
});

/**
 * A DECLARED NETWORK (NET-1, 2026-08-05 — charter docs/networking-modello-2026-08-04.md): one of the four per-node network categories, cluster-level — `{name, category, vlanId?, subnet, mtu?}` — plus per-node BINDINGS (the sub-resource: interface or bond, and the node's address inside `subnet`). Declaring a network is what migrates a surface off its dated address seam: replicaInternal re-homes the internal NVMe/TCP transport (and the ENC-1 WireGuard endpoints), client re-homes the iSCSI portals and becomes the validation home of the NAS serviceAddresses and S3 endpoints, replicaExternal sources the DR links. With no networks declared everything stays on the seams — the honest fallback, dated 2026-08-05.
 *
 */
export const zNetwork = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    category: z.enum([
        'management',
        'replicaInternal',
        'client',
        'replicaExternal',
        'san'
    ]),
    vlanId: z.optional(z.union([
        z.int().gte(1).lte(4094),
        z.null()
    ])),
    subnet: z.string().min(9).max(64),
    mtu: z.optional(z.union([
        z.int().gte(576).lte(65520),
        z.null()
    ])),
    qos: z.optional(z.union([
        z.object({
            enabled: z.optional(z.boolean()),
            classes: z.optional(z.record(z.string(), z.object({
                weight: z.int().gte(1).lte(1000),
                floorMbps: z.optional(z.union([
                    z.int().gte(1),
                    z.null()
                ])),
                ceilingMbps: z.optional(z.union([
                    z.int().gte(1),
                    z.null()
                ]))
            })))
        }),
        z.null()
    ])),
    bindings: z.optional(z.array(zNetworkNodeBinding).readonly()),
    phase: z.optional(z.enum([
        'ready',
        'degraded',
        'unknown'
    ])),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zNetworkList = z.object({
    items: z.array(zNetwork),
    nextCursor: z.optional(z.string())
});

/**
 * Creation body of `POST /api/v1/networks` (NET-1, 2026-08-05) — the writable half of `Network` (the VolumeCreateRequest precedent: the request shape narrows the writable surface, not the known one — readOnly fields stay known-and-ignored on input, round #149 strictness). A network is born with NO bindings: declare them per node afterwards (`PUT /networks/{network}/bindings/{node}`) — nothing changes on any node until its binding lands, so declaring the model is always safe.
 *
 */
export const zNetworkCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.optional(z.string().max(255)),
    notes: z.optional(z.string().max(65536)),
    category: z.enum([
        'management',
        'replicaInternal',
        'client',
        'replicaExternal',
        'san'
    ]),
    vlanId: z.optional(z.union([
        z.int().gte(1).lte(4094),
        z.null()
    ])),
    subnet: z.string().min(9).max(64),
    mtu: z.optional(z.union([
        z.int().gte(576).lte(65520),
        z.null()
    ])),
    qos: z.optional(z.union([
        z.object({
            enabled: z.optional(z.boolean()),
            classes: z.optional(z.record(z.string(), z.object({
                weight: z.int().gte(1).lte(1000),
                floorMbps: z.optional(z.union([
                    z.int().gte(1),
                    z.null()
                ])),
                ceilingMbps: z.optional(z.union([
                    z.int().gte(1),
                    z.null()
                ]))
            })))
        }),
        z.null()
    ]))
});

export const zNetworkNodeBindingList = z.object({
    items: z.array(zNetworkNodeBinding)
});

/**
 * The joiner's FIRST word (join TLS pin, 2026-08-08) — deliberately SECRET-FREE: a nonce and nothing else. Everything a joiner owns (the one-time token above all) stays on the joiner until this exchange has proven who is at the other end of the connection.
 *
 */
export const zInternalJoinAttestRequest = z.object({
    nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/)
});

/**
 * The member's PROOF OF IDENTITY (join TLS pin, 2026-08-08). Until this round the joiner verified the CA the handshake DELIVERED against the fingerprint its token embeds — but a certificate is PUBLIC material, so the check was satisfied by anything able to forward it: an interceptor terminating the joiner's (unverified) TLS and relaying to the real member passed the pin verbatim while reading the join token inbound and the keystore MASTER KEY outbound. This operation replaces provenance of a public document with POSSESSION plus CHANNEL BINDING: the member signs, with the CA PRIVATE KEY, the triple `tvstor-join-attest-v1|<caFingerprint>|<serverCertSha256>|<nonce>`, where `serverCertSha256` is the TLS certificate THE MEMBER ITSELF SERVES. A relay terminates the joiner's connection with a different certificate, so the signed hash and the hash the joiner observes on its own socket disagree — and they disagree BEFORE the joiner has sent a secret byte. The private key never travels; only a signature over it does.
 *
 */
export const zInternalJoinAttestResponse = z.object({
    ca: z.object({
        certPem: z.string(),
        fingerprint: z.string().regex(/^[0-9a-f]{64}$/)
    }),
    serverCertSha256: z.string().regex(/^[0-9a-f]{64}$/),
    signature: z.string()
});

/**
 * La richiesta di emissione (PKI-1, 2026-08-09). Nessun segreto: una CSR e una firma di possesso.
 *
 */
export const zInternalPkiSignRequest = z.object({
    nodeName: z.string(),
    csrPem: z.string(),
    nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    signature: z.string()
});

export const zInternalPkiSignResponse = z.object({
    certPem: z.string(),
    caCertPem: z.string(),
    notAfter: z.iso.datetime()
});

/**
 * The joiner's half of the handshake (M5a, 2026-08-04), assembled by `db:init-join` from the operator-carried token and the node's own identity. The TOKEN is the sole credential: it names (via its pre-registration) which node may join, and its embedded CA fingerprint is the pin the joiner will verify the answer against.
 *
 */
export const zInternalJoinRequest = z.object({
    token: z.string(),
    nodeName: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    address: z.string().max(45),
    publicKey: z.string(),
    wrappingPublicKey: z.string(),
    wrappingMechanism: z.enum([
        'rsa-oaep-sha256',
        'rsa-oaep-sha1'
    ])
});

/**
 * A WHOLE-CONFIG snapshot at ONE generation (M5a, 2026-08-04) — the exact envelope the config replicator applies (`configReplication.ts`: generation + epoch + every `CONFIG_REPLICA_TABLES` table, rows verbatim in FK-safe apply order, taken in one REPEATABLE READ transaction). `db:init-join` seeds the replica from it in ONE local transaction with the watermark stamped alongside (`replica_state`), so the newborn replica is never torn and the first replicate tick is an ordinary catch-up, not a special case.
 *
 */
export const zConfigSnapshot = z.object({
    generation: z.int(),
    epoch: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    tables: z.record(z.string(), z.array(z.record(z.string(), z.unknown())))
});

/**
 * The member's answer (M5a, 2026-08-04) — everything the joiner needs to BE a member, in one shot: the CA (verified by the joiner against the token's out-of-band fingerprint BEFORE anything is written), the cluster identity to pin (`db:init-join` writes name + epoch + node name into the node PG membership pin), and the whole-config snapshot seeding the replica atomically. #59 S3 (join v2, 2026-08-20): the answer carries NO key material any more — the field `keystoreMasterKey` is GONE. The joiner's DEK envelopes (`cluster_key_wraps`, re-encrypted for the wrapping key the request presented, minted in the same transaction as the member flip) ride INSIDE the snapshot like every config-class table: the joiner resolves them from its own replica and opens them with its own custodian. Answering consumed the token and flipped the node row to `joinPending` in the same store transaction — a replayed request meets a dead token, and only the joiner's CONFIRM (the joinConfirm operation below, spoken after its own half succeeded) commits `joinPending→member` (JOIN-FANTASMA #203 §3.1, ratifica 2026-08-23 §1).
 *
 */
export const zInternalJoinResponse = z.object({
    cluster: z.object({
        id: z.optional(z.uuid()),
        name: z.string(),
        epoch: z.uuid()
    }),
    node: z.object({
        id: z.uuid(),
        name: z.string()
    }),
    ca: z.object({
        certPem: z.string(),
        fingerprint: z.string().regex(/^[0-9a-f]{64}$/)
    }),
    storeCredentials: z.optional(z.object({
        tvstor: z.string().min(1),
        tvstorRepl: z.string().min(1),
        storeSuperuser: z.optional(z.string().min(1))
    })),
    configSnapshot: zConfigSnapshot
});

/**
 * The joiner's CONFIRM (JOIN-FANTASMA #203 §3.1, ratifica 2026-08-23 §1) — the second half of the two-phase flip, spoken only AFTER the joiner's own half of the join succeeded: replica seeded from the snapshot, the unwrap proof passed, the membership pin written. No secret travels: a node name, a nonce and a possession signature — the same shape as `pki/sign` and `nas/idmap`.
 *
 */
export const zInternalJoinConfirmRequest = z.object({
    node: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    signature: z.string()
});

/**
 * The flip's second half, committed: the registry row is `member`. The same answer serves an idempotent replay (a row already `member` under a verifying proof) — a joiner that lost the first answer retries for free.
 *
 */
export const zInternalJoinConfirmResponse = z.object({
    node: z.string(),
    phase: z.enum([
        'member'
    ])
});

/**
 * La domanda che un gateway fa quando un soggetto di dominio si presenta per la prima volta (R5, #159 2026-08-16). Nessun segreto: un SID, una specie, un nonce e una firma di possesso.
 *
 */
export const zInternalNasIdmapRequest = z.object({
    node: z.string(),
    sid: z.string(),
    kind: z.optional(z.enum([
        'UID',
        'GID',
        'XID'
    ])),
    nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    signature: z.string()
});

/**
 * I numeri, LETTI SUBITO (R5): l'esito viaggia nella risposta e non solo nel commit, perche' chi ha chiesto sta servendo un accesso adesso e non puo' aspettare che la replica abbia girato.
 *
 */
export const zInternalNasIdmapResponse = z.object({
    realm: z.string(),
    sid: z.string(),
    uid: z.int(),
    gid: z.int(),
    kind: z.enum([
        'UID',
        'GID',
        'XID'
    ])
});

/**
 * The destination's answer to a fully received and committed delta stream (M4b, 2026-08-04).
 *
 */
export const zReplicationReceiveResult = z.object({
    clusterName: z.string(),
    volume: z.uuid(),
    toStamp: z.string().regex(/^[0-9a-f]{8}$/),
    bytesWritten: z.int()
});

/**
 * The destination's answer to an admitted internal promotion (#38 REP-PROMOZIONE, 2026-08-22). Like the receive's 200 it means ADMITTED — the promotion itself is the destination's `replication.promote` task (dated deviation of the receive route, shared honestly).
 *
 */
export const zReplicationPromoteResult = z.object({
    clusterName: z.string(),
    volume: z.uuid(),
    toStamp: z.string().regex(/^[0-9a-f]{8}$/)
});

/**
 * Body of `POST /api/v1/internal/replication/mirror/peer` (ENEA-A-R9): the SOURCE cluster's worker asks the destination to become the peer of one of its CephFS filesystems and to materialize the replica shares. The source cluster is the TOKEN's peer, never claimed here.
 *
 */
export const zReplicationMirrorPeerRequest = z.object({
    sourceLink: z.uuid(),
    sourceFileSystem: z.object({
        volume: z.uuid(),
        name: z.string()
    }),
    siteName: z.string().min(1).max(63),
    shares: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
        sizeGiB: z.int().gte(1)
    })).min(1).max(256)
});

export const zReplicationMirrorPeerOffer = z.object({
    offer: z.uuid(),
    clusterName: z.string()
});

export const zReplicationMirrorPeerToken = z.object({
    token: z.string(),
    remoteFileSystem: z.string(),
    clusterName: z.string(),
    msgr2Secure: z.boolean(),
    shares: z.array(z.object({
        sourceShare: z.uuid(),
        replicaShare: z.uuid()
    }))
});

/**
 * A CEPH CLUSTER (REMODEL #214, 2026-08-26 — the ceph half of the old datapool, now a first-class resource): a named set of `{node, vdisk}` members whose vdisks back one OSD each. The POST births the whole fabric (Annex N birth doctrine restored — no unbound state exists); volumes on this cluster are RBD images on the one `/volumes` surface.
 *
 */
export const zCephCluster = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    members: z.array(z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    })).min(1),
    encryption: z.optional(z.object({
        mode: z.enum([
            'none',
            'luks2'
        ])
    })),
    gatewayAddresses: z.optional(z.array(z.string().max(64)).max(16)),
    gatewayQos: z.optional(z.union([
        z.object({
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ])),
    gatewayAddressScopes: z.optional(z.array(z.object({
        address: z.string().max(64),
        scope: z.string()
    })).max(16)),
    gatewayAddressHolders: z.optional(z.array(z.object({
        address: z.optional(z.string()),
        node: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        since: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ])),
        leaseState: z.optional(z.enum([
            'fresh',
            'stale',
            'none'
        ])),
        scope: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        qosShaped: z.optional(z.union([
            z.boolean(),
            z.null()
        ]))
    })).readonly()),
    phase: z.optional(z.enum([
        'initializing',
        'ready',
        'degraded',
        'error',
        'destroying'
    ])),
    vdisks: z.optional(z.array(z.object({
        node: z.optional(z.string()),
        vdisk: z.optional(z.string()),
        state: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        sizeBytes: z.optional(z.union([
            z.number(),
            z.null()
        ])),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    capacityBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    usedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    provisionedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    promisedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    promisedNetBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    admittableBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    ceph: z.optional(z.union([
        z.object({
            fsid: z.optional(z.union([
                z.uuid(),
                z.null()
            ])),
            health: z.optional(z.nullable(z.enum([
                'healthy',
                'degraded',
                'error'
            ]))),
            mons: z.optional(z.array(z.object({
                node: z.optional(z.string()),
                inQuorum: z.optional(z.union([
                    z.boolean(),
                    z.null()
                ]))
            }))),
            osds: z.optional(z.array(z.object({
                node: z.optional(z.string()),
                vdisk: z.optional(z.string()),
                osdId: z.optional(z.union([
                    z.int(),
                    z.null()
                ])),
                up: z.optional(z.union([
                    z.boolean(),
                    z.null()
                ])),
                in: z.optional(z.union([
                    z.boolean(),
                    z.null()
                ])),
                deviceClass: z.optional(z.union([
                    z.string(),
                    z.null()
                ]))
            }))),
            mds: z.optional(z.array(z.object({
                name: z.optional(z.string()),
                filesystem: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                rank: z.optional(z.union([
                    z.int(),
                    z.null()
                ])),
                state: z.optional(z.union([
                    z.string(),
                    z.null()
                ]))
            }))),
            capacity: z.optional(z.union([
                z.object({
                    totalBytes: z.optional(z.union([
                        z.number(),
                        z.null()
                    ])),
                    usedBytes: z.optional(z.union([
                        z.number(),
                        z.null()
                    ])),
                    availBytes: z.optional(z.union([
                        z.number(),
                        z.null()
                    ]))
                }),
                z.null()
            ])),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zCephClusterList = z.object({
    items: z.array(zCephCluster),
    nextCursor: z.optional(z.string())
});

/**
 * Body of the `addMember` action — the ONE membership growth action: one new `{node, vdisk}` member = one new OSD.
 *
 */
export const zCephClusterAddMemberRequest = z.object({
    node: z.uuid(),
    vdisk: z.uuid()
});

/**
 * Body of the `destroy` action — the ceph teardown, carrying the full tvirt danger ladder (hci.yaml actionDestroy precedent): sudo + typed confirm + MANDATORY explicit `wipeDevices` with NO default — omitting it is `422 validation` (the "never destroys bytes silently" principle).
 *
 */
export const zCephClusterDestroyRequest = z.object({
    confirm: z.string(),
    wipeDevices: z.boolean()
});

/**
 * Body of the `migrateSecure` action (MSGR2-MIGRAZIONE, 2026-08-25 — docs/msgr2-migrazione-2026-08-25.md): sudo + typed confirm, nothing else — the migration has no knobs on purpose (the target is the ONE Fase-A secure form, byte-identical to a born-secure cluster).
 *
 */
export const zCephClusterMigrateSecureRequest = z.object({
    confirm: z.string()
});

/**
 * R4 EXPLORER (2026-08-26, remodel-disegno §2.5) — the mon's DETAILED health checks, not just the healthy/degraded/error mapping of the cluster render: what `ceph health detail` says, normalized. Served over the LEASE-FREE QUERY CHANNEL (the `volume.diff` precedent, tvirt `ceph.query` round #91): a read never takes the per-cluster metadata lease, so it neither blocks nor is blocked by create/destroy/failover. The GET enqueues the query on the cluster's control node and answers synchronously; `503` names the still-running task when the node worker does not answer within the deadline.
 *
 */
export const zCephHealthReport = z.object({
    status: z.string(),
    checks: z.array(z.object({
        code: z.string(),
        severity: z.string(),
        summary: z.string()
    }))
});

/**
 * R4 EXPLORER — ONE pool as the cluster reports it. The explorer shows ALL pools, system pools included (the truth); it is the VOLUMES surface that hides system pools, never this one (the dettatura in remodel-disegno §2.5). `volume`/`role` say who owns the pool: a per-volume materialization (`tvstor-vol-…`, `tvstor-fs-…-data/meta`, `tvstor-obj-…-data/index` — the owning volume resolved by its id tail) or `system` (mgr/rgw-internal/legacy debris).
 *
 */
export const zCephPoolExplorerEntry = z.object({
    name: z.string(),
    size: z.int(),
    minSize: z.int(),
    pgNum: z.int(),
    application: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    usage: z.optional(z.union([
        z.object({
            bytesUsed: z.optional(z.coerce.bigint()),
            objects: z.optional(z.int())
        }),
        z.null()
    ])),
    volume: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    role: z.enum([
        'volume',
        'system'
    ])
});

export const zCephPoolExplorerList = z.object({
    items: z.array(zCephPoolExplorerEntry)
});

/**
 * R4 EXPLORER — `rados ls` of one pool, PAGINATED (diagnostics: the drill-down of the pool view). The cursor is the last object name of the previous page (lexicographic resume); `prefix` filters server-side. DECLARED SEAM: the real lane reads the pool listing and slices — on a pool with millions of objects this is a diagnostic tool, not a data path (the S3/RBD surfaces are the data paths).
 *
 */
export const zCephPoolObjectsPage = z.object({
    items: z.array(z.object({
        name: z.string()
    })),
    nextCursor: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * R4 EXPLORER — one node of the DECODED crush tree (root / datacenter / host / osd, with weights): the view that makes the stretch layout (#204) readable at a glance.
 *
 */
export const zCephCrushNode = z.object({
    name: z.string(),
    type: z.string(),
    weight: z.number(),
    osdId: z.optional(z.union([
        z.int(),
        z.null()
    ])),
    get children() {
        return z.array(z.lazy((): any => {
            return zCephCrushNode;
        }));
    }
});

export const zCephCrushMap = z.object({
    tree: z.array(zCephCrushNode),
    rules: z.array(z.object({
        id: z.int(),
        name: z.string(),
        steps: z.array(z.string())
    }))
});

/**
 * R4 EXPLORER — the extended OSD census (`ceph osd df` + `ceph osd perf`): class, weight, reweight, up/in, utilization and latency — what an operator looks at before and after `out`/`in`.
 *
 */
export const zCephOsdExplorerEntry = z.object({
    id: z.int(),
    class: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    up: z.boolean(),
    in: z.boolean(),
    weight: z.number(),
    reweight: z.number(),
    node: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    utilization: z.optional(z.union([
        z.object({
            sizeBytes: z.optional(z.coerce.bigint()),
            usedBytes: z.optional(z.coerce.bigint()),
            percent: z.optional(z.number())
        }),
        z.null()
    ])),
    latency: z.optional(z.union([
        z.object({
            applyMs: z.optional(z.number()),
            commitMs: z.optional(z.number())
        }),
        z.null()
    ]))
});

export const zCephOsdExplorerList = z.object({
    items: z.array(zCephOsdExplorerEntry)
});

/**
 * R4 GESTIONE — cluster-wide OSD flags, the real-world maintenance verbs (a node reboot without a rebalance storm is `noout` — today impossible without a shell). One flag per call, value true = set, false = unset; sudo (danger ladder: these flags SUSPEND the cluster's self-healing).
 *
 */
export const zCephSetOsdFlagsRequest = z.object({
    flag: z.enum([
        'noout',
        'norebalance',
        'nobackfill',
        'norecover',
        'pause'
    ]),
    value: z.boolean()
});

/**
 * A MULTISCSI CLUSTER (REMODEL #214, 2026-08-26 — the multiscsi half of the old datapool, now a first-class resource): a named set of `{node, vdisk}` members, one VG + thin pool per member vdisk, thin-LV legs under cross-node md mirrors per volume. The POST births the whole fabric — no unbound state exists.
 *
 */
export const zMultiscsiCluster = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    members: z.array(z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    })).min(1),
    encryption: z.optional(z.object({
        mode: z.enum([
            'none',
            'luks2'
        ])
    })),
    phase: z.optional(z.enum([
        'initializing',
        'ready',
        'degraded',
        'error',
        'destroying'
    ])),
    scrub: z.optional(z.union([
        z.object({
            everyDays: z.int().gte(1).lte(365),
            startHour: z.int().gte(0).lte(23),
            durationHours: z.int().gte(1).lte(24),
            maxSpeedKiBps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ]))
        }),
        z.null()
    ])),
    vdisks: z.optional(z.array(z.object({
        node: z.optional(z.string()),
        vdisk: z.optional(z.string()),
        vg: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        state: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        sizeBytes: z.optional(z.union([
            z.number(),
            z.null()
        ])),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    capacityBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    usedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    provisionedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    promisedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    promisedNetBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    admittableBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    scrubStatus: z.optional(z.union([
        z.object({
            arrays: z.optional(z.array(z.object({
                node: z.optional(z.string()),
                kind: z.optional(z.enum([
                    'raid',
                    'volume'
                ])),
                array: z.optional(z.string()),
                vdisk: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                volume: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                level: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                action: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                requestedMode: z.optional(z.nullable(z.enum([
                    'check',
                    'repair'
                ]))),
                mismatchCount: z.optional(z.union([
                    z.int(),
                    z.null()
                ])),
                progressPercent: z.optional(z.union([
                    z.number(),
                    z.null()
                ])),
                startedAt: z.optional(z.union([
                    z.iso.datetime(),
                    z.null()
                ])),
                finishedAt: z.optional(z.union([
                    z.iso.datetime(),
                    z.null()
                ])),
                lastResult: z.optional(z.nullable(z.enum([
                    'completed',
                    'partial'
                ]))),
                partialReason: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                repairSemantics: z.optional(z.nullable(z.enum([
                    'arbitrary-copy',
                    'parity-recompute'
                ])))
            }))),
            skipped: z.optional(z.array(z.object({
                node: z.optional(z.string()),
                array: z.optional(z.string()),
                reason: z.optional(z.string())
            }))),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zMultiscsiClusterList = z.object({
    items: z.array(zMultiscsiCluster),
    nextCursor: z.optional(z.string())
});

/**
 * Body of the `addMember` action (#209 REP-S2S5 — the growth corsia, external vdisks included): one new `{node, vdisk}` member, converged by re-running the ensure-idempotent init family.
 *
 */
export const zMultiscsiClusterAddMemberRequest = z.object({
    node: z.uuid(),
    vdisk: z.uuid()
});

/**
 * Body of the `scrub` action (SCRUB-MS #94, ratifica Luca 2026-08-09 §9.1/§9.2): the ON-DEMAND half of scrubbing, "chi sospetta qualcosa deve poter guardare senza aspettare la finestra".
 *
 */
export const zMultiscsiClusterScrubRequest = z.object({
    mode: z.enum([
        'check',
        'repair'
    ]),
    confirm: z.optional(z.string())
});

/**
 * ENEA HP-SAN-1 (R19): the DESCRIPTIVE Lustre label of a volume exported to an I/O server pair — which target it is (`ost`, `mdt`, `mgt`) and its index (`OST0003` = `{role: ost, index: 3}`); the render, the CLI and the R20 runbook read it, nothing else does. Null = not a Lustre target. TWO ADMISSION RULES ride it (the coordinator's decisions, ratifica a posteriori 2026-10-24): `mdt` and `mgt` are the METADATA pool — `redundancy` must be REPLICATED with `copies: 3` and `deviceClass: nvme`, never erasure coded (`422 validation` naming the rule), and the class must exist on the cluster (no OSD of class `nvme` in the freshest census = `422` saying so: the metadata pool needs dedicated NVMe OSDs); `ost` accepts any redundancy (the offer's default is EC 4+2, R13).
 *
 */
export const zVolumeLustre = z.union([
    z.object({
        role: z.enum([
            'ost',
            'mdt',
            'mgt'
        ]),
        index: z.int().gte(0).lte(65535)
    }),
    z.null()
]);

/**
 * NAS-MDS-1 via B (2026-10-29; ratifica «Vada per B» 2026-10-27 §10, modello docs/nas-mds-1-2026-10-28.md §2.2): the CEPH FILE-PLANE options of a volume — they shape HOW a `filesystem` volume materializes on a ceph cluster and mean nothing elsewhere. THE DEFAULT (absent, null, or both flags false): the volume is a subvolume GROUP `vol-<vol8hex>` of the cluster's ONE CephFS `tvstor-fs-<cluster8hex>` (metadata pool `-meta` and default data pool `-data`, both replicated 3/2 — cluster fabric, born with the cluster's first file share, gone with the cluster's destroy), with the group's QUOTA = `sizeGiB` (the volume's capacity is enforced by Ceph, and `actions/expand` grows it); a volume whose `redundancy` is not the default 3/2 — erasure coded included — gets its OWN data pool `tvstor-fs-<cluster8hex>-<vol8hex>-data` (or `-ec`) laid on its group, so `redundancy` keeps its per-volume meaning. The shares are subvolumes INSIDE the volume's group (`/volumes/vol-<vol8hex>/<share uuid>`), served by the cluster's MDS set (one MDS per member node, kept by the reconciler at every pass — also for nodes that join later —, one active rank per filesystem plus standby-replay). `dedicated: true` = today's model kept on request: a CephFS of the volume's own `tvstor-fs-<vol8hex>` with its own pools sized by its redundancy — for strong isolation (cephx `fsname=`), or a mirror peer DIFFERENT from the cluster filesystem's (ReplicationLink file: one peer per filesystem). `dedicatedRank: true` = the group's tree pinned (`ceph.dir.pin`) to an MDS rank of ITS OWN on the cluster filesystem (`max_mds` grows by one; `pinnedRank` says which rank, fixed at materialization). Both flags together are `422 validation` (a dedicated filesystem already has a rank of its own). Both COUNT in the standby budget of `422 mds_standby_exhausted`: N member nodes serve at most N−1 of {the cluster filesystem, dedicated filesystems, pinned ranks}. Ceph only (`422 validation` on a multiscsi cluster); writable while the volume is `declared`, IMMUTABLE once materialized (`422 immutable_field` naming `cephfs` on PATCH). Volumes materialized BEFORE this round keep the filesystem they were born with — rendered `dedicated: true, inherited: true`, no byte moves at the upgrade — and move to the cluster filesystem only through the explicit `POST /volumes/{volume}/actions/migrateToSharedFilesystem`.
 *
 */
export const zVolumeCephfs = z.union([
    z.object({
        dedicated: z.optional(z.boolean()),
        dedicatedRank: z.optional(z.boolean()),
        inherited: z.optional(z.boolean().readonly()),
        pinnedRank: z.optional(z.union([
            z.int().gte(1).readonly(),
            z.null()
        ]).readonly())
    }),
    z.null()
]);

/**
 * ENEA HP-SAN-1 (R19): WHO HOLDS THE IMAGE — observed by the control node's census (`rbd status` + `rbd lock ls`, `volume_status.rbd`) for volumes exported to an I/O server pair; null when not exported or not observed yet. `lockOwner` is the `locker` of the exclusive lock (`client.<id>` of the writing server — the one Lustre has the target mounted on); `watchers` are the open rbd sessions (address `ip:port/nonce`, cookie), with or without the lock: the «who is writing» view before and after a failover or a fence.
 *
 */
export const zVolumeHolder = z.union([
    z.object({
        lockOwner: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        watchers: z.optional(z.array(z.object({
            address: z.string(),
            cookie: z.string()
        }))),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    }).readonly(),
    z.null()
]).readonly();

/**
 * A volume — the PROMISE of capacity with a redundancy policy (REMODEL R2 #214, 2026-08-26, docs/remodel-disegno-2026-08-26.md §2.3: the DECLARED model). The volume names its STORAGE CLUSTER (a CephCluster or a MultiscsiCluster — mandatory and immutable) and is born as PURE BUREAUCRACY: a `201` synchronous row, admitted by the anti-overcommit formula, with NOTHING on any device. The bytes rise LAZILY at the FIRST CONSUMER — a Target fixes the species `lun` (multiscsi: the MD RAID1 mirror over thin-LV legs; ceph: the volume's OWN pool `tvstor-vol-<vol8hex>` sized by its redundancy + the RBD image); the internal FileShare/ObjectStore backing births fix `filesystem`/`object` — and the materialization SURVIVES its consumer: only the volume's own delete removes the bytes. `redundancy {copies, minInSync}` is UNIFORM on both kinds (the policy is the contract; each materialization interprets it — mirror legs vs pool size/min_size). No volume field names a kind (architecture §5.2, carried through every remodel).
 *
 */
export const zVolume = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    cluster: z.uuid(),
    redundancy: z.object({
        copies: z.optional(z.int().gte(1)),
        minInSync: z.optional(z.int().gte(1)),
        deviceClass: z.optional(z.string().min(1).max(32).regex(/^[a-z0-9][a-z0-9_-]{0,31}$/)),
        erasure: z.optional(z.object({
            k: z.int().gte(2).lte(16),
            m: z.int().gte(1).lte(8)
        }))
    }),
    rawBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    sizeGiB: z.int().gte(1),
    qos: z.optional(z.union([
        z.object({
            iopsLimit: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            mbpsLimit: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ]))
        }),
        z.null()
    ])),
    ioServerPair: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    lustre: z.optional(zVolumeLustre),
    cephfs: z.optional(zVolumeCephfs),
    holder: z.optional(zVolumeHolder),
    phase: z.optional(z.enum([
        'declared',
        'materializing',
        'ready',
        'degraded',
        'blocked',
        'error',
        'deleting'
    ])),
    materialization: z.optional(z.union([
        z.object({
            specie: z.optional(z.enum([
                'lun',
                'filesystem',
                'object'
            ])),
            materializedAt: z.optional(z.iso.datetime()),
            pool: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            image: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            md: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            filesystem: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            subvolumeGroup: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            path: z.optional(z.union([
                z.string(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    authorityNode: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    authority: z.optional(z.union([
        z.object({
            node: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            leaseExpiresAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ])),
            epoch: z.optional(z.int()),
            phase: z.optional(z.enum([
                'held',
                'stale',
                'failingOver'
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    failoverHealth: z.optional(z.union([
        z.object({
            witnessReachable: z.optional(z.union([
                z.boolean(),
                z.null()
            ])),
            peerFencingReachable: z.optional(z.union([
                z.boolean(),
                z.null()
            ])),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    legs: z.optional(z.array(z.object({
        vdisk: z.optional(z.string()),
        node: z.optional(z.string()),
        vg: z.optional(z.string()),
        lv: z.optional(z.string()),
        state: z.optional(z.string()),
        at: z.optional(z.iso.datetime()),
        writeMostly: z.optional(z.boolean()),
        export: z.optional(z.object({
            nqn: z.string(),
            nsid: z.int().gte(1),
            exported: z.boolean(),
            at: z.iso.datetime()
        }).readonly()),
        fenced: z.optional(z.boolean()),
        fencedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    targets: z.optional(z.array(z.object({
        target: z.optional(z.uuid()),
        targetName: z.optional(z.string())
    })).readonly()),
    mdState: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    replica: z.optional(z.union([
        z.object({
            sourceCluster: z.optional(z.string()),
            sourceVolume: z.optional(z.uuid()),
            lastReceivedStamp: z.optional(z.union([
                z.string().regex(/^[0-9a-f]{8}$/),
                z.null()
            ])),
            receivedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    torn: z.optional(z.union([
        z.object({
            taskId: z.optional(z.union([
                z.uuid(),
                z.null()
            ])),
            node: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            since: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    sync: z.optional(z.union([
        z.object({
            remoteCluster: z.optional(z.string()),
            remoteVdisk: z.optional(z.uuid()),
            witnessEndpoints: z.optional(z.array(z.string())),
            degradeActive: z.optional(z.boolean())
        }).readonly(),
        z.null()
    ]).readonly()),
    adopted: z.optional(z.union([
        z.object({
            backingVolume: z.optional(z.uuid()),
            sourceCluster: z.optional(z.string()),
            adoptedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    fileShare: z.optional(z.union([
        z.uuid().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zVolumeList = z.object({
    items: z.array(zVolume),
    nextCursor: z.optional(z.string())
});

/**
 * Clone provenance (M2b, PROPOSAL §6.4 2026-08-02 — conceptually untouched by M2d, naming moved to per-vdisk VGs 2026-08-03). MULTISCSI: the new volume's legs are born as WRITABLE thin snapshots of the source snapshot's per-leg snap LVs — same VGs, hence same vdisks as the source volume — and a FRESH md array is created over them (own name/uuid, `tvstor-<vol8hex>` of the NEW volume). NO CHAIN DEPENDENCY (declared property of THAT engine, LVM thin construction): thin-pool blocks are shared for space efficiency but there is no qcow2-style backing chain — deleting the source snapshot, or the source volume itself, NEVER invalidates a clone. CEPH (M3, 2026-08-03 — THE DOCUMENTED SEMANTIC DIFFERENCE): the clone is an RBD CLONE from the source snapshot, which the executor PROTECTS first (`rbd snap protect` — required by RBD before cloning) — a REAL DEPENDENCY EDGE exists: the child image references the protected snapshot, so deleting that snapshot answers `409 in_use` naming its clones (and the source volume's delete cascade hits the same wall) until the clones are deleted; automatic flattening was deliberately NOT chosen (PROPOSAL 2026-08-03: `rbd flatten` copies the full provisioned size — a silent space explosion; an explicit flatten action is a later round if a use case appears). Same-engine admission everywhere: requires `volume.snapshot.read` on the source volume's scope in addition to `volume.create` on the target, needs the snapshot `ready`, and rides the datapool's metadata lease (`refuseMetadataMutationsOnSameDatapool` — `409 conflict`).
 *
 */
export const zVolumeSource = z.object({
    snapshot: z.uuid()
});

/**
 * Creation body of `POST /api/v1/volumes` — TWO variants since M2b (PROPOSAL §6.4, 2026-08-02; REMODEL R2 #214 reshapes both). PLAIN CREATE: pure bureaucracy — `cluster`, `sizeGiB` and `redundancy` are REQUIRED on BOTH kinds (the policy is uniform; the per-kind bounds are admission's), the anti-overcommit admission runs, and the row is born `declared` with a synchronous `201` and NO task: nothing touches a device until the first consumer. CLONE (`source` present): the new volume is born from an existing snapshot and therefore born MATERIALIZED (the clone task copies real bytes — species inherited from the source volume): `cluster`, `redundancy` and `sizeGiB` are INHERITED from the source volume; any of them sent alongside `source` is `422 validation` (`cluster`, if sent, must equal the source volume's — clone legs/images live beside their source by construction). The clone pays the SAME anti-overcommit admission (its inherited weight counts). The readOnly fields of `Volume` stay known-and-ignored on input (round #149 strictness rule).
 *
 */
export const zVolumeCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.optional(z.string().max(255)),
    notes: z.optional(z.string().max(65536)),
    cluster: z.optional(z.uuid()),
    redundancy: z.optional(z.object({
        copies: z.optional(z.int().gte(1)),
        minInSync: z.optional(z.int().gte(1)),
        erasure: z.optional(z.object({
            k: z.int().gte(2).lte(16),
            m: z.int().gte(1).lte(8)
        })),
        deviceClass: z.optional(z.string().min(1).max(32).regex(/^[a-z0-9][a-z0-9_-]{0,31}$/))
    })),
    sizeGiB: z.optional(z.int().gte(1)),
    qos: z.optional(z.union([
        z.object({
            iopsLimit: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            mbpsLimit: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ]))
        }),
        z.null()
    ])),
    ioServerPair: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    lustre: z.optional(zVolumeLustre),
    cephfs: z.optional(zVolumeCephfs),
    sync: z.optional(z.union([
        z.object({
            remoteCluster: z.string().min(1),
            remoteVdisk: z.uuid(),
            witnessEndpoints: z.optional(z.array(z.string().min(1)))
        }),
        z.null()
    ])),
    source: z.optional(zVolumeSource)
});

/**
 * ENEA-A-R5 (2026-10-23): the FROZEN RETENTION of a snapshot — block (`VolumeSnapshot`) and file (`FileShareSnapshot`) alike. Chosen at creation (`none` when omitted); while `retainUntil` is in the future the snapshot's delete, the destruction of the data under it and any destructive revert/clone answer `409 retention_active` naming the date, the sweeper and the internal cadences (the `@GMT-` previous versions of #177, their `keep` pruning) SKIP it and say so in a line, and a PATCH may only EXTEND it (`none → retainUntil`, or a later date) — shortening or removing a running retention is `422 retention_frozen`, not even with sudo. A RETENTION OF THE PRODUCT: it is an admission refusal, not a lock of the storage layer (an operator with the Ceph or LVM CLI can still remove the snapshot) — the WORM that survives the operator is SEC-WORM-1's tape copy. Bounds: at least one minute, at most one hundred years from now.
 *
 */
export const zSnapshotRetention = z.object({
    mode: z.enum([
        'none',
        'retainUntil'
    ]),
    retainUntil: z.optional(z.iso.datetime())
});

/**
 * A point-in-time snapshot of a volume (M2b, PROPOSAL §6.4 2026-08-02 — modeled on the TVirt Annex E wave B precedent, thin snapshot with stamp and LVM tags, adapted to md legs; conceptually UNTOUCHED by M2d 2026-08-03 — only the VGs the leg snaps live in are per-vdisk now): ONE thin snapshot of EVERY leg, taken under ONE shared stamp — leg snap LV `<volumeUuid>-l<index>-s<stamp>` on the leg's own VG, LVM tags `tvstor.snapshotOf=<volumeId>` + `tvstor.snapshotId=<snapshotId>` as the on-device identity. Instant and space-efficient by thin-pool construction; with synchronous RAID1 the legs are identical at every acknowledged write, so an unexported volume's snapshot is trivially consistent. EXPORTED VOLUMES (M2c wording updated to targets 2026-08-03, PROPOSAL pending ratification with Luca): a snapshot of a volume with an active target is declared CRASH-CONSISTENT — the point in time is what a consumer would find after a power cut (in-flight, unacked writes may be absent; filesystems must replay their journal). The §6.4 quiesce OPEN QUESTION stays open (dated 2026-08-02): data-plane quiesce at snapshot time — brief ack suspension on the target vs single-leg snapshot behind a write-intent-bitmap sync barrier — flagged to Luca; crash-consistent semantics ship, not silence. SHARE-BACKED VOLUMES (NAS-1, 2026-08-04 — the FILE half of §6.4 answered, `fileshares.yaml`): when the volume is a FileShare's backing volume with its filesystem mounted, the snapshot task FSFREEZES the mount on the authority node first — freeze → snapshot → thaw — so the copy is FS-CONSISTENT (journal quiesced, no replay needed), not merely crash-consistent; the freeze is a brief write stall the file clients ride out (NFS hard-mount retry, SMB reconnection — same experience as a failover blip). The BLOCK case (exported volumes) is untouched — the §6.4 question stays open there: the platform cannot freeze a filesystem a consumer mounts. Config-class row (`volume_snapshots`, replicated); the observed fields compose from the snapshot census the worker sweep writes into `volume_status` (staleness visible, never hidden). Snapshots are SUBRESOURCES, not scoped objects — no scope/tags/notes; `name` is unique PER VOLUME (PROPOSAL 2026-08-02: the round-#105 scope-wide name rule applies to top-level kinds only). Schedules and retention are M4 (dated fence 2026-08-02). CEPH DATAPOOLS (M3, 2026-08-03): the snapshot is an RBD SNAPSHOT of the volume's image — `<volumeUuid>@s<stamp>` (the SAME stamp scheme: one mint, one naming module) — instant and space-efficient by RADOS construction; the census comes from `rbd snap ls` via the `ceph_status` sweep. A snapshot PROTECTED for clones (see `VolumeSource`) carries the dependency edge: its delete is `409 in_use` naming the clones — the ceph-only difference from MultiSCSI's no-chain property, documented, never hidden.
 *
 */
export const zVolumeSnapshot = z.object({
    id: z.uuid().readonly(),
    volume: z.optional(z.uuid().readonly()),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    stamp: z.optional(z.string().regex(/^[0-9a-f]{8}$/).readonly()),
    retention: z.optional(zSnapshotRetention),
    phase: z.optional(z.enum([
        'pending',
        'ready',
        'error',
        'deleting'
    ])),
    state: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    createdAt: z.optional(z.iso.datetime().readonly()),
    revision: z.optional(z.int().readonly())
});

export const zVolumeSnapshotList = z.object({
    items: z.array(zVolumeSnapshot),
    nextCursor: z.optional(z.string())
});

/**
 * Body of the revert action (M2b, PROPOSAL §6.4 2026-08-02) — CONFIRM-TYPED: destructive enough to demand the volume's name typed back, deliberately NOT sudo-protected (PROPOSAL 2026-08-02: revert is an operator storage workflow, not an identity-plane mutation).
 *
 */
export const zVolumeRevertRequest = z.object({
    snapshot: z.uuid(),
    confirm: z.string()
});

/**
 * Body of the manual failover action (M2e, 2026-08-03) — the operator-triggered path of the same `volume.failover` machinery the automatic detection uses. All fields optional; an empty body lets placement choose the new authority.
 *
 */
export const zVolumeFailoverRequest = z.object({
    targetNode: z.optional(z.string())
});

/**
 * Body of the forcePromote action (M2e, ratifica fence-before-degrade 2026-08-03) — the operator's DECLARED DATA-LOSS decision: promote a STALE-FENCED leg, i.e. restart the volume from a leg known to miss writes that were ACKED to the client after its fence. Sudo-protected AND confirm-typed: destructive twice over.
 *
 */
export const zVolumeForcePromoteRequest = z.object({
    leg: z.int().gte(0),
    confirm: z.string()
});

/**
 * Body of the DR replica EMERGENCY promotion (#38 REP-PROMOZIONE, 2026-08-22 — docs/rep-promozione-2026-08-22.md): the destination operator's declared decision to make the replica's last committed chain a servable primary volume. Sudo-protected AND confirm-typed (the promote-store/refound idiom), and the point in time being promoted is ACKNOWLEDGED explicitly — the consumed RPO is declared, never implicit.
 *
 */
export const zVolumePromoteRequest = z.object({
    confirm: z.string(),
    acknowledgeStamp: z.string().regex(/^[0-9a-f]{8}$/),
    name: z.optional(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/))
});

/**
 * Body of the SYNC DEGRADE opt-in (#39 REP-SYNC-MS, 2026-08-22): the operator's declared decision to serve a sync volume WITHOUT its remote leg — availability bought by breaking the RPO=0 promise, said out loud. Sudo-protected AND confirm-typed (the promote idiom): breaking a declared promise is never one click.
 *
 */
export const zVolumeSyncDegradeRequest = z.object({
    confirm: z.string()
});

/**
 * Body of the SYNC RE-PROTECTION verb (#209 REP-S2S5, 2026-08-24 — PROPOSAL attuata: the sync declaration was creation-only until this round; the reversal NEEDS an attach-time declaration, because the adopted volume already exists and copying it into a fresh sync birth would betray the adoption). Sudo-protected AND confirm-typed: the verb commits the volume to the blocked-default RPO=0 semantics AND declares immediate degraded service (the seed window).
 *
 */
export const zVolumeSyncAttachRequest = z.object({
    confirm: z.string(),
    remoteCluster: z.string().min(1),
    remoteVdisk: z.uuid(),
    witnessEndpoints: z.optional(z.array(z.string().min(1)))
});

/**
 * Body of the ONLINE EXPANSION action (M5d, 2026-08-04 — brochure "espansione online: i volumi crescono a caldo, senza smontare nulla"). GROW ONLY: the new size must be STRICTLY GREATER than the current one — an equal or smaller value is `422 validation` with a speaking message. SHRINK (#34, ratifica 2026-08-28) is its OWN sudo + confirm-typed action (`actions/shrink`) with the ratified v1 perimeter — never this one, never PATCH: block shrink of a materialized lun stays OUT of v1 (a thin volume that shrank would tear the consumer's filesystem out from under it — the declared reason both actions repeat).
 *
 */
export const zVolumeExpandRequest = z.object({
    sizeGiB: z.int().gte(2)
});

/**
 * Body of the SHRINK action (#34, ratifica 2026-08-28 — the missing verse of the resize, perimeter ratified): sudo + CONFIRM-TYPED (the volume's current name typed back — the revert doctrine, D3: one surface for every branch, coherence over convenience). The new size must be STRICTLY SMALLER than the current one (`422 validation` speaking — grow is `actions/expand`).
 *
 */
export const zVolumeShrinkRequest = z.object({
    sizeGiB: z.int().gte(1),
    confirm: z.string()
});

/**
 * Body of the CHANGED BLOCK TRACKING action (M4a, 2026-08-04, docs/cbt-snapdiff-design-2026-08-04.md) — the two ends of the comparison. Both ends are addressed the way every item URL in this API is: by id (UUIDv7), plus the ONE literal `head` for the live volume. Engine-neutral by construction — nothing here names an engine, and the same body answers identically on a multiscsi and on a ceph datapool.
 *
 */
export const zVolumeDiffRequest = z.object({
    from: z.uuid(),
    to: z.string(),
    format: z.optional(z.enum([
        'extents'
    ]))
});

/**
 * Body of `actions/attachToIoServerPair` (ENEA HP-SAN-1, R19): the pair to export the volume to. Sudo-protected (the volume becomes reachable by the pair's servers from the next caps render).
 *
 */
export const zVolumeAttachIoServerPairRequest = z.object({
    pair: z.uuid()
});

/**
 * An initiator group (architecture §5.1, M2c 2026-08-02): the named consumer identity carrying the initiator IQNs of one client machine. Listing a host on a target (`targets.yaml` — the M2d masking, ratifica D-D 2026-08-03) creates the per-initiator ACLs on that target on EVERY datapool member node (D3 mirrored identity, ratifica 2026-08-02). ACCESS CONTROL (updated M2g, 2026-08-03): the ACL is always by IQN — an IQN is an identity claim, not a secret; since M2g the TARGET may additionally require CHAP (`Target.chap`, an `iscsiChap` keystore reference — per-export, not per-host). Without CHAP, deploy consumers on a trusted data network accordingly.
 *
 */
export const zHost = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    iqns: z.array(z.string().min(1).max(223).regex(/^(iqn\.[0-9]{4}-[0-9]{2}\.[a-z0-9.-]+(:[^ \t]+)?|eui\.[0-9A-Fa-f]{16}|naa\.[0-9A-Fa-f]{16,32})$/)).min(1).max(32),
    revision: z.optional(z.int().readonly())
}));

export const zHostList = z.object({
    items: z.array(zHost),
    nextCursor: z.optional(z.string())
});

/**
 * A named consumer export (ratifica D-D, 2026-08-03; `protocol` since M3, 2026-08-03): exposes exactly ONE volume to the hosts of its `hosts` list. `protocol: iscsi` — an iSCSI target serving the volume at LUN 0 on the portals of EVERY datapool member node (D3 mirrored identity), masked to the initiator IQNs of its `hosts` (ACL = the UNION of the hosts' `iqns`). An EMPTY `hosts` list means NO ACLs — and LIO with ACLs-only authentication means NO initiator can log in: an unhosted target exposes nothing (documented rule, 2026-08-03). `protocol: rbd` (M3, ceph-datapool volumes only) — a NATIVE RBD export: one platform-minted, pool/image-capped cephx identity per host, mount info on the readOnly `rbd` block; an empty `hosts` list mints nothing — the same parked-export rule. PROPOSAL (2026-08-03): unlike the M2c mapping edges, the target is a first-class SCOPED resource (scope/tags/notes via ResourceCommon) — it is the object operators name, list and grant on; admission still cross-checks `volume.read` on the volume's scope and `host.read` on each host's scope.
 *
 */
export const zTarget = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    volume: z.uuid(),
    protocol: z.optional(z.enum([
        'iscsi',
        'rbd',
        'lustre'
    ])),
    lustreRole: z.optional(z.nullable(z.enum([
        'mgt',
        'mdt',
        'ost'
    ]))),
    lustreIndex: z.optional(z.union([
        z.int().gte(0),
        z.null()
    ])),
    fsname: z.optional(z.union([
        z.string().max(8).regex(/^[a-zA-Z][a-zA-Z0-9]*$/),
        z.null()
    ])),
    hosts: z.array(z.uuid()),
    chap: z.optional(z.union([
        z.object({
            credential: z.uuid(),
            mutual: z.optional(z.boolean()).default(false)
        }),
        z.null()
    ])),
    iqn: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    rbd: z.optional(z.union([
        z.object({
            monitors: z.optional(z.array(z.string())),
            pool: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            image: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            clients: z.optional(z.array(z.object({
                host: z.optional(z.uuid()),
                hostName: z.optional(z.string()),
                entity: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                credential: z.optional(z.union([
                    z.uuid(),
                    z.null()
                ]))
            })))
        }).readonly(),
        z.null()
    ]).readonly()),
    lustre: z.optional(z.union([
        z.object({
            fsname: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            role: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            index: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            mgsnode: z.optional(z.array(z.string())),
            mountPoint: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            recovery: z.optional(z.union([
                z.object({
                    status: z.optional(z.union([
                        z.string(),
                        z.null()
                    ])),
                    completed: z.optional(z.union([
                        z.int(),
                        z.null()
                    ])),
                    total: z.optional(z.union([
                        z.int(),
                        z.null()
                    ])),
                    observedAt: z.optional(z.union([
                        z.iso.datetime(),
                        z.null()
                    ]))
                }),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    paths: z.optional(z.array(z.object({
        node: z.optional(z.string()),
        portal: z.optional(z.string()),
        alua: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zTargetList = z.object({
    items: z.array(zTarget),
    nextCursor: z.optional(z.string())
});

/**
 * Creation body of `POST /api/v1/targets` (`create-iscsi-target <name> --volume <vol> [--hosts h1,h2]`, dettatura 2026-08-03; the M3 `protocol: rbd` variant is the `ceph` CLI namespace's export verb, 2026-08-03). `hosts` is optional and defaults to the empty list — a parked export with no access (no ACL = no access, documented rule; no minted cephx identity for `rbd`). The readOnly fields of `Target` stay known-and-ignored on input (round #149 strictness rule).
 *
 */
export const zTargetCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.optional(z.string().max(255)),
    notes: z.optional(z.string().max(65536)),
    volume: z.uuid(),
    protocol: z.optional(z.enum([
        'iscsi',
        'rbd',
        'lustre'
    ])),
    lustreRole: z.optional(z.nullable(z.enum([
        'mgt',
        'mdt',
        'ost'
    ]))),
    lustreIndex: z.optional(z.union([
        z.int().gte(0),
        z.null()
    ])),
    fsname: z.optional(z.union([
        z.string().max(8).regex(/^[a-zA-Z][a-zA-Z0-9]*$/),
        z.null()
    ])),
    hosts: z.optional(z.array(z.uuid())),
    chap: z.optional(z.union([
        z.object({
            credential: z.uuid(),
            mutual: z.optional(z.boolean()).default(false)
        }),
        z.null()
    ]))
});

export const zIoServerPairMember = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    addresses: z.array(z.string().max(64)).min(1).max(8),
    fence: z.optional(z.union([
        z.object({
            state: z.optional(z.enum([
                'none',
                'fenced'
            ])),
            at: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ])),
            by: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            blocklisted: z.optional(z.array(z.string()))
        }).readonly(),
        z.null()
    ]).readonly())
});

/**
 * A pair (2..4 members) of Lustre I/O servers as ONE native rbd consumer of a Ceph cluster: one cephx identity, caps confined to the pools of the volumes exported to it, fence from the SAN side per member. Scoped resource (ResourceCommon); admission cross-checks `cephcluster.read` on the cluster.
 *
 */
export const zIoServerPair = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    cluster: z.uuid(),
    members: z.array(zIoServerPairMember).min(2).max(4),
    lustre: z.optional(z.union([
        z.object({
            filesystem: z.optional(z.string().min(1).max(8))
        }),
        z.null()
    ])),
    entity: z.optional(z.string().readonly()),
    credential: z.optional(z.union([
        z.uuid().readonly(),
        z.null()
    ]).readonly()),
    caps: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    volumes: z.optional(z.array(z.object({
        id: z.uuid(),
        name: z.string(),
        lustre: z.optional(zVolumeLustre),
        holder: z.optional(zVolumeHolder)
    })).readonly()),
    credentialRotatedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    metadataDeviceClass: z.optional(z.string().min(1).max(32).regex(/^[a-z0-9][a-z0-9_-]{0,31}$/)).default('nvme'),
    phase: z.optional(z.enum([
        'pending',
        'ready',
        'error',
        'deleting'
    ])),
    revision: z.optional(z.int().readonly())
}));

export const zIoServerPairList = z.object({
    items: z.array(zIoServerPair),
    nextCursor: z.optional(z.string())
});

/**
 * The pair's rbd client identity, ready to install on BOTH servers of the pair (R20 runbook: `/etc/ceph/ceph.conf` and `/etc/ceph/ceph.<entity>.keyring`, mode 0600). The ONE read that hands the secret out: sudo-protected and registered as an R11 SENSITIVE READ (`AuditEvent.kind: sensitiveRead`) — the audit line says who read which pair's key, when. Nothing here is stored in the task log or the render.
 *
 */
export const zIoServerPairCredential = z.object({
    entity: z.string(),
    key: z.string(),
    fsid: z.string(),
    monitors: z.array(z.object({
        name: z.string(),
        address: z.string()
    })),
    keyringText: z.string(),
    cephConfText: z.string()
});

/**
 * Body of `actions/rotateCredential` — sudo + confirm-typed. The rotation is «new key, the servers receive it»: Ceph has no double key, so the old key stops working the moment the new one is minted (declared; the R20 runbook rotates one member at a time with the pair fenced on the SAN side if needed).
 *
 */
export const zIoServerPairRotateRequest = z.object({
    confirm: z.string()
});

/**
 * Body of `actions/fence` and `actions/unfence` — sudo + CONFIRM-TYPED (the pair's current name typed back: the revert doctrine, D3). `member` is the member's name as declared in `members[]`.
 *
 */
export const zIoServerPairFenceRequest = z.object({
    member: z.string(),
    confirm: z.string()
});

/**
 * The catalog policy of a source — the same leaf as `FileShare.catalog` (ENEA-A-R10) with two differences, both from the nature of a mount: `mode` admits only `auto` and `full` (both = a FULL scan by mtime; `incremental` is `422 catalog_mode_not_supported`: a mount has no recursive ctime, and an mtime-only pass would miss deletions — the incremental way for Lustre is its changelog, declared), and `maxEntries` null = 5 000 000 (a Lustre client can see billions of entries; past the cap the scan closes `partial` and says so, as in R10). Absent/null = NO catalog (opt-in).
 *
 */
export const zCatalogSourcePolicy = z.union([
    z.object({
        enabled: z.boolean(),
        everyMinutes: z.optional(z.union([
            z.int().gte(15).lte(10080),
            z.null()
        ])),
        mode: z.optional(z.nullable(z.enum([
            'auto',
            'full',
            'incremental'
        ]))),
        fullEvery: z.optional(z.union([
            z.int().gte(1).lte(365),
            z.null()
        ])),
        maxEntries: z.optional(z.union([
            z.int().gte(1000).lte(1000000000),
            z.null()
        ]))
    }),
    z.null()
]);

/**
 * A filesystem already mounted on one node, catalogued by that node's worker (full scans by mtime) into the node's catalog database. Scoped resource (ResourceCommon). `node` and `mountPath` are IMMUTABLE after creation (`422 immutable_field`): the mount belongs to the node.
 *
 */
export const zCatalogSource = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    node: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    mountPath: z.string().min(1).max(4096),
    subPath: z.string().max(1024).default(''),
    excludes: z.array(z.string().min(1).max(1024)).max(64).default([]),
    catalog: z.optional(zCatalogSourcePolicy),
    fstype: z.string().readonly(),
    fsSource: z.string().readonly(),
    revision: z.optional(z.int().readonly())
}));

export const zCatalogSourceList = z.object({
    items: z.array(zCatalogSource),
    nextCursor: z.optional(z.string())
});

/**
 * Body of `POST /catalogSources`. Admission (pure, common `catalogSourceAdmissionOf`): the node exists, is `member` and not in maintenance; its mount census exists and is at most 10 minutes old; `mountPath` equals a census target; it is outside the reserved trees; `subPath` and `excludes` are well formed; `catalog.mode` is not `incremental`. `fstype`/`fsSource` are photographed from the census.
 *
 */
export const zCatalogSourceCreate = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.optional(z.string().max(255)),
    node: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    mountPath: z.string().min(1).max(4096),
    subPath: z.optional(z.string().max(1024)).default(''),
    excludes: z.optional(z.array(z.string().min(1).max(1024)).max(64)),
    catalog: z.optional(zCatalogSourcePolicy),
    notes: z.optional(z.string().max(65536))
});

/**
 * Merge-patch body of `PATCH /catalogSources/{source}`: `name`, `subPath`, `excludes`, `catalog`, `notes`. `node` and `mountPath` are NOT here — a patch naming them is `422 immutable_field` (delete and recreate). The patch RE-ADMITS the source against the node's current census (the same rules as creation): a source whose mount disappeared cannot be re-shaped until it is back, and says why.
 *
 */
export const zCatalogSourceUpdate = z.object({
    name: z.optional(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)),
    subPath: z.optional(z.string().max(1024)),
    excludes: z.optional(z.array(z.string().min(1).max(1024)).max(64)),
    catalog: z.optional(zCatalogSourcePolicy),
    notes: z.optional(z.string().max(65536))
});

/**
 * ENEA-A-R10: one catalogued filesystem entry of a share, as the crawler saw it at `seenAt` (an `lstat` on the mounted share). Paths are relative to the share root, without a leading slash; names that were not valid UTF-8 carry U+FFFD replacements (declared).
 *
 */
export const zCatalogEntry = z.object({
    path: z.string(),
    name: z.string(),
    type: z.enum([
        'file',
        'dir',
        'symlink',
        'other'
    ]),
    size: z.int(),
    blocks: z.int(),
    uid: z.int(),
    gid: z.int(),
    mode: z.int(),
    nlink: z.int(),
    inode: z.int(),
    atime: z.iso.datetime(),
    mtime: z.iso.datetime(),
    ctime: z.iso.datetime(),
    extension: z.union([
        z.string(),
        z.null()
    ]),
    depth: z.int(),
    seenAt: z.iso.datetime()
});

export const zCatalogEntryList = z.object({
    items: z.array(zCatalogEntry),
    nextCursor: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * One scan of the share's catalog (a `catalog_scans` row).
 */
export const zCatalogScan = z.object({
    id: z.uuid(),
    mode: z.enum([
        'full',
        'incremental'
    ]),
    trigger: z.enum([
        'schedule',
        'manual',
        'bootstrap'
    ]),
    startedAt: z.iso.datetime(),
    finishedAt: z.union([
        z.iso.datetime(),
        z.null()
    ]),
    outcome: z.nullable(z.enum([
        'ok',
        'partial',
        'failed',
        'cancelled'
    ])),
    entries: z.int(),
    files: z.int(),
    directories: z.int(),
    bytes: z.int(),
    errors: z.int(),
    skippedSubtrees: z.int(),
    removed: z.int(),
    message: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * ENEA-A-R10: the state of a share's catalog, answered by the OWNER node (`GET …/catalog/status`). With the catalog disabled the policy and owner are rendered and everything else is `null`.
 *
 */
export const zCatalogStatus = z.object({
    enabled: z.boolean(),
    policy: z.object({
        everyMinutes: z.int(),
        mode: z.enum([
            'auto',
            'full',
            'incremental'
        ]),
        fullEvery: z.int(),
        maxEntries: z.union([
            z.int(),
            z.null()
        ])
    }),
    owner: z.object({
        node: z.union([
            z.string(),
            z.null()
        ]),
        engine: z.enum([
            'ceph',
            'multiscsi',
            'mount'
        ])
    }),
    mount: z.optional(z.union([
        z.object({
            present: z.boolean(),
            fstype: z.union([
                z.string(),
                z.null()
            ]),
            source: z.union([
                z.string(),
                z.null()
            ]),
            options: z.union([
                z.string(),
                z.null()
            ]),
            seenAt: z.union([
                z.iso.datetime(),
                z.null()
            ])
        }),
        z.null()
    ])),
    nodeState: z.optional(z.union([
        z.object({
            inService: z.boolean(),
            maintenance: z.boolean()
        }),
        z.null()
    ])),
    database: z.union([
        z.object({
            host: z.string(),
            port: z.int(),
            name: z.string(),
            schemaVersion: z.union([
                z.int(),
                z.null()
            ]),
            sizeBytes: z.union([
                z.int(),
                z.null()
            ])
        }),
        z.null()
    ]),
    lastScan: z.union([
        zCatalogScan,
        z.null()
    ]),
    running: z.union([
        zCatalogScan,
        z.null()
    ]),
    nextDueAt: z.union([
        z.iso.datetime(),
        z.null()
    ]),
    totals: z.union([
        z.object({
            entries: z.int(),
            files: z.int(),
            directories: z.int(),
            bytes: z.int()
        }),
        z.null()
    ])
});

/**
 * The share's catalog by extension, by owner uid and by top-level directory (from the catalog's views; top 50 each).
 */
export const zCatalogSummary = z.object({
    byExtension: z.array(z.object({
        extension: z.string(),
        files: z.int(),
        bytes: z.int()
    })),
    byOwner: z.array(z.object({
        uid: z.int(),
        files: z.int(),
        bytes: z.int()
    })),
    byDirectory: z.array(z.object({
        directory: z.string(),
        files: z.int(),
        subdirectories: z.int(),
        bytes: z.int(),
        newestMtime: z.union([
            z.iso.datetime(),
            z.null()
        ])
    }))
});

/**
 * Body of `POST …/catalog/actions/scan` (all optional).
 */
export const zCatalogScanRequest = z.object({
    mode: z.optional(z.enum([
        'full',
        'incremental'
    ]))
});

/**
 * ENEA-A-R10: the READ-ONLY SQL credential of the catalog database on the owner node (sudo). One role per database — it reads the catalog of EVERY share of that owner node and nothing else (CONNECT revoked from PUBLIC on the catalog database; no table grants anywhere else). The PostgreSQL port of the owner node must be reachable from the management network, with TLS and a `pg_hba` line for this role on this database only — the deployment's part (manual §6.11); `tls` reports what the server presents today.
 *
 */
export const zCatalogCredentials = z.object({
    host: z.string(),
    port: z.int(),
    database: z.string(),
    username: z.string(),
    password: z.string(),
    dsn: z.string(),
    tls: z.object({
        offered: z.boolean(),
        fingerprintSha256: z.union([
            z.string(),
            z.null()
        ])
    }),
    note: z.string()
});

/**
 * One shared disk of the enclosure as the render composes it: the declared identity (`wwid`, pinned `serial`), the OWNER head, the observed facts (size, model, who sees it now) and the objects built on it by its owner (the vdisk, the OSD). All readOnly — the disk list is declared at creation as wwids (`EnclosureCreateRequest.disks`) and the owner moves only through `actions/failover` / `actions/failback`.
 *
 */
export const zEnclosureDisk = z.object({
    wwid: z.string(),
    serial: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    owner: z.string(),
    sizeBytes: z.optional(z.union([
        z.int(),
        z.null()
    ])),
    model: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    vdisk: z.optional(z.union([
        z.object({
            id: z.uuid(),
            name: z.string()
        }),
        z.null()
    ])),
    osd: z.optional(z.union([
        z.object({
            id: z.int(),
            host: z.optional(z.union([
                z.string(),
                z.null()
            ]))
        }),
        z.null()
    ])),
    seenBy: z.array(z.string())
});

/**
 * A shared disk shelf declared by an operator (R25 JBOF-DC-1, design §2): the heads that see its disks and, per disk, the one owner among them. Scoped resource (ResourceCommon). The disks and their owners are readOnly on the resource — declared as wwids at creation, moved only by the ownership verbs.
 *
 */
export const zEnclosure = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    heads: z.array(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)).min(2),
    disks: z.array(zEnclosureDisk).readonly(),
    reservePercent: z.optional(z.int().gte(0).lte(50)).default(15),
    phase: z.optional(z.enum([
        'ready',
        'degraded',
        'failing-over',
        'error',
        'deleting'
    ])),
    revision: z.optional(z.int().readonly())
}));

export const zEnclosureList = z.object({
    items: z.array(zEnclosure),
    nextCursor: z.optional(z.string())
});

/**
 * Body of `POST /enclosures` — the DECLARATION of a shared shelf (design §2 admission): the heads, the disks as wwids, the initial owner of all of them. Everything the render composes (serial, size, model, seenBy, vdisk, osd) comes from the census, never from the body.
 *
 */
export const zEnclosureCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.optional(z.string().max(255)),
    heads: z.array(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)).min(2),
    disks: z.array(z.string().min(1)).min(1),
    owner: z.optional(z.string()),
    reservePercent: z.optional(z.int().gte(0).lte(50)).default(15),
    notes: z.optional(z.string().max(65536))
});

/**
 * Body of `actions/failover` (design §5.1 — `from` is DEAD): move the ownership of every disk owned by `from` to `to`, then adopt the OSDs on `to` without mkfs (`enclosure.import`). The fence of `from` must be confirmed BEFORE the activation on `to`: a double activation of the same disk corrupts BlueStore; without `fence.confirmed: true` the request is refused (`422 validation` on field `fence.confirmed`).
 *
 */
export const zEnclosureFailoverRequest = z.object({
    from: z.string(),
    to: z.string(),
    fence: z.object({
        confirmed: z.boolean(),
        method: z.enum([
            'power-off',
            'nvme-reservation',
            'pcie-port',
            'bmc',
            'declared'
        ]),
        note: z.optional(z.string().max(4096))
    })
});

/**
 * Body of `actions/failback` (design §5.3 — BOTH heads alive): the orderly return of the ownership from `from` (the current owner, typically the survivor of a failover) to `to` (the head that came back). The ownership transaction is written first; the parent task `enclosure.failback` runs `enclosure.release` on `from` (OSDs stopped, VG deactivated, LUKS closed, released marker) and THEN `enclosure.import` on `to` — the import never starts before the release has succeeded.
 *
 */
export const zEnclosureFailbackRequest = z.object({
    from: z.string(),
    to: z.string()
});

/**
 * A FILE SHARE — the NAS resource (NAS-1, 2026-08-04): a named filesystem exported over one or more of NFS, SMB and SFTP. ENGINE-NEUTRAL like volumes: the share lives on a DATAPOOL bound to an engine and never names one (architecture §5.2). On a `multiscsi` datapool the share OWNS a backing mirror volume (`share-<share8hex>`) mounted and served by that volume's AUTHORITY node — single-mounter gateway, active/passive, service IP follows the authority. On a `ceph` datapool the share is a CephFS SUBVOLUME in its volume's group of the cluster filesystem `tvstor-fs-<cluster8hex>` (or of the volume's own filesystem when `Volume.cephfs.dedicated` — NAS-MDS-1 B, 2026-10-29), exported from every member node over a CephFS MOUNT (D7, 2026-08-15 — no `vfs_ceph`), AND THE TOPOLOGY IS NOT THE SAME FOR EVERY PROTOCOL: NFS (NFS-Ganesha FSAL_CEPH, or the kernel NFS server when `cluster.nfsEngine.ceph = knfsd` — ENEA-A-R2), SFTP and FTP are ACTIVE/ACTIVE on every member, while SMB is ACTIVE/STANDBY — exactly one gateway serves it, and `smbOwner` names it (#152, 2026-08-15). The reason is measured, not stylistic: NFS byte-range locks reach the CephFS MDS and are therefore refereed across gateways, Samba's do not (#153, 2026-08-17 — `nfs` and `smbOwner` below carry the full statement).
 *
 */
export const zFileShare = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    volume: z.uuid(),
    cluster: z.optional(z.uuid().readonly()),
    sizeGiB: z.int().gte(1),
    quota: z.optional(z.union([
        z.object({
            scope: z.enum([
                'share',
                'subtree'
            ]),
            limitBytes: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            directories: z.optional(z.union([
                z.array(z.object({
                    path: z.string().min(1).max(512),
                    limitBytes: z.int().gte(1048576)
                })).max(64),
                z.null()
            ]))
        }),
        z.null()
    ])),
    protocols: z.array(z.enum([
        'nfs',
        'smb',
        'sftp',
        'ftp',
        'tftp',
        'webdav'
    ])).min(0),
    identity: z.optional(z.union([
        z.object({
            mode: z.enum([
                'local',
                'directory'
            ]),
            realmId: z.optional(z.union([
                z.uuid(),
                z.null()
            ]))
        }),
        z.null()
    ])),
    aclModel: z.optional(z.enum([
        'posix',
        'nt'
    ])),
    source: z.optional(z.union([
        z.object({
            kind: z.optional(z.enum([
                'shareSnapshot'
            ])),
            share: z.optional(z.uuid()),
            snapshot: z.optional(z.uuid())
        }),
        z.null()
    ])),
    replica: z.optional(z.union([
        z.object({
            sourceCluster: z.optional(z.string()),
            sourceShare: z.optional(z.uuid()),
            sourceLink: z.optional(z.uuid()),
            path: z.optional(z.string()),
            lastSyncedSnapshot: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            syncedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    previousVersions: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            intervalMinutes: z.optional(z.union([
                z.int().gte(5).lte(10080),
                z.null()
            ])),
            keep: z.optional(z.union([
                z.int().gte(1).lte(64),
                z.null()
            ]))
        }),
        z.null()
    ])),
    catalog: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            everyMinutes: z.optional(z.union([
                z.int().gte(15).lte(10080),
                z.null()
            ])),
            mode: z.optional(z.nullable(z.enum([
                'auto',
                'full',
                'incremental'
            ]))),
            fullEvery: z.optional(z.union([
                z.int().gte(1).lte(365),
                z.null()
            ])),
            maxEntries: z.optional(z.union([
                z.int().gte(1000).lte(1000000000),
                z.null()
            ]))
        }),
        z.null()
    ])),
    audit: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            operations: z.optional(z.union([
                z.array(z.enum([
                    'connect',
                    'mount',
                    'auth',
                    'open',
                    'read',
                    'write',
                    'create',
                    'delete',
                    'rename',
                    'mkdir',
                    'rmdir',
                    'setattr',
                    'setacl',
                    'list',
                    'lock'
                ])).max(32),
                z.null()
            ]))
        }),
        z.null()
    ])),
    qos: z.optional(z.union([
        z.object({
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ])),
    qosStatus: z.optional(z.union([
        z.object({
            address: z.optional(z.string()),
            device: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            shaped: z.optional(z.boolean()),
            readBytesPerSec: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            reason: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            node: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    volumeQos: z.optional(z.union([
        z.object({
            iopsLimit: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            mbpsLimit: z.optional(z.union([
                z.int(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    nfs: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            allow: z.array(z.string().min(1).max(64)).min(1),
            squash: z.optional(z.enum([
                'root',
                'none'
            ])),
            sec: z.optional(z.enum([
                'sys',
                'krb5',
                'krb5i',
                'krb5p'
            ]))
        }),
        z.null()
    ])),
    smb: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.optional(z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            }))),
            principals: z.optional(z.array(z.string().min(3).max(320))),
            homes: z.optional(z.boolean()),
            encryption: z.optional(z.enum([
                'required',
                'desired',
                'off'
            ]))
        }),
        z.null()
    ])),
    sftp: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            })).min(1)
        }),
        z.null()
    ])),
    ftp: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            })).min(1),
            tls: z.optional(z.enum([
                'explicit',
                'none'
            ])),
            passivePortRange: z.object({
                from: z.int().gte(1024).lte(65535),
                to: z.int().gte(1024).lte(65535)
            })
        }),
        z.null()
    ])),
    tftp: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            subpath: z.optional(z.string().min(1).max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/))
        }),
        z.null()
    ])),
    webdav: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            })).min(1),
            port: z.optional(z.int().gte(1024).lte(65535)),
            tls: z.optional(z.enum([
                'explicit',
                'none'
            ]))
        }),
        z.null()
    ])),
    pxe: z.optional(z.union([
        z.object({
            defaultEntry: z.optional(z.string()),
            timeoutSeconds: z.optional(z.int().gte(0).lte(3600)),
            entries: z.array(z.object({
                name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
                label: z.optional(z.string().max(128)),
                kernel: z.string().min(1).max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                initrd: z.optional(z.union([
                    z.string().max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                    z.null()
                ])),
                append: z.optional(z.union([
                    z.string().max(1024),
                    z.null()
                ]))
            })).min(1)
        }),
        z.null()
    ])),
    serviceAddress: z.optional(z.union([
        z.string().max(64),
        z.null()
    ])),
    fsType: z.optional(z.nullable(z.enum([
        'xfs',
        'ext4'
    ]))),
    phase: z.optional(z.enum([
        'pending',
        'creating',
        'ready',
        'degraded',
        'error',
        'deleting'
    ])),
    lockdown: z.optional(z.union([
        z.object({
            lockedAt: z.iso.datetime(),
            lockedBy: z.string()
        }).readonly(),
        z.null()
    ]).readonly()),
    path: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    mountedOn: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    serviceAddressOn: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    gatewayAddresses: z.optional(z.array(z.object({
        address: z.string(),
        node: z.union([
            z.string(),
            z.null()
        ])
    })).readonly()),
    smbOwner: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    smbLeaseUnrenewedSince: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    daemons: z.optional(z.array(z.object({
        node: z.optional(z.string()),
        protocol: z.optional(z.enum([
            'nfs',
            'smb',
            'sftp',
            'ftp',
            'tftp',
            'webdav',
            'winbind',
            'sssd',
            'rpcbind',
            'statd',
            'svcgssd'
        ])),
        engine: z.optional(z.nullable(z.enum([
            'ganesha',
            'knfsd'
        ]))),
        transports: z.optional(z.union([
            z.array(z.enum([
                'tcp',
                'rdma'
            ])),
            z.null()
        ])),
        addressesHeld: z.optional(z.union([
            z.array(z.string()),
            z.null()
        ])),
        state: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        pid: z.optional(z.union([
            z.int(),
            z.null()
        ])),
        connections: z.optional(z.union([
            z.int(),
            z.null()
        ])),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    clientCount: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    capacityBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    usedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    availBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    quotaUsage: z.optional(z.union([
        z.array(z.object({
            path: z.optional(z.string()),
            limitBytes: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            usedBytes: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        })).readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    objectStore: z.optional(z.union([
        z.uuid().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zFileShareList = z.object({
    items: z.array(zFileShare),
    nextCursor: z.optional(z.string())
});

/**
 * Body of `POST /fileShares/{fileShare}/actions/resize` (#34 SHRINK, ratifica 2026-08-28): the share's new declared size — both verses (grow within the volume's promise, shrink above the observed usage plus margin). On cephfs both verses are synchronous (the quota); on multiscsi the GROW is this action too (`202` + the `fileshare.grow` task — GROW-MS 2026-09-04) while the SHRINK answers `422` naming the volume's `actions/shrink` (the one ms share IS the volume).
 *
 */
export const zFileShareResizeRequest = z.object({
    sizeGiB: z.int().gte(1)
});

/**
 * What the kill-switch did (SEC-REVOKE D5, #79): the lockdown state now in force and the counts of what fell with it — the `actionRevokeSessions` shape (credentials.yaml), because this verb COUNTS effects and is therefore idempotent: a second press answers the SAME state (`lockedAt` does not move) and re-sweeps whatever was born in between (normally 0). Both presses land in the #73 audit registry — idempotency hides nothing from the record.
 *
 */
export const zFileShareLockdownResult = z.object({
    lockdown: z.object({
        lockedAt: z.iso.datetime(),
        lockedBy: z.string()
    }),
    revokedSessions: z.int(),
    revokedEditorTokens: z.int()
});

/**
 * Body of `POST /fileShares/{fileShare}/actions/promote` (ENEA-A-R9): the destination operator's declared decision to make a mirror replica share an ordinary, writable share. Sudo-protected AND confirm-typed; the recovery point is ACKNOWLEDGED explicitly.
 *
 */
export const zFileSharePromoteRequest = z.object({
    confirm: z.string(),
    acknowledgeSnapshot: z.union([
        z.string(),
        z.null()
    ]),
    name: z.optional(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/))
});

/**
 * ENEA-A-R5 (2026-10-23): a SNAPSHOT OF A CEPH SHARE as a resource — a snapshot of the share's CephFS SUBVOLUME (`ceph fs subvolume snapshot create`) with an operator-given name. Config-class row (`share_snapshots`, replicated); the phase follows the task (`ceph.fileshare.snapshot` → ready, failed → error; delete → row gone). Visible to clients through the kernel CephFS `.snap` directory of the share root as `_tvs-<name>_<inode>` (measured on kernel mounts: knfsd, SMB, SFTP, WebDAV); through Ganesha FSAL_CEPH the `.snap` traversal is MEASURED on the #148 bench and stated in docs/enea-a-r5-2026-10-23.md. MULTISCSI shares have no snapshot resource of their own: their snapshot IS the `VolumeSnapshot` of the backing volume (fsfreeze → snapshot → thaw) — `POST …/snapshots` on a multiscsi share answers `422` naming that route. The previous-versions cadence of #177 (`@GMT-…` names) is NOT a resource: never listed here, never created or deleted here (the `tvs-` and `@GMT-` namespaces are disjoint by construction).
 *
 */
export const zFileShareSnapshot = z.object({
    id: z.uuid().readonly(),
    share: z.uuid().readonly(),
    name: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    cephName: z.string().readonly(),
    origin: z.optional(z.enum([
        'user',
        'mirror'
    ])),
    phase: z.enum([
        'pending',
        'ready',
        'error',
        'deleting'
    ]),
    retention: z.optional(zSnapshotRetention),
    createdAt: z.optional(z.iso.datetime().readonly()),
    revision: z.optional(z.int().readonly())
});

export const zFileShareSnapshotList = z.object({
    items: z.array(zFileShareSnapshot),
    nextCursor: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * ENEA-A-R12 «gestione 2» (2026-10-23): ONE client operation as the audit collectors of a serving node classified it — the same fields the RFC 5424 structured-data `[tvstor@66054 …]` carries to the SIEM. `user` is the identity as the daemon saw it (DOMAIN\user, `uid:<n>`, S3 access key, Kerberos principal); `client` the address without port, null where the source does not carry it (WebDAV gate v1); `path` is relative to the share (or `bucket/key`), truncated at 1024 bytes, NEVER content; `security` marks failed authentications and denied operations.
 *
 */
export const zClientAuditEvent = z.object({
    ts: z.iso.datetime(),
    node: z.string(),
    protocol: z.enum([
        'smb',
        'nfs',
        's3',
        'webdav'
    ]),
    source: z.enum([
        'smbd',
        'ganesha',
        'mountd',
        'svcgssd',
        'knfsd',
        'rgw',
        'versitygw',
        'webdavd'
    ]),
    resource: z.object({
        kind: z.enum([
            'share',
            'objectstore'
        ]),
        id: z.string(),
        name: z.string()
    }),
    user: z.union([
        z.string(),
        z.null()
    ]),
    client: z.union([
        z.string(),
        z.null()
    ]),
    op: z.enum([
        'connect',
        'disconnect',
        'mount',
        'unmount',
        'auth',
        'open',
        'read',
        'write',
        'create',
        'delete',
        'rename',
        'mkdir',
        'rmdir',
        'setattr',
        'setacl',
        'list',
        'head',
        'copy',
        'lock',
        'other'
    ]),
    path: z.union([
        z.string(),
        z.null()
    ]),
    outcome: z.enum([
        'ok',
        'denied',
        'error'
    ]),
    bytes: z.union([
        z.number(),
        z.null()
    ]),
    security: z.boolean(),
    detail: z.union([
        z.record(z.string(), z.unknown()),
        z.null()
    ])
});

/**
 * ENEA-A-R12 (2026-10-23): the last N client audit events of ONE resource. E2E-2b (2026-10-26, rilievo 24 — ratified by the coordinator): the tail is MERGED across every node that serves the resource (ceph: every member gateway of the datapool, because the tenant's floating address — and so the client's traffic — moves between them; multiscsi: the mounter; stores: every member node), each node's local file read on that node and the events merged by time, oldest first; every event carries its origin in `node`. `nodes` lists the nodes whose files were merged, `unavailable` the serving nodes that did not answer (the tail is then partial, and says so); `503 audit_tail_unavailable` only when no serving node answered. `node`/`file` name the answering node and its local file. `truncated` is true when more events than `tail` matched (on any node, or after the merge). Events are re-read from the structured-data of the file lines (never re-classified).
 *
 */
export const zClientAuditTail = z.object({
    node: z.string(),
    file: z.string(),
    tail: z.int(),
    truncated: z.boolean(),
    items: z.array(zClientAuditEvent),
    nodes: z.array(z.string()),
    unavailable: z.array(z.string())
});

/**
 * One entry of a web-explorer folder listing (NAS-WebExplorer #30, 2026-08-05). REGULAR FILES AND FOLDERS ONLY: symlinks, devices, sockets and every other special kind are OMITTED from listings and unservable through the fence (PROPOSAL 2026-08-05 — the web plane lists exactly what it can also stream; a symlink is never followed, see the path fence on `listFileShareFsEntries`).
 *
 */
export const zFsEntry = z.object({
    name: z.string().min(1).max(255),
    kind: z.enum([
        'file',
        'folder'
    ]),
    sizeBytes: z.optional(z.union([
        z.number(),
        z.null()
    ])),
    modifiedAt: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ]))
});

/**
 * One page of a NON-RECURSIVE folder listing (NAS-WebExplorer #30, 2026-08-05), ordered FOLDERS FIRST then by name (bytewise codepoint order — deterministic and locale-independent, PROPOSAL 2026-08-05).
 *
 */
export const zFsListing = z.object({
    path: z.string(),
    entries: z.array(zFsEntry),
    nextCursor: z.optional(z.string())
});

/**
 * UNA VOCE di una ACL (NAS-2 Fase 2, #169 2026-08-17). La stessa forma per i due modelli: cio' che cambia e' come si scrive `trustee`.
 *
 */
export const zFsAclEntry = z.object({
    kind: z.enum([
        'allow',
        'deny',
        'audit'
    ]),
    trustee: z.string().min(1).max(256),
    rights: z.array(z.enum([
        'readData',
        'writeData',
        'appendData',
        'readEa',
        'writeEa',
        'execute',
        'deleteChild',
        'readAttributes',
        'writeAttributes',
        'delete',
        'readControl',
        'writeDac',
        'writeOwner',
        'synchronize',
        'accessSystemSecurity',
        'maximumAllowed',
        'genericAll',
        'genericExecute',
        'genericWrite',
        'genericRead'
    ])),
    inherit: z.optional(z.array(z.enum([
        'objectInherit',
        'containerInherit',
        'noPropagateInherit',
        'inheritOnly',
        'inherited',
        'auditSuccess',
        'auditFailure'
    ]))),
    unnamedMask: z.optional(z.coerce.bigint()).default(BigInt(0)),
    unnamedFlags: z.optional(z.coerce.bigint()).default(BigInt(0))
});

/**
 * UNA PERDITA di traduzione, come DATO e non come nota a pie' di pagina (design §4.3.1). L'elenco completo, con la ragione di ciascuna, sta in `docs/nas2-acl-ricche-2026-08-17.md` §4 — che e' il testo del manuale.
 *
 */
export const zFsAclLoss = z.object({
    code: z.enum([
        'denyAce',
        'aceOrder',
        'granularRights',
        'appendWithoutWrite',
        'richInheritance',
        'inheritOnly',
        'noPropagateInherit',
        'posixMask',
        'creatorTrustee',
        'auditing',
        'unmappableTrustee',
        'posixMaskWidening',
        'defaultAclWidening'
    ]),
    severity: z.enum([
        'lossy',
        'notRepresentable'
    ]),
    entries: z.array(z.int()),
    detail: z.string(),
    remedy: z.string()
});

/**
 * La ACL di un percorso dentro la share, NELLA RAPPRESENTAZIONE NATIVA della share e mai in una traduzione inventata (design §4.3.1).
 *
 */
export const zFsAcl = z.object({
    path: z.string(),
    model: z.enum([
        'posix',
        'nt'
    ]),
    owner: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    group: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    entries: z.array(zFsAclEntry),
    daclPresent: z.boolean(),
    lossyFor: z.array(z.enum([
        'nfs',
        'smb',
        'sftp',
        'ftp'
    ])),
    losses: z.array(zFsAclLoss)
});

/**
 * A minted DOCUMENT EDITOR session (WEBEXP-EDIT #63, 2026-08-05 — ratifica Luca 2026-08-05: Collabora/CODE via WOPI; the OnlyOffice spike's findings are BINDING, docs/onlyoffice-spike-2026-08-05.md). The `accessToken` is a DEDICATED short-lived `tvse_` EDITOR TOKEN — NEVER the caller's session token (spike finding 1: the token lands in the editor server's URLs and logs, so only a purpose-bound, expiring, file-scoped credential may travel there). Rendered EXACTLY ONCE here (the store keeps only its hash — the sessions discipline).
 *
 */
export const zFsEditorSession = z.object({
    editorUrl: z.string(),
    wopiSrc: z.string(),
    accessToken: z.string(),
    accessTokenTtl: z.number(),
    expiresAt: z.iso.datetime()
});

/**
 * An OPTIONAL platform-managed DHCPv4 service (NAS-3, 2026-08-05 — PROPOSAL, pending ratification): one supervised Kea instance serving ONE subnet, anchored to a TFTP-enabled multiscsi file share — the daemon runs on the share's authority node bound to its `serviceAddress`, follows it on failover, and (when `bootOptions` is present) hands PXE clients next-server/filename pointing at that same address. Sites with an existing DHCP server never create one of these: they configure options 66/67 on their own server instead (documented).
 *
 */
export const zDhcpService = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    fileShare: z.uuid(),
    subnet: z.string().min(9).max(43),
    pool: z.object({
        from: z.string().max(15),
        to: z.string().max(15)
    }),
    leaseSeconds: z.optional(z.int().gte(60).lte(604800)),
    routers: z.optional(z.array(z.string().max(15)).max(8)),
    dnsServers: z.optional(z.array(z.string().max(15)).max(8)),
    domainName: z.optional(z.union([
        z.string().max(253),
        z.null()
    ])),
    reservations: z.optional(z.array(z.object({
        mac: z.string().regex(/^[0-9a-f]{2}(:[0-9a-f]{2}){5}$/),
        address: z.string().max(15),
        hostname: z.optional(z.union([
            z.string().max(63),
            z.null()
        ]))
    })).max(1024)),
    bootOptions: z.optional(z.union([
        z.object({
            nextServer: z.optional(z.union([
                z.string().max(15),
                z.null()
            ])),
            filename: z.optional(z.union([
                z.string().max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                z.null()
            ])),
            efiFilename: z.optional(z.union([
                z.string().max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                z.null()
            ]))
        }),
        z.null()
    ])),
    enabled: z.optional(z.boolean()),
    phase: z.optional(z.enum([
        'pending',
        'running',
        'degraded',
        'stopped',
        'error'
    ])),
    daemon: z.optional(z.union([
        z.object({
            node: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            state: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            pid: z.optional(z.union([
                z.int(),
                z.null()
            ])),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly()),
    leaseCount: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zDhcpServiceList = z.object({
    items: z.array(zDhcpService),
    nextCursor: z.optional(z.string())
});

/**
 * A DIRECTORY REALM (NAS-2 Fase 1a, 2026-08-15): the cluster-level declaration of an Active Directory domain (`type: ad`) or a plain LDAP directory (`type: ldap`) the file services take identities from. N per cluster (ratifica B3, 2026-08-12). The realm's `name` is the QUALIFYING half of every `REALM\user` on this cluster and is also the key of its uid range in the cluster allocator — one to one (design §3.2). Every mutation is sudo-gated. In Fase 1a a realm is a DECLARATION with a checkable preflight: the join, the daemons and the rendered domain configuration are the rest of Fase 1, ramo `ad`, and landed with #157 (2026-08-16) on the node side, not on this resource.
 *
 */
export const zDirectoryRealm = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    type: z.enum([
        'ad',
        'ldap'
    ]),
    credential: z.uuid(),
    servers: z.optional(z.array(z.string().min(1).max(261)).max(16)),
    ad: z.optional(z.union([
        z.object({
            kerberosRealm: z.string().min(1).max(255),
            netbiosDomain: z.string().min(1).max(15),
            machineAccountOu: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ]))
        }),
        z.null()
    ])),
    ldap: z.optional(z.union([
        z.object({
            baseDn: z.string().min(1).max(1024),
            tls: z.enum([
                'ldaps',
                'startTls'
            ]),
            userFilter: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ])),
            groupFilter: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ])),
            kerberos: z.optional(z.union([
                z.object({
                    realm: z.string().min(1).max(255),
                    kdcs: z.array(z.string().min(1).max(261)).min(1).max(8),
                    adminServer: z.optional(z.union([
                        z.string().max(261),
                        z.null()
                    ])),
                    nfsHostname: z.string().min(1).max(253),
                    keytabCredential: z.optional(z.union([
                        z.uuid(),
                        z.null()
                    ]))
                }),
                z.null()
            ]))
        }),
        z.null()
    ])),
    uidRange: z.optional(z.union([
        z.object({
            base: z.int().gte(2000000).lte(999999999),
            span: z.int().gte(1).lte(998000000),
            declared: z.optional(z.boolean().readonly())
        }),
        z.null()
    ])),
    domainSid: z.optional(z.union([
        z.string().max(128),
        z.null()
    ])),
    identityKeyAttribute: z.optional(z.string().min(1).max(63)),
    offlineWindowSeconds: z.optional(z.int().gte(0).lte(2592000)),
    adminRoles: z.optional(z.union([
        z.array(z.object({
            group: z.string().min(1).max(1024),
            role: z.string().min(1).max(63),
            scope: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ]))
        })).max(64),
        z.null()
    ])),
    allocatedIdentities: z.optional(z.int().readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zDirectoryRealmList = z.object({
    items: z.array(zDirectoryRealm),
    nextCursor: z.optional(z.string())
});

/**
 * The preflight report (NAS-2 Fase 1a, 2026-08-15): what the platform can check BEFORE anybody touches the domain, said one prerequisite at a time. Two properties give it its shape, and both are structural rather than stylistic — see the file header.
 *
 */
export const zDirectoryRealmPreflight = z.object({
    outcome: z.enum([
        'ready',
        'blocked',
        'inconclusive'
    ]),
    observedFrom: z.string(),
    checks: z.array(z.object({
        id: z.enum([
            'declaration',
            'credential',
            'idRange',
            'idRangeDisjoint',
            'domainIdentity',
            'identityKey',
            'identityKeyInDirectory',
            'dnsSrv',
            'serverDns',
            'reverseDns',
            'clockSkew',
            'machineAccountRights',
            'directoryBindRights',
            'sharePrincipal'
        ]),
        title: z.string(),
        verdict: z.object({
            kind: z.enum([
                'pass',
                'fail',
                'unknown'
            ]),
            detail: z.string(),
            remedy: z.optional(z.string())
        })
    }))
});

/**
 * Body of `actions/refound` (NAS-2 Fase 1a.1, 2026-08-15) — THE WAY OUT OF THE D4 REFUSAL, and it is part of that ratifica rather than a leftover: "un rifiuto senza via d'uscita e' un guasto travestito da guardia; la dottrina del progetto lo vieta". Somebody whose domain was legitimately rebuilt — after a disaster — must be able to SAY SO. AND IT IS DELIBERATELY NOT CONVENIENT. Three things have to be typed, on top of `X-TVStor-Sudo`, and none of them can be guessed from the request that failed: they all have to be READ off the realm first. That is the tvirt danger-ladder shape (the `destroy` of a datapool: sudo + typed confirm + an explicit decision with no default), applied to a gesture whose damage is quieter and slower than destroying a pool and just as permanent.
 *
 */
export const zDirectoryRealmRefoundRequest = z.object({
    confirm: z.string(),
    domainSid: z.string(),
    acknowledgeAllocations: z.int().gte(0)
});

/**
 * What the refoundation did (NAS-2 Fase 1a.1, 2026-08-15). It reports the consequence rather than assuming it was read in the request description — the same reason the preflight carries `remedy` as a FIELD.
 *
 */
export const zDirectoryRealmRefoundResult = z.object({
    realm: z.string(),
    previousDomainSid: z.string(),
    domainSid: z.string(),
    retainedAllocations: z.int(),
    consequence: z.string()
});

export const zS3ProfileVerdict = z.object({
    status: z.enum([
        'measured',
        'declared'
    ]),
    evidence: z.string()
}).readonly();

export const zS3ProfileVerdictRow = z.object({
    id: z.string(),
    rgw: zS3ProfileVerdict,
    versitygw: zS3ProfileVerdict,
    bidirectional: zS3ProfileVerdict
}).readonly();

/**
 * An OBJECT STORE — the S3 endpoint resource (OBJ-1, 2026-08-04): ENGINE-NEUTRAL like volumes and file shares — the store lives on a DATAPOOL bound to an engine and never names one (architecture §5.2). On a `ceph` datapool the store is an RGW zone served ACTIVE/ACTIVE by one supervised `radosgw` per member node, objects in the store's own RADOS pools — scale-out, no failover machinery of its own. On a `multiscsi` datapool (OBJ-MS, 2026-08-05 — PROPOSAL pending ratification) the store OWNS a backing mirror volume (`objs-<store8hex>`) mounted and served by that volume's AUTHORITY node through ONE supervised `versitygw` gateway — single-mounter, active/passive, floating `serviceAddress` following the authority (the FileShare twin); buckets are directories and current objects plain files on the mount (open formats, declared). BOTH engines serve the SAME ratified TVStor S3 Profile v1 (header table) — the engine changes the machinery, never the promise.
 *
 */
export const zObjectStore = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    volume: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    share: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    form: z.optional(z.enum([
        'rgw',
        'versitygw',
        'bidirectional'
    ])),
    fileOwner: z.optional(z.union([
        z.object({
            uid: z.int().gte(0).lte(4294967294),
            gid: z.int().gte(0).lte(4294967294)
        }),
        z.null()
    ])),
    cluster: z.optional(z.uuid().readonly()),
    placement: z.optional(z.union([
        z.object({
            id: z.string(),
            dataPool: z.string(),
            indexPool: z.string()
        }).readonly(),
        z.null()
    ]).readonly()),
    port: z.int().gte(1).lte(65535),
    tlsCertificate: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    sizeGiB: z.optional(z.union([
        z.int().gte(1).readonly(),
        z.null()
    ]).readonly()),
    serviceAddress: z.optional(z.union([
        z.string().max(64),
        z.null()
    ])),
    phase: z.optional(z.enum([
        'pending',
        'creating',
        'ready',
        'degraded',
        'error',
        'deleting'
    ])),
    fsType: z.optional(z.nullable(z.enum([
        'xfs',
        'ext4'
    ]))),
    mountedOn: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    serviceAddressOn: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    daemons: z.optional(z.array(z.object({
        node: z.optional(z.string()),
        state: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        pid: z.optional(z.union([
            z.int(),
            z.null()
        ])),
        version: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    endpoints: z.optional(z.array(z.object({
        node: z.optional(z.string()),
        url: z.optional(z.string()),
        healthy: z.optional(z.union([
            z.boolean(),
            z.null()
        ])),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    bucketCount: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    usedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    objectCount: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    unmanagedBuckets: z.optional(z.array(z.object({
        name: z.optional(z.string()),
        owner: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        usedBytes: z.optional(z.union([
            z.number(),
            z.null()
        ])),
        objectCount: z.optional(z.union([
            z.number(),
            z.null()
        ])),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ]))
    })).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    capabilities: z.optional(z.object({
        profileVersion: z.optional(z.int()),
        included: z.optional(z.array(z.string())),
        excluded: z.optional(z.array(z.string())),
        verdicts: z.optional(z.array(zS3ProfileVerdictRow).readonly()),
        limits: z.optional(z.object({
            maxBucketsPerAccount: z.optional(z.int()),
            bucketCountPromised: z.optional(z.int()),
            objectSizeBytesPromised: z.optional(z.coerce.bigint())
        }).readonly())
    }).readonly()),
    revision: z.optional(z.int().readonly()),
    audit: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            operations: z.optional(z.union([
                z.array(z.enum([
                    'auth',
                    'read',
                    'write',
                    'create',
                    'delete',
                    'list',
                    'head',
                    'copy',
                    'setacl'
                ])).max(32),
                z.null()
            ]))
        }),
        z.null()
    ])),
    qos: z.optional(z.union([
        z.object({
            readOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            writeOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ])),
    qosStatus: z.optional(z.union([
        z.object({
            engine: z.optional(z.string()),
            scope: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            applied: z.optional(z.union([
                z.record(z.string(), z.unknown()),
                z.null()
            ])),
            window: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            perInstance: z.optional(z.union([
                z.boolean(),
                z.null()
            ])),
            address: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            device: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            shaped: z.optional(z.union([
                z.boolean(),
                z.null()
            ])),
            reason: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            node: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            observedAt: z.optional(z.union([
                z.iso.datetime(),
                z.null()
            ]))
        }).readonly(),
        z.null()
    ]).readonly())
}));

export const zObjectStoreList = z.object({
    items: z.array(zObjectStore),
    nextCursor: z.optional(z.string())
});

/**
 * A BUCKET — the S3 container (OBJ-1, 2026-08-04; the name is free and unambiguous post-RENAME-1, ratifica §3: the physical pool is the datapool). Buckets are SUBRESOURCES of their ObjectStore, not scoped objects — no scope/tags/notes; `name` is unique PER STORE (it is the S3 wire namespace of the zone). Config-class row (`object_buckets`, replicated); the observed usage composes from the usage census in `objectstore_status` (ceph: `radosgw-admin bucket stats`; multiscsi: the gateway/mount census — OBJ-MS 2026-08-05; worker sweep — staleness visible, never hidden). On a multiscsi store the bucket IS a directory on the backing mount (versitygw POSIX backend) — the open-formats honesty of the engine, declared. Since OBJ-MS (2026-08-05) the bucket carries the TVStor S3 Profile v1 per-bucket surface: `versioning` and `objectLock` — IDENTICAL on both engines (the ratified promise, header table).
 *
 */
export const zBucket = z.object({
    id: z.uuid().readonly(),
    objectStore: z.optional(z.uuid().readonly()),
    name: z.string().min(3).max(63).regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/),
    owner: z.uuid(),
    quotaBytes: z.optional(z.union([
        z.int().gte(1),
        z.null()
    ])),
    versioning: z.optional(z.nullable(z.enum([
        'enabled',
        'suspended'
    ]))),
    qos: z.optional(z.union([
        z.object({
            readOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            writeOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ])),
    objectLock: z.optional(z.union([
        z.object({
            defaultRetention: z.optional(z.union([
                z.object({
                    mode: z.enum([
                        'governance',
                        'compliance'
                    ]),
                    days: z.optional(z.union([
                        z.int().gte(1).lte(36500),
                        z.null()
                    ])),
                    years: z.optional(z.union([
                        z.int().gte(1).lte(100),
                        z.null()
                    ]))
                }),
                z.null()
            ]))
        }),
        z.null()
    ])),
    phase: z.optional(z.enum([
        'pending',
        'creating',
        'ready',
        'deleting',
        'error'
    ])),
    usedBytes: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    objectCount: z.optional(z.union([
        z.number().readonly(),
        z.null()
    ]).readonly()),
    observedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    createdAt: z.optional(z.iso.datetime().readonly()),
    revision: z.optional(z.int().readonly())
});

export const zBucketList = z.object({
    items: z.array(zBucket),
    nextCursor: z.optional(z.string())
});

/**
 * An S3 ACCOUNT — the DATA-PLANE consumer identity of an ObjectStore (OBJ-1, 2026-08-04): never an operator, never a session (the two-plane doctrine — the NAS `fileUser` twin). The PLATFORM MINTS the key pair at creation (keystore kind `s3Account`, provenance-marked — `credentials.yaml`), propagates it to the engine — ceph: `radosgw-admin user create` with the minted uid (PROPOSAL 2026-08-04: `tvstor-<account8hex>`); multiscsi (OBJ-MS 2026-08-05 — PROPOSAL): the account converges into versitygw's internal file-backed IAM on the authority, rendered by the worker from the keystore replica (never argv, never logs — the secretfile discipline; NOTE the engine asymmetry, declared: versitygw's identity IS the access key id, there is no separate uid, spike §3) — and renders the secret EXACTLY ONCE in the create response — never again (M2g write-only discipline; the M5a join-token precedent). Accounts are SUBRESOURCES of their store — no scope/tags/notes; `name` unique per store. Config-class row (`s3_accounts`, replicated).
 *
 */
export const zS3Account = z.object({
    id: z.uuid().readonly(),
    objectStore: z.optional(z.uuid().readonly()),
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    credential: z.optional(z.union([
        z.uuid().readonly(),
        z.null()
    ]).readonly()),
    accessKeyId: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    phase: z.optional(z.enum([
        'pending',
        'creating',
        'ready',
        'deleting',
        'error'
    ])),
    createdAt: z.optional(z.iso.datetime().readonly()),
    revision: z.optional(z.int().readonly())
});

export const zS3AccountList = z.object({
    items: z.array(zS3Account),
    nextCursor: z.optional(z.string())
});

/**
 * The minted key pair — RENDERED EXACTLY ONCE, inside the create (and rotate) response, and never again (OBJ-1, 2026-08-04 — the M5a join-token write-once discipline): the keystore stores the secret envelope-encrypted and write-only; no read path re-renders it, and losing it means rotate.
 *
 */
export const zS3AccountSecret = z.object({
    accessKeyId: z.string(),
    secretAccessKey: z.string()
});

/**
 * Answer of the account create and rotate operations (OBJ-1, 2026-08-04): the account row plus the ONE-TIME render of its minted key pair — the `secret` half exists ONLY in this response body.
 *
 */
export const zS3AccountWithSecret = z.object({
    account: zS3Account,
    secret: zS3AccountSecret
});

/**
 * Body of the account rotate action (OBJ-1, 2026-08-04). Rotation MINTS a new pair (rendered once in the response), propagates it to RGW, and retires the old key.
 *
 */
export const zS3AccountRotateRequest = z.object({
    graceMinutes: z.optional(z.int().gte(0).lte(1440)).default(0)
});

/**
 * A named keystore entry (M2g, 2026-08-03). `kind` is IMMUTABLE after creation; exactly ONE per-kind block — matching `kind` — must be present (`422 validation` otherwise, on create and on PATCH alike). The block's PUBLIC fields render back on every read; its SECRET fields are WRITE-ONLY forever — never rendered, never logged, never on argv (the never-rendered discipline, stated 2026-08-03) — required at creation and replaceable ONLY through `actions/rotate` (a PATCH carrying a secret field is `422 validation` — TVStor deviation from TVirt's PATCH-rotation, dated 2026-08-03: secret material is sudo-gated, PATCH is not). The stored secret is envelope-encrypted on the cluster store (AES-256-GCM, per-secret random IV, AAD = credential id + kind; DEK wrapped by the node-local 0600 master key file — see the file header).
 *
 */
export const zCredential = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'iscsiChap',
        'bmcAccount',
        'cephx',
        'luksKey',
        's3Account',
        'replicationToken',
        'fileUser',
        'smtpAccount',
        'webhookSecret',
        'tlsCertificate',
        'wireguardKey',
        'machineAccount',
        'directoryAccount',
        'cephfsMirrorBootstrap'
    ]),
    iscsiChap: z.optional(z.object({
        username: z.string().min(1).max(255),
        mutualUsername: z.optional(z.string().min(1).max(255))
    })),
    bmcAccount: z.optional(z.object({
        username: z.string().min(1).max(255),
        certificateFingerprint: z.optional(z.string().regex(/^[0-9A-Fa-f]{2}(:?[0-9A-Fa-f]{2}){31}$/))
    })),
    cephx: z.optional(z.object({
        entity: z.string().min(1).max(255)
    })),
    luksKey: z.optional(z.object({
        minted: z.optional(z.boolean().readonly())
    })),
    s3Account: z.optional(z.object({
        accessKeyId: z.string().min(1).max(255)
    })),
    replicationToken: z.optional(z.object({
        clusterName: z.string().min(1).max(63)
    })),
    cephfsMirrorBootstrap: z.optional(z.object({
        peerCluster: z.string(),
        sourceFileSystem: z.uuid(),
        remoteFileSystem: z.string(),
        clientName: z.string(),
        state: z.enum([
            'pending',
            'ready'
        ])
    })),
    fileUser: z.optional(z.object({
        username: z.string().min(1).max(63).regex(/^[a-z_][a-z0-9._-]{0,62}$/),
        sshPublicKeys: z.optional(z.array(z.string().min(1).max(4096)))
    })),
    smtpAccount: z.optional(z.object({
        username: z.string().min(1).max(255)
    })),
    tlsCertificate: z.optional(z.object({
        certificatePem: z.string().min(1).max(65536)
    })),
    wireguardKey: z.optional(z.object({
        publicKey: z.optional(z.string().readonly()),
        minted: z.optional(z.boolean().readonly())
    })),
    machineAccount: z.optional(z.object({
        realm: z.string().min(1).max(255),
        netbiosDomain: z.optional(z.string().min(1).max(15)),
        accountName: z.string().min(1).max(15),
        form: z.optional(z.enum([
            'samba',
            'keytab'
        ])),
        rotatedAt: z.optional(z.iso.datetime().readonly()),
        rotationRequestedAt: z.optional(z.iso.datetime().readonly()),
        rotationRequestedReason: z.optional(z.string().readonly())
    })),
    directoryAccount: z.optional(z.object({
        username: z.string().min(1).max(1024)
    })),
    usedBy: z.optional(z.array(z.object({
        resource: z.enum([
            'target',
            'clusterNode',
            'backupTarget',
            'replicationLink',
            'vdisk',
            'cephcluster',
            'fileShare',
            'alertSink',
            'objectStore',
            's3Account',
            'directoryRealm'
        ]),
        id: z.uuid(),
        name: z.string(),
        detail: z.optional(z.string())
    })).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zCredentialList = z.object({
    items: z.array(zCredential),
    nextCursor: z.optional(z.string())
});

/**
 * Body of `actions/rotate` (M2g, 2026-08-03) — SECRET FIELDS ONLY, in the per-kind block matching the credential's kind (`422 validation` on a mismatched or missing block): rotate replaces the secret material, never the public fields (`username`/`entity` changes are ordinary PATCH). Mirrors the creation blocks' secret halves.
 *
 */
export const zCredentialRotateRequest = z.object({
    tlsCertificate: z.optional(z.object({
        certificatePem: z.string().min(1).max(65536)
    }))
});

/**
 * Body of `actions/rotateMachineAccount` (#162, 2026-08-16). Three costs, and the two typed ones cannot be guessed from the refusal that sent you here — they have to be READ off the credential first. It is the `actions/refound` shape (NAS-2 Fase 1a.1): rotating the shared machine account puts EVERY OTHER GATEWAY out of the domain for as long as it takes the re-sealed envelope to reach them, and the platform never does that on a flag alone.
 *
 */
export const zMachineAccountRotationRequest = z.object({
    confirm: z.string(),
    acknowledgeRevision: z.coerce.bigint(),
    reason: z.optional(z.string().max(1024))
});

/**
 * What was recorded, who will do it, and what it costs (#162, 2026-08-16). It reports the consequence rather than assuming the request description was read — the same structural choice as `refound`'s `consequence` and the preflight's `remedy`.
 *
 */
export const zMachineAccountRotationDispatch = z.object({
    credential: z.string(),
    requestedAt: z.iso.datetime(),
    rotator: z.union([
        z.string(),
        z.null()
    ]),
    pendingRotatorCaveat: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    consequence: z.string()
});

/**
 * Body of `actions/exportRecovery` (M4c, 2026-08-04) — the typed confirm of the danger ladder: exporting recovery keys puts every at-rest secret of the cluster on one screen, once.
 *
 */
export const zRecoveryKeysExportRequest = z.object({
    confirm: z.string(),
    storageCluster: z.optional(z.uuid())
});

/**
 * The recovery export (M4c, 2026-08-04 — brochure: "le chiavi di ripristino della cifratura nascono per la cassaforte"): every `luksKey` passphrase visible to the caller, IN CLEAR, EXACTLY ONCE — this response is the single sanctioned exception to the never-rendered discipline. Print it, put it in the physical safe beside the break-glass credentials, and close the browser tab: the platform keeps NO rendered copy, and the export is audited. THE POINT (document it to the customer): an encrypted disk is recoverable outside the appliance ONLY with this export (or with the keystore master key file + a store dump) — lose both and the disk honestly tells nothing, which is the promise.
 *
 */
export const zRecoveryKeysExport = z.object({
    cluster: z.string(),
    exportedAt: z.iso.datetime(),
    keys: z.array(z.object({
        credential: z.uuid(),
        name: z.string(),
        minted: z.optional(z.boolean()),
        storageCluster: z.optional(z.union([
            z.uuid(),
            z.null()
        ])),
        usedBy: z.optional(z.array(z.object({
            resource: z.optional(z.string()),
            id: z.optional(z.string()),
            name: z.optional(z.string())
        }))),
        passphrase: z.string()
    }))
});

/**
 * A backup DESTINATION (M4b, 2026-08-04): where backup objects live and under which immutability policy. Pure config-class row — synchronous CRUD, no task; reachability problems surface on the backup tasks that use the target (a dated seam 2026-08-04: a `verify` action probing the destination may join later). `kind` is IMMUTABLE after creation (the credentials.yaml rationale, adopted: retyping is a NEW target); exactly the per-kind block matching `kind` must be present (`422 validation` otherwise, on create and PATCH alike).
 *
 */
export const zBackupTarget = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'local',
        's3',
        'tape'
    ]),
    local: z.optional(z.object({
        path: z.string().min(1).max(4096).regex(/^\//)
    })),
    s3: z.optional(z.object({
        endpoint: z.url().max(2048),
        bucket: z.string().min(3).max(63),
        region: z.optional(z.string().max(64)),
        prefix: z.optional(z.string().max(512)),
        forcePathStyle: z.optional(z.boolean()).default(true),
        credential: z.uuid()
    })),
    tape: z.optional(z.object({
        node: z.string().min(1),
        drive: z.string().min(1),
        changer: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        worm: z.optional(z.enum([
            'required',
            'any'
        ])),
        blockSize: z.optional(z.int().gte(512).lte(16777216))
    })),
    immutability: z.object({
        mode: z.enum([
            'off',
            'governance',
            'compliance'
        ]),
        defaultRetentionDays: z.optional(z.int().gte(1).lte(36500))
    }),
    timestamp: z.optional(z.union([
        z.object({
            url: z.url().max(2048),
            anchorPem: z.string().min(1).max(262144),
            endpointTlsPin: z.optional(z.string().regex(/^[0-9a-f]{64}$/)),
            policyOid: z.optional(z.string().max(128).regex(/^[0-9]+(\.[0-9]+)+$/)),
            hashAlg: z.optional(z.enum([
                'sha256',
                'sha384',
                'sha512'
            ])),
            verifyEvery: z.optional(z.int().gte(0).lte(36500)).default(30),
            qualified: z.optional(z.boolean()).default(false)
        }),
        z.null()
    ])),
    scrub: z.optional(z.union([
        z.object({
            everyDays: z.int().gte(1).lte(3650),
            windowHours: z.int().gte(1).lte(168)
        }),
        z.null()
    ])),
    backupCount: z.optional(z.int().readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zBackupTargetList = z.object({
    items: z.array(zBackupTarget),
    nextCursor: z.optional(z.string())
});

export const zBackupScrub = z.object({
    state: z.enum([
        'none',
        'ok',
        'failed',
        'unreadable'
    ]),
    checkedAt: z.union([
        z.iso.datetime(),
        z.null()
    ]),
    report: z.union([
        z.object({
            sha256Expected: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            sha256Read: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            bytesRead: z.optional(z.int()),
            durationMs: z.optional(z.int()),
            reason: z.optional(z.union([
                z.string(),
                z.null()
            ]))
        }),
        z.null()
    ])
}).readonly();

export const zBackupCopy = z.object({
    id: z.uuid().readonly(),
    backup: z.uuid().readonly(),
    target: z.uuid().readonly(),
    medium: z.object({
        label: z.string(),
        serial: z.union([
            z.string(),
            z.null()
        ]),
        worm: z.boolean()
    }).readonly(),
    position: z.union([
        z.object({
            fileNumber: z.optional(z.int()),
            blockSize: z.optional(z.int()),
            bytes: z.optional(z.int())
        }).readonly(),
        z.null()
    ]).readonly(),
    sequence: z.union([
        z.int().readonly(),
        z.null()
    ]).readonly(),
    sha256: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    phase: z.enum([
        'pending',
        'writing',
        'verifying',
        'ready',
        'error'
    ]),
    mediumState: z.enum([
        'loaded',
        'ejected',
        'unknown',
        'lost'
    ]),
    retention: z.enum([
        'archived'
    ]),
    error: z.union([
        z.object({
            title: z.optional(z.string()),
            detail: z.optional(z.string())
        }).readonly(),
        z.null()
    ]).readonly(),
    scrub: zBackupScrub,
    verifiedAt: z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly(),
    notes: z.optional(z.string()),
    createdAt: z.iso.datetime().readonly(),
    updatedAt: z.iso.datetime().readonly()
});

/**
 * A point-in-time copy of a volume on a BackupTarget (M4b, 2026-08-04), created FROM A SNAPSHOT — never from the live head: the snapshot IS the point in time, and its crash-consistent semantics (§6.4 open question, `VolumeSnapshot`) carry over unchanged. NO `name` field (PROPOSAL 2026-08-04, deviation from the round-#105 named-kinds pattern): backups are machine-born rows — potentially thousands, schedule-born later — addressed by id and found by `?filter=` on `volume`/`target`/`phase`. The row OUTLIVES ITS VOLUME: deleting the volume leaves its backups restorable (`restore` with `newVolumeName`); it outlives its SNAPSHOT too — the snapshot reference is provenance, and a deleted base snapshot merely forces the next backup of that volume to be `full` (no hard pin, stated 2026-08-04). `retainUntil` and `immutabilityMode` are FROZEN COPIES of the target's policy at creation — the brochure's binding rule lives on this row, not on the target.
 *
 */
export const zBackup = zResourceCommon.and(zRenderedResource).and(z.object({
    volume: z.uuid(),
    snapshot: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    target: z.uuid(),
    kind: z.optional(z.enum([
        'full',
        'incremental'
    ])),
    baseBackup: z.optional(z.union([
        z.uuid().readonly(),
        z.null()
    ]).readonly()),
    volumeSizeGiB: z.optional(z.int().readonly()),
    sizeBytes: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    retainUntil: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    immutabilityMode: z.optional(z.enum([
        'off',
        'governance',
        'compliance'
    ])),
    phase: z.optional(z.enum([
        'pending',
        'running',
        'ready',
        'error',
        'expiring'
    ])),
    timestamp: z.optional(z.object({
        state: z.enum([
            'none',
            'ok',
            'unverifiable',
            'unreachable',
            'refused'
        ]),
        authority: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        qualified: z.boolean(),
        stampedAt: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        policy: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        reason: z.string(),
        observedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ])),
        ltv: z.optional(z.enum([
            'none',
            'partial',
            'complete',
            'retrofitted'
        ])),
        checkedAt: z.optional(z.union([
            z.iso.datetime().readonly(),
            z.null()
        ]).readonly()),
        claim: z.string().readonly()
    }).readonly()),
    scrub: z.optional(zBackupScrub),
    copies: z.optional(z.array(zBackupCopy).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zBackupList = z.object({
    items: z.array(zBackup),
    nextCursor: z.optional(z.string())
});

/**
 * Body of the restore action (M4b, 2026-08-04): EXACTLY ONE of `targetVolume` / `newVolumeName` (`422 validation` otherwise — none or both). OVERWRITE VARIANT (`targetVolume`): destructive, so CONFIRM-TYPED like revert (revert doctrine, M2b: an operator storage workflow, not an identity-plane mutation — no sudo). NEW-VOLUME VARIANT (`newVolumeName`): `datapool` names where the volume is born, and the engine-half fields follow the `VolumeCreateRequest` gates.
 *
 */
export const zBackupRestoreRequest = z.object({
    targetVolume: z.optional(z.uuid()),
    confirm: z.optional(z.string()),
    newVolumeName: z.optional(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)),
    datapool: z.optional(z.uuid()),
    redundancy: z.optional(z.object({
        copies: z.optional(z.int().gte(1)),
        minInSync: z.optional(z.int().gte(1))
    }))
});

export const zBackupArchiveRequest = z.object({
    target: z.uuid(),
    medium: z.object({
        label: z.string().min(1).max(64)
    }),
    notes: z.optional(z.string())
});

export const zBackupCopyList = z.object({
    items: z.array(zBackupCopy)
});

export const zBackupCopyForgetRequest = z.object({
    confirm: z.string().min(1)
});

/**
 * An OUTBOUND asynchronous replication relationship to a second TVStor cluster (M4b, 2026-08-04). Config-class row; the per-volume watermarks (`volumeStates[]`) are state-class, composed onto the render.
 *
 */
export const zReplicationLink = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    direction: z.optional(z.enum([
        'outbound'
    ])),
    kind: z.optional(z.enum([
        'block',
        'file'
    ])),
    remote: z.object({
        url: z.url().max(2048),
        clusterName: z.string().min(1).max(63),
        credential: z.uuid(),
        certFingerprint: z.optional(z.union([
            z.string().max(95),
            z.null()
        ]))
    }),
    volumes: z.array(z.uuid()).max(256),
    shares: z.optional(z.array(z.uuid()).max(256)).default([]),
    mirror: z.optional(z.union([
        z.object({
            retainSnapshots: z.optional(z.int().gte(1).lte(1000)).default(24),
            peers: z.optional(z.array(z.object({
                fileSystemVolume: z.uuid(),
                fileSystem: z.optional(z.string()),
                peerUuid: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                remoteFileSystem: z.optional(z.union([
                    z.string(),
                    z.null()
                ])),
                phase: z.enum([
                    'pending',
                    'bound',
                    'error',
                    'unbinding'
                ]),
                lastError: z.optional(z.union([
                    z.string(),
                    z.null()
                ]))
            })).readonly()),
            daemon: z.optional(z.union([
                z.object({
                    node: z.optional(z.string()),
                    up: z.optional(z.boolean()),
                    failureCount: z.optional(z.int()),
                    recoveryCount: z.optional(z.int())
                }).readonly(),
                z.null()
            ]).readonly())
        }),
        z.null()
    ])),
    schedule: z.optional(z.union([
        z.object({
            mode: z.optional(z.enum([
                'scheduled',
                'continuous'
            ])),
            everyMinutes: z.optional(z.int().gte(1).lte(10080))
        }),
        z.null()
    ])),
    enabled: z.optional(z.boolean()).default(true),
    phase: z.optional(z.enum([
        'idle',
        'syncing',
        'error',
        'promoted'
    ])),
    lastSyncedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    lagSeconds: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    volumeStates: z.optional(z.array(z.object({
        volume: z.uuid(),
        baseSnapshot: z.optional(z.union([
            z.uuid(),
            z.null()
        ])),
        lastSyncedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ])),
        lastError: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })).readonly()),
    shareStates: z.optional(z.array(z.object({
        share: z.uuid(),
        path: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        state: z.enum([
            'unknown',
            'idle',
            'syncing',
            'failed'
        ]),
        lastSnapshot: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        lastSyncedSnapshot: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        lastSyncedAt: z.optional(z.union([
            z.iso.datetime(),
            z.null()
        ])),
        syncDurationSeconds: z.optional(z.union([
            z.number(),
            z.null()
        ])),
        syncBytes: z.optional(z.union([
            z.int(),
            z.null()
        ])),
        failureReason: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zReplicationLinkList = z.object({
    items: z.array(zReplicationLink),
    nextCursor: z.optional(z.string())
});

/**
 * Body of the ORDERLY DR handover (#38 REP-PROMOZIONE, 2026-08-22): the SOURCE operator's declared decision to hand this link's volumes over to the destination. Sudo-protected AND confirm-typed.
 *
 */
export const zReplicationLinkPromoteRequest = z.object({
    confirm: z.string()
});

/**
 * Body of the REVERSAL's first move (#208 RESIDUI-REP, 2026-08-23): the OLD SOURCE operator's declared decision to demote this frozen (`promoted`) link's volumes into adoptable replicas of the promoted side. Sudo-protected AND confirm-typed. The listed pairs are the acknowledgeStamp discipline again — declaring means having read the promoted cluster: nothing here is ever auto-filled.
 *
 */
export const zReplicationLinkDemoteRequest = z.object({
    confirm: z.string(),
    volumes: z.array(z.object({
        volume: z.uuid(),
        sourceVolume: z.uuid()
    })).min(1).max(256)
});

/**
 * The demotion's synchronous answer (#208): what was demoted, what memory was consumed. The link itself is GONE (consumed in the same transaction — the frozen story ended; the reverse protection is a new link on the promoted cluster).
 *
 */
export const zReplicationLinkDemoteResult = z.object({
    link: z.uuid(),
    volumes: z.array(z.object({
        volume: z.uuid(),
        name: z.string(),
        sourceCluster: z.string(),
        sourceVolume: z.uuid()
    })),
    promotionsConsumed: z.int()
});

/**
 * The registration, on the SERVING cluster (site B), of one remote sync volume's identity (#39 REP-SYNC-MS, 2026-08-22). Config-class row; `phase` is its whole lifecycle (`serving` → `promoted`, terminal).
 *
 */
export const zSyncLink = zResourceCommon.and(z.object({
    name: z.string(),
    sourceCluster: z.string(),
    sourceVolume: z.uuid(),
    volumeName: z.string(),
    sizeGiB: z.int().gte(1),
    volume: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    witnessEndpoints: z.optional(z.array(z.string())),
    phase: z.enum([
        'serving',
        'promoted'
    ]),
    promotedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    promotedEpoch: z.optional(z.union([
        z.int().readonly(),
        z.null()
    ]).readonly()),
    promotedMode: z.optional(z.nullable(z.enum([
        'witness',
        'forced'
    ]))),
    promotedDevice: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    adoptedVolume: z.optional(z.union([
        z.uuid().readonly(),
        z.null()
    ]).readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zSyncLinkList = z.object({
    items: z.array(zSyncLink),
    nextCursor: z.optional(z.string())
});

/**
 * Creation body of `POST /api/v1/syncLinks` — run on the SERVING cluster (site B) when the pair is set up. `volume` must name a local MULTISCSI volume that is `ready`, not itself a sync volume, not a DR replica and not already registered by another SyncLink (`422 validation` / `409 conflict`, speaking). Exposing that volume as the LUN the source consumes stays the ordinary targets machinery (`targets.yaml` — one volume at LUN 0, host ACLs, CHAP): this object registers IDENTITY, it does not mint exports.
 *
 */
export const zSyncLinkCreateRequest = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    scope: z.optional(z.string().max(255)),
    notes: z.optional(z.string().max(65536)),
    sourceCluster: z.string().min(1),
    sourceVolume: z.uuid(),
    volumeName: z.string().min(1).max(63),
    sizeGiB: z.int().gte(1),
    volume: z.uuid(),
    witnessEndpoints: z.optional(z.array(z.string().min(1)))
});

/**
 * Body of the SITE-B PROMOTION (#39 REP-SYNC-MS, 2026-08-22). Sudo AND confirm-typed (the #38 idiom); NO `acknowledgeStamp` — the leg IS the bytes (see the file header). `force` is the declared manual floor for a dead or never-configured witness.
 *
 */
export const zSyncLinkPromoteRequest = z.object({
    confirm: z.string(),
    force: z.optional(z.boolean())
});

/**
 * Body of the ADOPTION (#209 REP-S2S5, 2026-08-24 — file header): make the promoted device a FIRST-CLASS volume of this cluster, without copying a byte. Confirm-typed (the family discipline); no `acknowledgeStamp` twin exists — nothing is consumed, a volume is born.
 *
 */
export const zSyncLinkAdoptRequest = z.object({
    confirm: z.string(),
    name: z.optional(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/))
});

/**
 * Body of the SYNC REVERSAL's consuming move (#209 REP-S2S5, 2026-08-24 — ratified: consumption is a DELETE, never a new phase). Sudo-protected AND confirm-typed; `sourceVolume` is the acknowledge discipline of the async demote (#208) again — the operator RE-WRITES the consumed pair, declaring the old primary's volume at the source cluster demolished or re-enslaved as the new direction's backing. Nothing here is ever auto-filled.
 *
 */
export const zSyncLinkDemoteRequest = z.object({
    confirm: z.string(),
    sourceVolume: z.uuid()
});

/**
 * The demotion's synchronous answer (#209): the consumed registration. The row itself is GONE — the fence against the returning old primary is released by this very consumption, and a future re-registration of the same pair (the failback leg) is possible again.
 *
 */
export const zSyncLinkDemoteResult = z.object({
    link: z.uuid(),
    sourceCluster: z.string(),
    sourceVolume: z.uuid(),
    adoptedVolume: z.optional(z.union([
        z.uuid(),
        z.null()
    ]))
});

/**
 * The composite cluster-health snapshot (M5b, 2026-08-04) — one read composing counts the platform already has from replicated config and the observation sweeps: nothing here triggers new probes. PROPOSAL (2026-08-04): the exact block shapes below are pending ratification with the implementing round — counts were chosen over lists (the lists live on their own collections; this endpoint answers "is anything wrong" in one round trip, the wall-dashboard contract).
 *
 */
export const zMonitoringOverview = z.object({
    observedAt: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    nodes: z.object({
        total: z.int(),
        member: z.int(),
        joining: z.int()
    }),
    store: z.object({
        health: z.enum([
            'healthy',
            'degraded',
            'unavailable'
        ]),
        degradedReason: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }),
    storageClusters: z.object({
        total: z.int(),
        degraded: z.int()
    }),
    volumes: z.object({
        total: z.int(),
        ready: z.int(),
        degraded: z.int(),
        error: z.int()
    }),
    fileShares: z.object({
        total: z.int(),
        degraded: z.int()
    }),
    alerts: z.object({
        firing: z.int(),
        unacknowledged: z.int(),
        bySeverity: z.object({
            info: z.int(),
            warning: z.int(),
            critical: z.int()
        })
    })
});

/**
 * One downsampled time series from `metric_samples` (M5b, 2026-08-04). Points are per-step AVERAGES (the scope's "downsampling media per step") with min/max carried alongside — a UI can draw the band without a second query. Steps with NO samples are OMITTED, never zero-filled: a gap in the series is a gap in observation (the staleness-visible doctrine), and the client renders it as such.
 *
 */
export const zMonitoringTrendSeries = z.object({
    targetKind: z.enum([
        'node',
        'cephcluster',
        'multiscsicluster',
        'volume',
        'share',
        'store',
        'objectstore',
        'backuptarget'
    ]),
    targetId: z.union([
        z.string(),
        z.null()
    ]),
    metric: z.string(),
    rangeStart: z.iso.datetime(),
    rangeEnd: z.iso.datetime(),
    stepSeconds: z.int(),
    points: z.array(z.object({
        t: z.iso.datetime(),
        avg: z.number(),
        min: z.number(),
        max: z.number(),
        samples: z.int()
    }))
});

/**
 * A threshold rule over the fixed metric catalog (M5b, 2026-08-04) — config-class, replicated. The backend sweeper evaluates every enabled rule against the latest samples of every matching target: a breach sustained for `forMinutes` FIRES one Alert per (rule, target) pair — the dedup key: a still-burning breach never births a second row — and the value crossing back past `threshold ∓ clearMargin` RESOLVES it (hysteresis). PROPOSAL (2026-08-04): the whole rule shape is this round's proposal on the scope's sketch, pending ratification.
 *
 */
export const zAlertRule = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    metric: z.enum([
        'node.cpuLoad1',
        'node.memUsedPercent',
        'node.fsUsedPercent',
        'node.netRxBytesPerSec',
        'node.netTxBytesPerSec',
        'node.netErrors',
        'node.netDrops',
        'node.replicaTunnelPeersDown',
        'node.configReplicaAgeSeconds',
        'node.crlAgeSeconds',
        'node.machineAccountDivergedSeconds',
        'node.daemonRestarts',
        'node.cephDaemonsMute',
        'node.auditWriteFailures',
        'node.replicationSpoolPercent',
        'node.sanPathsDown',
        'node.diskWearMaxPercent',
        'node.diskMediaErrors',
        'node.maintenanceHours',
        'cephcluster.capacityGiB',
        'cephcluster.usedGiB',
        'cephcluster.usedPercent',
        'cephcluster.daysToFull',
        'cephcluster.osdsDown',
        'cephcluster.pgsInactive',
        'multiscsicluster.capacityGiB',
        'multiscsicluster.usedGiB',
        'multiscsicluster.usedPercent',
        'multiscsicluster.daysToFull',
        'multiscsicluster.missingPvs',
        'multiscsicluster.scrubMismatches',
        'multiscsicluster.scrubAgeDays',
        'volume.health',
        'volume.backupAgeHours',
        'volume.replicaLagSeconds',
        'volume.syncDegraded',
        'volume.resyncPercent',
        'volume.readBytesPerSec',
        'volume.writeBytesPerSec',
        'volume.readIops',
        'volume.writeIops',
        'volume.ioAwaitMs',
        'share.health',
        'share.clients',
        'share.fsUsedPercent',
        'share.daysToFull',
        'share.daemonsNotServing',
        'share.daemonRestarts',
        'share.smbLeaseUnrenewedSeconds',
        'share.opsPerSec',
        'share.bytesPerSec',
        'share.deleteOps',
        'share.authFailures',
        'share.accessDenied',
        'store.health',
        'store.lagSeconds',
        'objectstore.health',
        'objectstore.requestsPerSec',
        'objectstore.bytesPerSec',
        'objectstore.deleteOps',
        'objectstore.authFailures',
        'objectstore.accessDenied',
        'backuptarget.timestampUnreachable',
        'backuptarget.timestampRefused',
        'backuptarget.timestampUnverifiable',
        'backuptarget.timestampInvalid',
        'backuptarget.timestampTampered',
        'backuptarget.timestampAgeDays'
    ]),
    targetKind: z.enum([
        'node',
        'cephcluster',
        'multiscsicluster',
        'volume',
        'share',
        'store',
        'objectstore',
        'backuptarget'
    ]),
    targetSelector: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    operator: z.enum([
        'gt',
        'lt',
        'anomaly'
    ]),
    threshold: z.number(),
    forMinutes: z.optional(z.int().gte(0).lte(1440)).default(0),
    severity: z.enum([
        'info',
        'warning',
        'critical'
    ]),
    clearMargin: z.optional(z.number().gte(0)).default(0),
    enabled: z.optional(z.boolean()).default(true),
    firingCount: z.optional(z.int().readonly()),
    revision: z.optional(z.int().readonly())
}));

export const zAlertRuleList = z.object({
    items: z.array(zAlertRule),
    nextCursor: z.optional(z.string())
});

/**
 * A notification DESTINATION (M5b, 2026-08-04) — config-class, replicated. On every alert transition (fire AND resolve) the sweeper births one `alert.deliver` task per ENABLED sink; the task engine's retry/backoff and log are the delivery record — a sink outage delays notifications, never evaluation. `kind` is IMMUTABLE after creation (the credentials.yaml rationale: retyping is a NEW sink); exactly the per-kind block matching `kind` must be present (`422 validation`, on create and PATCH alike). Mutations are SUDO-GATED (PROPOSAL 2026-08-04): a sink names WHERE alert traffic goes — re-pointing it is an exfiltration primitive, the danger-ladder argument without a secret on the row.
 *
 */
export const zAlertSink = zResourceCommon.and(zRenderedResource).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'email',
        'webhook',
        'syslog'
    ]),
    enabled: z.optional(z.boolean()).default(true),
    email: z.optional(z.object({
        host: z.string().min(1).max(255),
        port: z.optional(z.int().gte(1).lte(65535)).default(587),
        tls: z.optional(z.enum([
            'starttls',
            'implicit',
            'none'
        ])),
        from: z.string().min(3).max(255),
        to: z.array(z.string().min(3).max(255)).min(1).max(32),
        credential: z.optional(z.union([
            z.uuid(),
            z.null()
        ]))
    })),
    webhook: z.optional(z.object({
        url: z.url().max(2048).regex(/^https:\/\//),
        format: z.optional(z.enum([
            'alert',
            'callhome'
        ])),
        secret: z.optional(z.union([
            z.uuid(),
            z.null()
        ]))
    })),
    syslog: z.optional(z.object({
        host: z.string().min(1).max(255),
        port: z.optional(z.int().gte(1).lte(65535)).default(6514),
        transport: z.optional(z.enum([
            'tls',
            'tcp'
        ])),
        tls: z.optional(z.object({
            caPem: z.optional(z.union([
                z.string().max(65536),
                z.null()
            ])),
            fingerprintSha256: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            clientCertificate: z.optional(z.union([
                z.uuid(),
                z.null()
            ])),
            serverName: z.optional(z.union([
                z.string().max(255),
                z.null()
            ]))
        })),
        facility: z.optional(z.enum([
            'kern',
            'user',
            'mail',
            'daemon',
            'auth',
            'syslog',
            'lpr',
            'news',
            'uucp',
            'cron',
            'authpriv',
            'ftp',
            'local0',
            'local1',
            'local2',
            'local3',
            'local4',
            'local5',
            'local6',
            'local7'
        ])),
        appName: z.optional(z.string().max(48)).default('tvstor'),
        forward: z.optional(z.object({
            alerts: z.optional(z.boolean()).default(true),
            journal: z.optional(z.boolean()).default(true),
            daemonLogs: z.optional(z.boolean()).default(true)
        }))
    })),
    revision: z.optional(z.int().readonly())
}));

export const zAlertSinkList = z.object({
    items: z.array(zAlertSink),
    nextCursor: z.optional(z.string())
});

/**
 * ENEA-A-R11 (2026-10-23, N5): the READ-ONLY bearer token of `GET /metrics`, rendered ONCE by `POST /monitoring/metricsToken` — the store keeps only its SHA-256 (`cluster.settings.metrics.tokenSha256`); a new POST ROTATES it (the previous token stops working at once), `DELETE` revokes it (the endpoint answers 401 until a new one is issued). Prometheus uses it as `bearer_token` / `bearer_token_file` in its scrape config.
 *
 */
export const zMetricsToken = z.object({
    token: z.string(),
    issuedAt: z.iso.datetime()
});

/**
 * A fired alert (M5b, 2026-08-04) — a RUNTIME row on the store, born by the evaluation sweeper, never by a client: read + ack only, no create, no delete (resolved alerts are reaped by the retention sweep past the cluster's `monitoring.retentionDays` — default 90, configurable since M5b-1, ratifica 2026-08-04 #43 — the metric-retention twin: one knob covers both halves of monitoring history). NOT a scoped resource (no scope/tags/notes — the tasks precedent: machine-born rows, addressed by id, found by `?filter=`). DEDUP KEY (rule, target): at most ONE `firing` row per pair exists — a persisting breach keeps the one row burning; resolve closes it and the NEXT breach births a NEW row, so history accumulates as rows, one per episode. The rule-derived fields (`metric`, `severity`, `operator`, `threshold`) are FROZEN COPIES taken at fire time: editing the rule afterwards never rewrites what an existing alert says happened (the backups frozen-policy doctrine).
 *
 */
export const zAlert = z.object({
    id: z.uuid().readonly(),
    rule: z.uuid().readonly(),
    ruleName: z.string().readonly(),
    targetKind: z.enum([
        'node',
        'cephcluster',
        'multiscsicluster',
        'volume',
        'share',
        'store',
        'objectstore',
        'backuptarget'
    ]),
    targetId: z.union([
        z.string().readonly(),
        z.null()
    ]).readonly(),
    targetName: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    metric: z.string().readonly(),
    severity: z.enum([
        'info',
        'warning',
        'critical'
    ]),
    operator: z.enum([
        'gt',
        'lt',
        'anomaly'
    ]),
    threshold: z.number().readonly(),
    value: z.number().readonly(),
    phase: z.enum([
        'firing',
        'resolved'
    ]),
    firedAt: z.iso.datetime().readonly(),
    resolvedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    acknowledgedBy: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    acknowledgedAt: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly()),
    message: z.optional(z.string().readonly()),
    deliveriesExpected: z.optional(z.int().readonly()),
    deliveriesUnborn: z.optional(z.int().readonly()),
    deliveriesUnbornReason: z.optional(z.union([
        z.string().readonly(),
        z.null()
    ]).readonly()),
    unclosableSince: z.optional(z.union([
        z.iso.datetime().readonly(),
        z.null()
    ]).readonly())
});

export const zAlertList = z.object({
    items: z.array(zAlert),
    nextCursor: z.optional(z.string())
});

/**
 * The resource a task operates on.
 */
export const zTaskTarget = z.object({
    kind: z.optional(z.string()),
    id: z.optional(z.uuid()),
    name: z.optional(z.string())
});

/**
 * The atom of execution — a single idempotent operation with an owner, an executor, a structured log, progress and a terminal outcome. Flat, server-created and entirely read-only. A task may own CHILDREN (TVirt round #128 model): the children are its ordered steps, each gated on the success of the previous, and the parent itself executes no code — its phase derives from the children (running while one runs, failed when one fails, cancelled when the family's cancellation was requested, succeeded when all closed).
 *
 */
export const zTask = z.object({
    id: z.string(),
    scope: z.optional(z.string().readonly()),
    type: z.string(),
    target: z.optional(zTaskTarget),
    params: z.optional(z.record(z.string(), z.unknown()).readonly()),
    phase: z.enum([
        'queued',
        'running',
        'succeeded',
        'failed',
        'cancelled',
        'aborted'
    ]),
    parent: z.optional(z.string().readonly()),
    stepName: z.optional(z.string().readonly()),
    stepSeq: z.optional(z.number().readonly()),
    superseded: z.optional(z.boolean().readonly()),
    failedStep: z.optional(z.string().readonly()),
    progressPercent: z.optional(z.number().gte(0).lte(100)),
    message: z.optional(z.string()),
    startedAt: z.optional(z.iso.datetime()),
    finishedAt: z.optional(z.iso.datetime()),
    error: z.optional(zProblem),
    result: z.optional(z.record(z.string(), z.unknown()).readonly()),
    node: z.optional(z.string())
});

export const zTaskList = z.object({
    items: z.array(zTask),
    nextCursor: z.optional(z.string())
});

/**
 * Outcome of a parent-task abort — declared undo actions of completed steps run in reverse where they exist; honest where they do not.
 *
 */
export const zAbortResult = z.object({
    undone: z.optional(z.array(z.string())),
    reported: z.optional(z.array(z.string()))
});

/**
 * A session on the answering node (sessions are node-local — TVirt API spec §3 heritage, adopted 2026-08-02). Scoped semantics (TVirt #24/#25, adopted 2026-08-02): a session inherits the SCOPE of its owner — the caller always reaches their own sessions, and reaches another principal's only with `user.sessions.revoke` granted on that principal's scope.
 *
 */
export const zSessionWritable = z.object({
    user: zSessionUser,
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    mustChangePassword: z.optional(z.boolean()),
    mfaEnrollmentDeadline: z.optional(z.iso.datetime()),
    mfaEnrollmentDaysLeft: z.optional(z.int()),
    mode: z.optional(z.enum([
        'console',
        'kiosk'
    ])),
    kioskShares: z.optional(z.array(zKioskShare))
});

/**
 * Fields shared by every flat resource (TVirt ADR-22, adopted 2026-08-02). The kind's identifier field (`name`, `username` for users) is declared per kind. Writes are last-write-wins (no client-side optimistic locking). RESPONSE-REQUIRED RULE (#195, 2026-08-21 — reperti R1/R5 of #183): resource schemas declare `id` and — where every render carries it — `scope` as `required`, because the wire ALWAYS sends them on responses (every backend model renders `id: r.id` and `scope: r.scopePath`); before this the generated client types said `id?: string` about a field that never misses, and api-js had to re-strengthen 25 schemas at runtime. Flat resources compose the `RenderedResource` schema below next to this one; kinds that declare their own `id` (Grant, ClusterNode, NodeRaid, NodeVdisk, VolumeSnapshot, Bucket, S3Account) list it in their own `required` directly. On REQUESTS those entries do not bind: `id` is `readOnly` (the OpenAPI 3.0 semantics of readOnly+required, kept deliberately under 3.1) and `scope` is marked `x-tvstor-request-optional` (writable at creation but defaulted server-side, so a create may omit it) — the backend's request validator strips both classes from `required` before compiling (http/validation.ts).
 *
 */
export const zResourceCommonWritable = z.object({
    scope: z.optional(z.string().max(255).regex(/^(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){0,7}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/)),
    notes: z.optional(z.string().max(65536))
});

/**
 * The response-required half of a rendered flat resource (#195, 2026-08-21 — reperti R1/R5 of #183; see the rule on ResourceCommon): composed via allOf NEXT TO ResourceCommon by every resource whose render always carries `id` and `scope`. A SEPARATE schema, not a `required` on ResourceCommon, because the Cluster singleton and the root Scope legitimately omit them; and `id`/`scope` are REDECLARED here (types only — the full constraints stay on ResourceCommon and still bind, the fields being always present) because every generator in the toolchain honors `required` only for properties declared at the same object level.
 *
 */
export const zRenderedResourceWritable = z.object({
    scope: z.string()
});

/**
 * A user account. `username` is the kind's identifier field.
 */
export const zUserWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    username: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?$/),
    displayName: z.optional(z.string()),
    enabled: z.optional(z.boolean()).default(true),
    mustChangePassword: z.optional(z.boolean()).default(false),
    initialPassword: z.optional(z.string()),
    mfaExternal: z.optional(z.boolean()).default(false),
    mfaRequired: z.optional(z.boolean()).default(false)
}));

/**
 * A node of the namespace. Creating `{scope: "/roma-1", name: "tenant-web"}` makes `/roma-1/tenant-web` available for resources, RBAC bindings and delegation.
 *
 */
export const zScopeWritable = zResourceCommonWritable.and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    quota: z.optional(z.union([
        z.object({
            capacityBytes: z.optional(z.int().gte(1073741824))
        }),
        z.null()
    ]))
}));

export const zGrantWritable = z.intersection(z.unknown(), z.object({
    user: z.optional(z.string().regex(/^[a-z0-9]([a-z0-9.-]{0,61}[a-z0-9])?@[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)),
    group: z.optional(z.string()),
    role: z.string(),
    grantRight: z.optional(z.boolean()).default(false),
    scope: z.string().max(257).regex(/^(\/\*|(\/[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?){1,8}(\/\*)?|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/\*)?)$/)
}));

/**
 * The CLUSTER STORE's high-availability state (M2f, dettatura Luca 2026-08-03, 8° giro — YugabyteDB DROPPED). The store is PostgreSQL MANAGED BY US at EVERY scale: one primary plus N secondaries in streaming replication, worker-supervised detached instances (Annex M pattern: pidfile + logfile under `<systemPath>/store/`). ACK POLICY (RATIFICATA Luca 2026-08-03, 9° giro — supersedes the M2f `syncPolicy` PROPOSAL): a config commit is confirmed only when replicated on ALL live, non-excluded secondaries — the ACK SET, rendered onto the primary as `FIRST <n>` over every set member. A fallen secondary is FENCED and EXCLUDED (fence-before-degrade on the control plane; see `degradedReason`), and a member re-enters the set only once caught up (`members[].inAckSet` / `members[].fenced`). There is no engine switch any more — growing from 2 to 3 nodes is "add a secondary" (`addStoreMember`), and the quorum deciding a PROMOTION stays the PLATFORM's (witness grant or verified fencing cascade), never the DB's. Everything here is OBSERVED — since the 9° giro the block carries no writable field (the retired `syncPolicy` write took the `cluster.store.update` permission with it). READ GUARD (PROPOSAL 2026-08-03, pending ratification): rendering this block requires `cluster.store.read` on top of the operation's `cluster.read` — endpoints, slots and lag describe the store's topology.
 *
 */
export const zClusterStoreWritable = z.record(z.string(), z.unknown());

/**
 * The node's cluster — a singleton, install-born (TVirt heritage, adopted 2026-08-02). Not an ordinary scoped resource: no `scope` field (it OWNS the single tree's root, named after it), never listed, never deletable at runtime. Cluster renders expose `revision`: it is the store generation clients watch for convergence.
 *
 */
export const zClusterWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    security: z.optional(z.object({
        passwordPolicy: z.optional(z.object({
            minLength: z.optional(z.int().gte(8).lte(128)).default(12)
        })),
        sessionMaxIdleMinutes: z.optional(z.int().gte(5).lte(43200)).default(480),
        lockout: z.optional(z.object({
            threshold: z.optional(z.int().gte(1)).default(5),
            durationMinutes: z.optional(z.int().gte(1)).default(15)
        }))
    })),
    monitoring: z.optional(z.object({
        anomaly: z.optional(z.object({
            windowHours: z.optional(z.int().gte(1).lte(168)).default(24),
            minSamples: z.optional(z.int().gte(6).lte(2016)).default(24),
            alpha: z.optional(z.union([
                z.number().gt(0).lte(1),
                z.null()
            ])).default(null)
        })),
        retentionDays: z.optional(z.int().gte(7).lte(3650)).default(90)
    })),
    audit: z.optional(z.object({
        retentionDays: z.optional(z.int().gte(90).lte(3650)).default(365),
        sensitiveReads: z.optional(z.object({
            enabled: z.boolean().default(false),
            scopes: z.optional(z.array(z.string().max(255)).max(64)).default(['/*'])
        }))
    })),
    logging: z.optional(z.object({
        rotation: z.optional(z.object({
            maxSizeMiB: z.optional(z.int().gte(1).lte(10240)).default(100),
            maxAgeDays: z.optional(z.int().gte(1).lte(3650)).default(30),
            keep: z.optional(z.int().gte(1).lte(365)).default(10),
            compress: z.optional(z.boolean()).default(true)
        })),
        journal: z.optional(z.object({
            maxUseMiB: z.optional(z.int().gte(16).lte(1048576)).default(1024),
            maxRetentionDays: z.optional(z.int().gte(1).lte(3650)).default(30)
        })),
        audit: z.optional(z.object({
            maxSizeMiB: z.optional(z.int().gte(16).lte(4096)).default(256),
            keep: z.optional(z.int().gte(1).lte(64)).default(8)
        }))
    })),
    transportSecurity: z.optional(z.object({
        plainHttp: z.optional(z.enum([
            'allowed',
            'refused'
        ]))
    })),
    nfsEngine: z.optional(z.object({
        ceph: z.optional(z.enum([
            'ganesha',
            'knfsd'
        ])),
        rdma: z.optional(z.object({
            enabled: z.optional(z.boolean()).default(false),
            port: z.optional(z.int().gte(1).lte(65535)).default(20049)
        }))
    })),
    gatewayFailover: z.optional(z.object({
        rebalance: z.optional(z.enum([
            'automatic',
            'manual'
        ])),
        rebalanceAfterMs: z.optional(z.int().gte(0).lte(3600000)).default(60000)
    })),
    documentEditor: z.optional(z.union([
        z.object({
            serverUrl: z.string().min(1).max(1024),
            wopiBaseUrl: z.optional(z.string().min(1).max(1024)),
            certFingerprint: z.optional(z.union([
                z.string().max(95),
                z.null()
            ]))
        }),
        z.null()
    ])),
    externalMfa: z.optional(z.union([
        z.object({
            serverUrl: z.string().min(1).max(1024),
            realm: z.optional(z.union([
                z.string().max(255),
                z.null()
            ])),
            certFingerprint: z.optional(z.union([
                z.string().max(95),
                z.null()
            ]))
        }),
        z.null()
    ])),
    mfaGraceDays: z.optional(z.int().gte(0).lte(365)).default(14),
    mfaIssuer: z.optional(z.union([
        z.string().min(1).max(64),
        z.null()
    ])),
    replicaEncryption: z.optional(z.object({
        enabled: z.optional(z.boolean()).default(false),
        listenPort: z.optional(z.int().gte(1).lte(65535)).default(51820),
        mtu: z.optional(z.union([
            z.int().gte(1280).lte(9216),
            z.null()
        ])).default(null),
        persistentKeepaliveSec: z.optional(z.int().gte(0).lte(3600)).default(25)
    })),
    store: z.optional(zClusterStoreWritable),
    witness: z.optional(z.union([
        z.object({
            endpoints: z.array(z.string().min(1).max(255)).min(1),
            order: z.optional(z.enum([
                'witnessFirst',
                'fencingFirst'
            ])),
            leaseTtlMs: z.optional(z.int().gte(1000)).default(15000),
            renewMs: z.optional(z.int().gte(250)).default(5000),
            marginMs: z.optional(z.int().gte(0)).default(5000)
        }),
        z.null()
    ])),
    failover: z.optional(z.object({
        mode: z.optional(z.enum([
            'auto',
            'quorum',
            'fencing'
        ])),
        witnessNode: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    networkQos: z.optional(z.object({
        enabled: z.optional(z.boolean()).default(false),
        classes: z.optional(z.record(z.string(), z.object({
            weight: z.int().gte(1).lte(1000),
            floorMbps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            ceilingMbps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ]))
        })))
    })),
    notes: z.optional(z.string().max(65536))
});

/**
 * One member of the store (M2f, 2026-08-03): its declared membership plus the replication state observed on the primary. Pure observation for everything but `node`/`role` — never a declaration.
 *
 */
export const zClusterStoreMemberWritable = z.object({
    node: z.optional(z.uuid()),
    name: z.optional(z.string()),
    role: z.optional(z.enum([
        'primary',
        'secondary',
        'none'
    ])),
    endpoint: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    slot: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    syncState: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    lagBytes: z.optional(z.union([
        z.number(),
        z.null()
    ])),
    lagSeconds: z.optional(z.union([
        z.number(),
        z.null()
    ])),
    observedAt: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ]))
});

export const zAuditEventListWritable = z.object({
    items: z.array(z.unknown()),
    nextCursor: z.optional(z.string())
});

/**
 * A cluster member (TVirt heritage, adopted 2026-08-02) — nodes are OBJECTS of this registry, not scopes. In M1 the registry holds exactly the creating node, registered `member` by install-time seeding (`db:init-cluster` in dev). Exposes `revision`. Since M2a (2026-08-02) the node item accepts PATCH for the first per-node declaration, `dataDisks`, and renders the observed `blockDevices` inventory. ROADMAP (2026-08-02): the joiner lifecycle (`pending-join`/`joining`, `tvsj_` tokens) and the remaining declarations (settings, storageNetworks, BMC) join with their milestones.
 *
 */
export const zClusterNodeWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    ip: z.string().max(45),
    site: z.optional(z.union([
        z.string().max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
        z.null()
    ])),
    dataDisks: z.optional(z.array(z.object({
        name: z.string(),
        serial: z.string().min(1)
    }))),
    fencingDevices: z.optional(z.array(zNodeFencingDevice)),
    notes: z.optional(z.string().max(65536))
});

/**
 * The CHUNK CENSUS of ONE marked disk (DISK-1 L2, 2026-08-05 — dettature e ratifiche Luca 2026-08-04, docs/vdisk-partial-disk-2026-08-04.md): the disk is a PV in ITS OWN VG (`tvstor-<serial>` — topologia (a) ratificata: blast radius = the disk; a disk dies alone) carved into ANONYMOUS, FUNGIBLE chunks, all of the SAME per-disk size. `create-chunks` decides that size PER DISK (rettifica 2026-08-04: /dev/sda at 1TiB chunks and /dev/sdb at 512GiB chunks coexist on one node); no chunk is individually addressable — no names, no ids, only COUNTERS (ratifica finale 2026-08-04: "i chunk sono ANONIMI... conta solo la QUANTITÀ"). Consumers — raids (per-disk aggregates) and jbod vdisks (same-disk concat) — allocate by QUANTITY and the allocator takes free chunks of the disk. BINDING INTERPRETATION (this round, shared domain `common/src/multiscsi/chunks.ts`): a chunk is the ALLOCATION GRANULE of its disk — carving creates NO LVs; the physical LVs are the per-disk AGGREGATES and jbod vdisks, materialized at create-raid/create-vdisk time (LVM extent allocation IS the ratified same-disk concat). Config-class row keyed by the disk serial (replicated, born by `node.chunks.create`); `chunksAllocated` is config (the sum of live claims, moved at admission in the SAME store transaction as the consuming row), `chunksTotal` is computed at admission from the observed inventory and confirmed by the executor's VG census, and the observed VG state composes from the worker sweep — staleness visible, never hidden. HONESTY (manuale operatore, ratifica 2026-08-04, the SHR lesson): consumers sharing a disk via chunks contend for its IOPS — a rebuild of one array reads/writes a disk that serves others; the M5b metrics make the contention visible.
 *
 */
export const zNodeDiskChunksWritable = z.object({
    disk: z.optional(z.object({
        name: z.optional(z.string()),
        serial: z.optional(z.string().min(1))
    }))
});

/**
 * One member of a node raid — a PER-DISK AGGREGATE (nota ratificata Luca 2026-08-04): the `count` chunks asked of `disk` concatenate into ONE LV, and md sees exactly one device per disk. The aggregate's byte size is `count` × the disk's chunk size — the quantity the redundant levels' byte-wise admission compares.
 *
 */
export const zNodeRaidMemberWritable = z.object({
    disk: z.object({
        name: z.string(),
        serial: z.string().min(1)
    }),
    count: z.int().gte(1)
});

/**
 * A RAID — a node-local md array over PER-DISK CHUNK AGGREGATES (DISK-1 L3, 2026-08-05 — CLI ratificata 2026-08-04: `config-node <node> create-raid --name rvol1 --level 0 --members /dev/sda:1x,/dev/sdb:2x`). For each member the allocator takes `count` free chunks of that disk and concatenates them into ONE aggregate LV; md is built over the aggregates — one device per disk BY CONSTRUCTION. Levels 0/1/5/6/10 ONLY: linear is ratified OUT (rettifica finale 2026-08-04 — no cross-disk concatenation in any form; growing past one disk is raid0's job, zone striping absorbing unequal aggregates). ADMISSION IN BYTES (nota ratificata 2026-08-04): for the redundant levels (1/5/6/10) every aggregate must have the SAME total size in BYTES, counting each disk's OWN chunk size — `/dev/sda:1x` of 1TiB chunks mirrors `/dev/sdb:2x` of 512GiB chunks and MUST be admitted; a mismatch is `422 validation` SPEAKING GiB per aggregate, never bare chunk counts. raid0 admits differing aggregates (md zone striping, no waste). DISTINCT DISKS are the redundancy rule and hold by construction (one aggregate = one disk): a disk may appear ONCE in `members` — a duplicate is `422 validation` on every level (more capacity from one disk is a higher `count`, never a second entry). Subresource of the node registry like the vdisks (no scope/tags/notes); `name` unique PER NODE. Config-class row (replicated); observed fields compose from the worker sweep through the node observation outbox (staleness visible, never hidden). HONESTY (manuale operatore, ratifica 2026-08-04): a raid0 loses the WHOLE device with ANY one member disk, and its reconstruction after disk replacement is the ENTIRE mirror resync over the replica network; arrays sharing disks via chunks contend under rebuild (the SHR lesson) — declared, not hidden.
 *
 */
export const zNodeRaidWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    level: z.enum([
        'raid0',
        'raid1',
        'raid5',
        'raid6',
        'raid10'
    ]),
    members: z.array(zNodeRaidMemberWritable).min(2)
});

/**
 * A VDISK — the node-local block device an engine consumes, LEVEL FOUR of the chunk stack (DISK-1, 2026-08-05 — the M2d whole-disk md model is RETIRED; pre-GA break ratified 2026-08-04: existing vdisks migrate by RECREATION, the old `--type/--members` surface is GONE). THREE FORMS, exactly one per vdisk: the RAID form (`create-vdisk vdisk1 rvol1`) — the vdisk wraps one of the node's raids, whose md device IS the vdisk's device (the raid is claimed single-owner); the JBOD form (`create-vdisk vdisk1 --disk /dev/sda --chunks 2` — shape PROPOSAL fixed this round per the ratified same-disk concat) — the vdisk is ONE LV concatenating K chunks of ONE disk. A jbod vdisk NEVER crosses disks (rettifica finale 2026-08-04: no cross-disk concatenation in any form — growing past one disk is raid0's job); the EXTERNAL form (#201 EXT-1, 2026-08-21 — ratifica docs/ratifiche-2026-08-21.md §9, uscita B of docs/ext-disegno-2026-08-21.md) — the vdisk's device is the LUN of a FOREIGN SAN (iSCSI or NVMe-oF): no local chunks, no local md — disk-fault tolerance is the foreign SAN's business (the honesty manual), while node/site redundancy stays with the EXISTING models on top (the vdisk external is a FULL datapool member: RAID1 multiscsi mirror across two external vdisks, ceph 3/2 at three or more). The LOGIN FOLLOWS THE VOLUME AUTHORITY (addendum ratificato): only the volume's primary connects and assembles; on failover the NEW primary re-connects — the same choreography as today's internal fabric md. Downstream NOTHING changes: the vdisk remains the internal-transport export unit (ratifica D-C: one nvmet subsystem per vdisk, NQN `nqn.2026-08.it.trantor.tvstor:<cluster>:<node>:vdisk:<vdisk8hex>` — exported when a datapool using it is engine-bound, not at vdisk creation) and the contribution unit of datapools (`datapools.yaml`, UNTOUCHED). Vdisks are SUBRESOURCES of the node registry, not scoped objects — no scope/tags/notes; `name` is unique PER NODE (dettatura 2026-08-03: `node1:vdisk1` and `node2:vdisk1` coexist). Config-class row (`vdisks`, replicated); observed fields compose from `vdisk_status` (worker sweep through the node observation outbox — staleness visible, never hidden). Vdisk RESIZE (lvextend) is ratified first-class (2026-08-04) and LANDED with DISK-1.1 (2026-08-05): the JBOD form grows through `actions/expand` (grow-only, whole chunks of its ONE disk); the RAID form is the dated seam `raid expand arrives with DISK-1.2`.
 *
 */
export const zNodeVdiskWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    raid: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    jbod: z.optional(z.union([
        z.object({
            disk: z.object({
                name: z.string(),
                serial: z.string().min(1)
            }),
            chunks: z.int().gte(1)
        }),
        z.null()
    ])),
    external: z.optional(z.union([
        z.object({
            transport: z.enum([
                'iscsi',
                'nvmeof'
            ]),
            portals: z.array(z.object({
                host: z.string().min(1),
                port: z.int().gte(1).lte(65535)
            })).min(1),
            iqn: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            lun: z.optional(z.union([
                z.int().gte(0),
                z.null()
            ])),
            nqn: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            nsid: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            credential: z.optional(z.union([
                z.uuid(),
                z.null()
            ])),
            wwid: z.optional(z.union([
                z.string(),
                z.null()
            ]))
        }),
        z.null()
    ]))
});

/**
 * A DECLARED NETWORK (NET-1, 2026-08-05 — charter docs/networking-modello-2026-08-04.md): one of the four per-node network categories, cluster-level — `{name, category, vlanId?, subnet, mtu?}` — plus per-node BINDINGS (the sub-resource: interface or bond, and the node's address inside `subnet`). Declaring a network is what migrates a surface off its dated address seam: replicaInternal re-homes the internal NVMe/TCP transport (and the ENC-1 WireGuard endpoints), client re-homes the iSCSI portals and becomes the validation home of the NAS serviceAddresses and S3 endpoints, replicaExternal sources the DR links. With no networks declared everything stays on the seams — the honest fallback, dated 2026-08-05.
 *
 */
export const zNetworkWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    category: z.enum([
        'management',
        'replicaInternal',
        'client',
        'replicaExternal',
        'san'
    ]),
    vlanId: z.optional(z.union([
        z.int().gte(1).lte(4094),
        z.null()
    ])),
    subnet: z.string().min(9).max(64),
    mtu: z.optional(z.union([
        z.int().gte(576).lte(65520),
        z.null()
    ])),
    qos: z.optional(z.union([
        z.object({
            enabled: z.optional(z.boolean()),
            classes: z.optional(z.record(z.string(), z.object({
                weight: z.int().gte(1).lte(1000),
                floorMbps: z.optional(z.union([
                    z.int().gte(1),
                    z.null()
                ])),
                ceilingMbps: z.optional(z.union([
                    z.int().gte(1),
                    z.null()
                ]))
            })))
        }),
        z.null()
    ]))
}));

/**
 * ONE NODE'S ATTACHMENT to a network (NET-1, 2026-08-05): which carrier the node uses — a plain `interface` OR a platform-rendered `bond`, EXACTLY ONE of the two (`422 validation` when both or neither) — and the node's `address` inside the network's subnet. Declared via UPSERT PUT keyed by node id (one binding per network per node by construction); the worker reconcile pass renders bond, VLAN sub-interface and address and drift-heals them every pass. The readOnly fields (`node`, `nodeName`, `observed`) are known-and-ignored on input (round #149 strictness).
 *
 */
export const zNetworkNodeBindingWritable = z.object({
    interface: z.optional(z.union([
        z.string().max(15).regex(/^[A-Za-z0-9_.-]{1,15}$/),
        z.null()
    ])),
    bond: z.optional(z.union([
        z.object({
            members: z.array(z.string().max(15).regex(/^[A-Za-z0-9_.-]{1,15}$/)).min(2),
            mode: z.enum([
                'active-backup',
                '802.3ad'
            ]),
            lacpRate: z.optional(z.nullable(z.enum([
                'slow',
                'fast'
            ])))
        }),
        z.null()
    ])),
    address: z.string().max(64)
});

/**
 * A CEPH CLUSTER (REMODEL #214, 2026-08-26 — the ceph half of the old datapool, now a first-class resource): a named set of `{node, vdisk}` members whose vdisks back one OSD each. The POST births the whole fabric (Annex N birth doctrine restored — no unbound state exists); volumes on this cluster are RBD images on the one `/volumes` surface.
 *
 */
export const zCephClusterWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    members: z.array(z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    })).min(1),
    encryption: z.optional(z.object({
        mode: z.enum([
            'none',
            'luks2'
        ])
    })),
    gatewayAddresses: z.optional(z.array(z.string().max(64)).max(16)),
    gatewayQos: z.optional(z.union([
        z.object({
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ])),
    gatewayAddressScopes: z.optional(z.array(z.object({
        address: z.string().max(64),
        scope: z.string()
    })).max(16))
}));

/**
 * A MULTISCSI CLUSTER (REMODEL #214, 2026-08-26 — the multiscsi half of the old datapool, now a first-class resource): a named set of `{node, vdisk}` members, one VG + thin pool per member vdisk, thin-LV legs under cross-node md mirrors per volume. The POST births the whole fabric — no unbound state exists.
 *
 */
export const zMultiscsiClusterWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    members: z.array(z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    })).min(1),
    encryption: z.optional(z.object({
        mode: z.enum([
            'none',
            'luks2'
        ])
    })),
    scrub: z.optional(z.union([
        z.object({
            everyDays: z.int().gte(1).lte(365),
            startHour: z.int().gte(0).lte(23),
            durationHours: z.int().gte(1).lte(24),
            maxSpeedKiBps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ]))
        }),
        z.null()
    ]))
}));

/**
 * NAS-MDS-1 via B (2026-10-29; ratifica «Vada per B» 2026-10-27 §10, modello docs/nas-mds-1-2026-10-28.md §2.2): the CEPH FILE-PLANE options of a volume — they shape HOW a `filesystem` volume materializes on a ceph cluster and mean nothing elsewhere. THE DEFAULT (absent, null, or both flags false): the volume is a subvolume GROUP `vol-<vol8hex>` of the cluster's ONE CephFS `tvstor-fs-<cluster8hex>` (metadata pool `-meta` and default data pool `-data`, both replicated 3/2 — cluster fabric, born with the cluster's first file share, gone with the cluster's destroy), with the group's QUOTA = `sizeGiB` (the volume's capacity is enforced by Ceph, and `actions/expand` grows it); a volume whose `redundancy` is not the default 3/2 — erasure coded included — gets its OWN data pool `tvstor-fs-<cluster8hex>-<vol8hex>-data` (or `-ec`) laid on its group, so `redundancy` keeps its per-volume meaning. The shares are subvolumes INSIDE the volume's group (`/volumes/vol-<vol8hex>/<share uuid>`), served by the cluster's MDS set (one MDS per member node, kept by the reconciler at every pass — also for nodes that join later —, one active rank per filesystem plus standby-replay). `dedicated: true` = today's model kept on request: a CephFS of the volume's own `tvstor-fs-<vol8hex>` with its own pools sized by its redundancy — for strong isolation (cephx `fsname=`), or a mirror peer DIFFERENT from the cluster filesystem's (ReplicationLink file: one peer per filesystem). `dedicatedRank: true` = the group's tree pinned (`ceph.dir.pin`) to an MDS rank of ITS OWN on the cluster filesystem (`max_mds` grows by one; `pinnedRank` says which rank, fixed at materialization). Both flags together are `422 validation` (a dedicated filesystem already has a rank of its own). Both COUNT in the standby budget of `422 mds_standby_exhausted`: N member nodes serve at most N−1 of {the cluster filesystem, dedicated filesystems, pinned ranks}. Ceph only (`422 validation` on a multiscsi cluster); writable while the volume is `declared`, IMMUTABLE once materialized (`422 immutable_field` naming `cephfs` on PATCH). Volumes materialized BEFORE this round keep the filesystem they were born with — rendered `dedicated: true, inherited: true`, no byte moves at the upgrade — and move to the cluster filesystem only through the explicit `POST /volumes/{volume}/actions/migrateToSharedFilesystem`.
 *
 */
export const zVolumeCephfsWritable = z.union([
    z.object({
        dedicated: z.optional(z.boolean()),
        dedicatedRank: z.optional(z.boolean())
    }),
    z.null()
]);

/**
 * A volume — the PROMISE of capacity with a redundancy policy (REMODEL R2 #214, 2026-08-26, docs/remodel-disegno-2026-08-26.md §2.3: the DECLARED model). The volume names its STORAGE CLUSTER (a CephCluster or a MultiscsiCluster — mandatory and immutable) and is born as PURE BUREAUCRACY: a `201` synchronous row, admitted by the anti-overcommit formula, with NOTHING on any device. The bytes rise LAZILY at the FIRST CONSUMER — a Target fixes the species `lun` (multiscsi: the MD RAID1 mirror over thin-LV legs; ceph: the volume's OWN pool `tvstor-vol-<vol8hex>` sized by its redundancy + the RBD image); the internal FileShare/ObjectStore backing births fix `filesystem`/`object` — and the materialization SURVIVES its consumer: only the volume's own delete removes the bytes. `redundancy {copies, minInSync}` is UNIFORM on both kinds (the policy is the contract; each materialization interprets it — mirror legs vs pool size/min_size). No volume field names a kind (architecture §5.2, carried through every remodel).
 *
 */
export const zVolumeWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    cluster: z.uuid(),
    redundancy: z.object({
        copies: z.optional(z.int().gte(1)),
        minInSync: z.optional(z.int().gte(1)),
        deviceClass: z.optional(z.string().min(1).max(32).regex(/^[a-z0-9][a-z0-9_-]{0,31}$/)),
        erasure: z.optional(z.object({
            k: z.int().gte(2).lte(16),
            m: z.int().gte(1).lte(8)
        }))
    }),
    sizeGiB: z.int().gte(1),
    qos: z.optional(z.union([
        z.object({
            iopsLimit: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            mbpsLimit: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ]))
        }),
        z.null()
    ])),
    ioServerPair: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    lustre: z.optional(zVolumeLustre),
    cephfs: z.optional(zVolumeCephfsWritable),
    holder: z.optional(zVolumeHolder)
}));

/**
 * A point-in-time snapshot of a volume (M2b, PROPOSAL §6.4 2026-08-02 — modeled on the TVirt Annex E wave B precedent, thin snapshot with stamp and LVM tags, adapted to md legs; conceptually UNTOUCHED by M2d 2026-08-03 — only the VGs the leg snaps live in are per-vdisk now): ONE thin snapshot of EVERY leg, taken under ONE shared stamp — leg snap LV `<volumeUuid>-l<index>-s<stamp>` on the leg's own VG, LVM tags `tvstor.snapshotOf=<volumeId>` + `tvstor.snapshotId=<snapshotId>` as the on-device identity. Instant and space-efficient by thin-pool construction; with synchronous RAID1 the legs are identical at every acknowledged write, so an unexported volume's snapshot is trivially consistent. EXPORTED VOLUMES (M2c wording updated to targets 2026-08-03, PROPOSAL pending ratification with Luca): a snapshot of a volume with an active target is declared CRASH-CONSISTENT — the point in time is what a consumer would find after a power cut (in-flight, unacked writes may be absent; filesystems must replay their journal). The §6.4 quiesce OPEN QUESTION stays open (dated 2026-08-02): data-plane quiesce at snapshot time — brief ack suspension on the target vs single-leg snapshot behind a write-intent-bitmap sync barrier — flagged to Luca; crash-consistent semantics ship, not silence. SHARE-BACKED VOLUMES (NAS-1, 2026-08-04 — the FILE half of §6.4 answered, `fileshares.yaml`): when the volume is a FileShare's backing volume with its filesystem mounted, the snapshot task FSFREEZES the mount on the authority node first — freeze → snapshot → thaw — so the copy is FS-CONSISTENT (journal quiesced, no replay needed), not merely crash-consistent; the freeze is a brief write stall the file clients ride out (NFS hard-mount retry, SMB reconnection — same experience as a failover blip). The BLOCK case (exported volumes) is untouched — the §6.4 question stays open there: the platform cannot freeze a filesystem a consumer mounts. Config-class row (`volume_snapshots`, replicated); the observed fields compose from the snapshot census the worker sweep writes into `volume_status` (staleness visible, never hidden). Snapshots are SUBRESOURCES, not scoped objects — no scope/tags/notes; `name` is unique PER VOLUME (PROPOSAL 2026-08-02: the round-#105 scope-wide name rule applies to top-level kinds only). Schedules and retention are M4 (dated fence 2026-08-02). CEPH DATAPOOLS (M3, 2026-08-03): the snapshot is an RBD SNAPSHOT of the volume's image — `<volumeUuid>@s<stamp>` (the SAME stamp scheme: one mint, one naming module) — instant and space-efficient by RADOS construction; the census comes from `rbd snap ls` via the `ceph_status` sweep. A snapshot PROTECTED for clones (see `VolumeSource`) carries the dependency edge: its delete is `409 in_use` naming the clones — the ceph-only difference from MultiSCSI's no-chain property, documented, never hidden.
 *
 */
export const zVolumeSnapshotWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    retention: z.optional(zSnapshotRetention)
});

/**
 * An initiator group (architecture §5.1, M2c 2026-08-02): the named consumer identity carrying the initiator IQNs of one client machine. Listing a host on a target (`targets.yaml` — the M2d masking, ratifica D-D 2026-08-03) creates the per-initiator ACLs on that target on EVERY datapool member node (D3 mirrored identity, ratifica 2026-08-02). ACCESS CONTROL (updated M2g, 2026-08-03): the ACL is always by IQN — an IQN is an identity claim, not a secret; since M2g the TARGET may additionally require CHAP (`Target.chap`, an `iscsiChap` keystore reference — per-export, not per-host). Without CHAP, deploy consumers on a trusted data network accordingly.
 *
 */
export const zHostWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    iqns: z.array(z.string().min(1).max(223).regex(/^(iqn\.[0-9]{4}-[0-9]{2}\.[a-z0-9.-]+(:[^ \t]+)?|eui\.[0-9A-Fa-f]{16}|naa\.[0-9A-Fa-f]{16,32})$/)).min(1).max(32)
}));

/**
 * A named consumer export (ratifica D-D, 2026-08-03; `protocol` since M3, 2026-08-03): exposes exactly ONE volume to the hosts of its `hosts` list. `protocol: iscsi` — an iSCSI target serving the volume at LUN 0 on the portals of EVERY datapool member node (D3 mirrored identity), masked to the initiator IQNs of its `hosts` (ACL = the UNION of the hosts' `iqns`). An EMPTY `hosts` list means NO ACLs — and LIO with ACLs-only authentication means NO initiator can log in: an unhosted target exposes nothing (documented rule, 2026-08-03). `protocol: rbd` (M3, ceph-datapool volumes only) — a NATIVE RBD export: one platform-minted, pool/image-capped cephx identity per host, mount info on the readOnly `rbd` block; an empty `hosts` list mints nothing — the same parked-export rule. PROPOSAL (2026-08-03): unlike the M2c mapping edges, the target is a first-class SCOPED resource (scope/tags/notes via ResourceCommon) — it is the object operators name, list and grant on; admission still cross-checks `volume.read` on the volume's scope and `host.read` on each host's scope.
 *
 */
export const zTargetWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    volume: z.uuid(),
    protocol: z.optional(z.enum([
        'iscsi',
        'rbd',
        'lustre'
    ])),
    lustreRole: z.optional(z.nullable(z.enum([
        'mgt',
        'mdt',
        'ost'
    ]))),
    lustreIndex: z.optional(z.union([
        z.int().gte(0),
        z.null()
    ])),
    fsname: z.optional(z.union([
        z.string().max(8).regex(/^[a-zA-Z][a-zA-Z0-9]*$/),
        z.null()
    ])),
    hosts: z.array(z.uuid()),
    chap: z.optional(z.union([
        z.object({
            credential: z.uuid(),
            mutual: z.optional(z.boolean()).default(false)
        }),
        z.null()
    ]))
}));

export const zIoServerPairMemberWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    addresses: z.array(z.string().max(64)).min(1).max(8)
});

/**
 * A pair (2..4 members) of Lustre I/O servers as ONE native rbd consumer of a Ceph cluster: one cephx identity, caps confined to the pools of the volumes exported to it, fence from the SAN side per member. Scoped resource (ResourceCommon); admission cross-checks `cephcluster.read` on the cluster.
 *
 */
export const zIoServerPairWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    cluster: z.uuid(),
    members: z.array(zIoServerPairMemberWritable).min(2).max(4),
    lustre: z.optional(z.union([
        z.object({
            filesystem: z.optional(z.string().min(1).max(8))
        }),
        z.null()
    ])),
    metadataDeviceClass: z.optional(z.string().min(1).max(32).regex(/^[a-z0-9][a-z0-9_-]{0,31}$/)).default('nvme')
}));

/**
 * A filesystem already mounted on one node, catalogued by that node's worker (full scans by mtime) into the node's catalog database. Scoped resource (ResourceCommon). `node` and `mountPath` are IMMUTABLE after creation (`422 immutable_field`): the mount belongs to the node.
 *
 */
export const zCatalogSourceWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    node: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    mountPath: z.string().min(1).max(4096),
    subPath: z.string().max(1024).default(''),
    excludes: z.array(z.string().min(1).max(1024)).max(64).default([]),
    catalog: z.optional(zCatalogSourcePolicy)
}));

/**
 * A shared disk shelf declared by an operator (R25 JBOF-DC-1, design §2): the heads that see its disks and, per disk, the one owner among them. Scoped resource (ResourceCommon). The disks and their owners are readOnly on the resource — declared as wwids at creation, moved only by the ownership verbs.
 *
 */
export const zEnclosureWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    heads: z.array(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)).min(2),
    reservePercent: z.optional(z.int().gte(0).lte(50)).default(15)
}));

/**
 * A FILE SHARE — the NAS resource (NAS-1, 2026-08-04): a named filesystem exported over one or more of NFS, SMB and SFTP. ENGINE-NEUTRAL like volumes: the share lives on a DATAPOOL bound to an engine and never names one (architecture §5.2). On a `multiscsi` datapool the share OWNS a backing mirror volume (`share-<share8hex>`) mounted and served by that volume's AUTHORITY node — single-mounter gateway, active/passive, service IP follows the authority. On a `ceph` datapool the share is a CephFS SUBVOLUME in its volume's group of the cluster filesystem `tvstor-fs-<cluster8hex>` (or of the volume's own filesystem when `Volume.cephfs.dedicated` — NAS-MDS-1 B, 2026-10-29), exported from every member node over a CephFS MOUNT (D7, 2026-08-15 — no `vfs_ceph`), AND THE TOPOLOGY IS NOT THE SAME FOR EVERY PROTOCOL: NFS (NFS-Ganesha FSAL_CEPH, or the kernel NFS server when `cluster.nfsEngine.ceph = knfsd` — ENEA-A-R2), SFTP and FTP are ACTIVE/ACTIVE on every member, while SMB is ACTIVE/STANDBY — exactly one gateway serves it, and `smbOwner` names it (#152, 2026-08-15). The reason is measured, not stylistic: NFS byte-range locks reach the CephFS MDS and are therefore refereed across gateways, Samba's do not (#153, 2026-08-17 — `nfs` and `smbOwner` below carry the full statement).
 *
 */
export const zFileShareWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    volume: z.uuid(),
    sizeGiB: z.int().gte(1),
    quota: z.optional(z.union([
        z.object({
            scope: z.enum([
                'share',
                'subtree'
            ]),
            limitBytes: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            directories: z.optional(z.union([
                z.array(z.object({
                    path: z.string().min(1).max(512),
                    limitBytes: z.int().gte(1048576)
                })).max(64),
                z.null()
            ]))
        }),
        z.null()
    ])),
    protocols: z.array(z.enum([
        'nfs',
        'smb',
        'sftp',
        'ftp',
        'tftp',
        'webdav'
    ])).min(0),
    identity: z.optional(z.union([
        z.object({
            mode: z.enum([
                'local',
                'directory'
            ]),
            realmId: z.optional(z.union([
                z.uuid(),
                z.null()
            ]))
        }),
        z.null()
    ])),
    aclModel: z.optional(z.enum([
        'posix',
        'nt'
    ])),
    source: z.optional(z.union([
        z.object({
            kind: z.optional(z.enum([
                'shareSnapshot'
            ])),
            share: z.optional(z.uuid()),
            snapshot: z.optional(z.uuid())
        }),
        z.null()
    ])),
    previousVersions: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            intervalMinutes: z.optional(z.union([
                z.int().gte(5).lte(10080),
                z.null()
            ])),
            keep: z.optional(z.union([
                z.int().gte(1).lte(64),
                z.null()
            ]))
        }),
        z.null()
    ])),
    catalog: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            everyMinutes: z.optional(z.union([
                z.int().gte(15).lte(10080),
                z.null()
            ])),
            mode: z.optional(z.nullable(z.enum([
                'auto',
                'full',
                'incremental'
            ]))),
            fullEvery: z.optional(z.union([
                z.int().gte(1).lte(365),
                z.null()
            ])),
            maxEntries: z.optional(z.union([
                z.int().gte(1000).lte(1000000000),
                z.null()
            ]))
        }),
        z.null()
    ])),
    audit: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            operations: z.optional(z.union([
                z.array(z.enum([
                    'connect',
                    'mount',
                    'auth',
                    'open',
                    'read',
                    'write',
                    'create',
                    'delete',
                    'rename',
                    'mkdir',
                    'rmdir',
                    'setattr',
                    'setacl',
                    'list',
                    'lock'
                ])).max(32),
                z.null()
            ]))
        }),
        z.null()
    ])),
    qos: z.optional(z.union([
        z.object({
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ])),
    nfs: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            allow: z.array(z.string().min(1).max(64)).min(1),
            squash: z.optional(z.enum([
                'root',
                'none'
            ])),
            sec: z.optional(z.enum([
                'sys',
                'krb5',
                'krb5i',
                'krb5p'
            ]))
        }),
        z.null()
    ])),
    smb: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.optional(z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            }))),
            principals: z.optional(z.array(z.string().min(3).max(320))),
            homes: z.optional(z.boolean()),
            encryption: z.optional(z.enum([
                'required',
                'desired',
                'off'
            ]))
        }),
        z.null()
    ])),
    sftp: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            })).min(1)
        }),
        z.null()
    ])),
    ftp: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            })).min(1),
            tls: z.optional(z.enum([
                'explicit',
                'none'
            ])),
            passivePortRange: z.object({
                from: z.int().gte(1024).lte(65535),
                to: z.int().gte(1024).lte(65535)
            })
        }),
        z.null()
    ])),
    tftp: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            subpath: z.optional(z.string().min(1).max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/))
        }),
        z.null()
    ])),
    webdav: z.optional(z.union([
        z.object({
            readOnly: z.optional(z.boolean()),
            users: z.array(z.object({
                credential: z.uuid(),
                readOnly: z.optional(z.boolean())
            })).min(1),
            port: z.optional(z.int().gte(1024).lte(65535)),
            tls: z.optional(z.enum([
                'explicit',
                'none'
            ]))
        }),
        z.null()
    ])),
    pxe: z.optional(z.union([
        z.object({
            defaultEntry: z.optional(z.string()),
            timeoutSeconds: z.optional(z.int().gte(0).lte(3600)),
            entries: z.array(z.object({
                name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
                label: z.optional(z.string().max(128)),
                kernel: z.string().min(1).max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                initrd: z.optional(z.union([
                    z.string().max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                    z.null()
                ])),
                append: z.optional(z.union([
                    z.string().max(1024),
                    z.null()
                ]))
            })).min(1)
        }),
        z.null()
    ])),
    serviceAddress: z.optional(z.union([
        z.string().max(64),
        z.null()
    ]))
}));

/**
 * ENEA-A-R5 (2026-10-23): a SNAPSHOT OF A CEPH SHARE as a resource — a snapshot of the share's CephFS SUBVOLUME (`ceph fs subvolume snapshot create`) with an operator-given name. Config-class row (`share_snapshots`, replicated); the phase follows the task (`ceph.fileshare.snapshot` → ready, failed → error; delete → row gone). Visible to clients through the kernel CephFS `.snap` directory of the share root as `_tvs-<name>_<inode>` (measured on kernel mounts: knfsd, SMB, SFTP, WebDAV); through Ganesha FSAL_CEPH the `.snap` traversal is MEASURED on the #148 bench and stated in docs/enea-a-r5-2026-10-23.md. MULTISCSI shares have no snapshot resource of their own: their snapshot IS the `VolumeSnapshot` of the backing volume (fsfreeze → snapshot → thaw) — `POST …/snapshots` on a multiscsi share answers `422` naming that route. The previous-versions cadence of #177 (`@GMT-…` names) is NOT a resource: never listed here, never created or deleted here (the `tvs-` and `@GMT-` namespaces are disjoint by construction).
 *
 */
export const zFileShareSnapshotWritable = z.object({
    name: z.string().regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    retention: z.optional(zSnapshotRetention)
});

/**
 * An OPTIONAL platform-managed DHCPv4 service (NAS-3, 2026-08-05 — PROPOSAL, pending ratification): one supervised Kea instance serving ONE subnet, anchored to a TFTP-enabled multiscsi file share — the daemon runs on the share's authority node bound to its `serviceAddress`, follows it on failover, and (when `bootOptions` is present) hands PXE clients next-server/filename pointing at that same address. Sites with an existing DHCP server never create one of these: they configure options 66/67 on their own server instead (documented).
 *
 */
export const zDhcpServiceWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    fileShare: z.uuid(),
    subnet: z.string().min(9).max(43),
    pool: z.object({
        from: z.string().max(15),
        to: z.string().max(15)
    }),
    leaseSeconds: z.optional(z.int().gte(60).lte(604800)),
    routers: z.optional(z.array(z.string().max(15)).max(8)),
    dnsServers: z.optional(z.array(z.string().max(15)).max(8)),
    domainName: z.optional(z.union([
        z.string().max(253),
        z.null()
    ])),
    reservations: z.optional(z.array(z.object({
        mac: z.string().regex(/^[0-9a-f]{2}(:[0-9a-f]{2}){5}$/),
        address: z.string().max(15),
        hostname: z.optional(z.union([
            z.string().max(63),
            z.null()
        ]))
    })).max(1024)),
    bootOptions: z.optional(z.union([
        z.object({
            nextServer: z.optional(z.union([
                z.string().max(15),
                z.null()
            ])),
            filename: z.optional(z.union([
                z.string().max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                z.null()
            ])),
            efiFilename: z.optional(z.union([
                z.string().max(255).regex(/^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/),
                z.null()
            ]))
        }),
        z.null()
    ])),
    enabled: z.optional(z.boolean())
}));

/**
 * A DIRECTORY REALM (NAS-2 Fase 1a, 2026-08-15): the cluster-level declaration of an Active Directory domain (`type: ad`) or a plain LDAP directory (`type: ldap`) the file services take identities from. N per cluster (ratifica B3, 2026-08-12). The realm's `name` is the QUALIFYING half of every `REALM\user` on this cluster and is also the key of its uid range in the cluster allocator — one to one (design §3.2). Every mutation is sudo-gated. In Fase 1a a realm is a DECLARATION with a checkable preflight: the join, the daemons and the rendered domain configuration are the rest of Fase 1, ramo `ad`, and landed with #157 (2026-08-16) on the node side, not on this resource.
 *
 */
export const zDirectoryRealmWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    type: z.enum([
        'ad',
        'ldap'
    ]),
    credential: z.uuid(),
    servers: z.optional(z.array(z.string().min(1).max(261)).max(16)),
    ad: z.optional(z.union([
        z.object({
            kerberosRealm: z.string().min(1).max(255),
            netbiosDomain: z.string().min(1).max(15),
            machineAccountOu: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ]))
        }),
        z.null()
    ])),
    ldap: z.optional(z.union([
        z.object({
            baseDn: z.string().min(1).max(1024),
            tls: z.enum([
                'ldaps',
                'startTls'
            ]),
            userFilter: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ])),
            groupFilter: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ])),
            kerberos: z.optional(z.union([
                z.object({
                    realm: z.string().min(1).max(255),
                    kdcs: z.array(z.string().min(1).max(261)).min(1).max(8),
                    adminServer: z.optional(z.union([
                        z.string().max(261),
                        z.null()
                    ])),
                    nfsHostname: z.string().min(1).max(253),
                    keytabCredential: z.optional(z.union([
                        z.uuid(),
                        z.null()
                    ]))
                }),
                z.null()
            ]))
        }),
        z.null()
    ])),
    uidRange: z.optional(z.union([
        z.object({
            base: z.int().gte(2000000).lte(999999999),
            span: z.int().gte(1).lte(998000000)
        }),
        z.null()
    ])),
    domainSid: z.optional(z.union([
        z.string().max(128),
        z.null()
    ])),
    identityKeyAttribute: z.optional(z.string().min(1).max(63)),
    offlineWindowSeconds: z.optional(z.int().gte(0).lte(2592000)),
    adminRoles: z.optional(z.union([
        z.array(z.object({
            group: z.string().min(1).max(1024),
            role: z.string().min(1).max(63),
            scope: z.optional(z.union([
                z.string().max(1024),
                z.null()
            ]))
        })).max(64),
        z.null()
    ]))
}));

/**
 * An OBJECT STORE — the S3 endpoint resource (OBJ-1, 2026-08-04): ENGINE-NEUTRAL like volumes and file shares — the store lives on a DATAPOOL bound to an engine and never names one (architecture §5.2). On a `ceph` datapool the store is an RGW zone served ACTIVE/ACTIVE by one supervised `radosgw` per member node, objects in the store's own RADOS pools — scale-out, no failover machinery of its own. On a `multiscsi` datapool (OBJ-MS, 2026-08-05 — PROPOSAL pending ratification) the store OWNS a backing mirror volume (`objs-<store8hex>`) mounted and served by that volume's AUTHORITY node through ONE supervised `versitygw` gateway — single-mounter, active/passive, floating `serviceAddress` following the authority (the FileShare twin); buckets are directories and current objects plain files on the mount (open formats, declared). BOTH engines serve the SAME ratified TVStor S3 Profile v1 (header table) — the engine changes the machinery, never the promise.
 *
 */
export const zObjectStoreWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    volume: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    share: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    fileOwner: z.optional(z.union([
        z.object({
            uid: z.int().gte(0).lte(4294967294),
            gid: z.int().gte(0).lte(4294967294)
        }),
        z.null()
    ])),
    port: z.int().gte(1).lte(65535),
    tlsCertificate: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    serviceAddress: z.optional(z.union([
        z.string().max(64),
        z.null()
    ])),
    audit: z.optional(z.union([
        z.object({
            enabled: z.boolean(),
            operations: z.optional(z.union([
                z.array(z.enum([
                    'auth',
                    'read',
                    'write',
                    'create',
                    'delete',
                    'list',
                    'head',
                    'copy',
                    'setacl'
                ])).max(32),
                z.null()
            ]))
        }),
        z.null()
    ])),
    qos: z.optional(z.union([
        z.object({
            readOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            writeOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ]))
}));

/**
 * A BUCKET — the S3 container (OBJ-1, 2026-08-04; the name is free and unambiguous post-RENAME-1, ratifica §3: the physical pool is the datapool). Buckets are SUBRESOURCES of their ObjectStore, not scoped objects — no scope/tags/notes; `name` is unique PER STORE (it is the S3 wire namespace of the zone). Config-class row (`object_buckets`, replicated); the observed usage composes from the usage census in `objectstore_status` (ceph: `radosgw-admin bucket stats`; multiscsi: the gateway/mount census — OBJ-MS 2026-08-05; worker sweep — staleness visible, never hidden). On a multiscsi store the bucket IS a directory on the backing mount (versitygw POSIX backend) — the open-formats honesty of the engine, declared. Since OBJ-MS (2026-08-05) the bucket carries the TVStor S3 Profile v1 per-bucket surface: `versioning` and `objectLock` — IDENTICAL on both engines (the ratified promise, header table).
 *
 */
export const zBucketWritable = z.object({
    name: z.string().min(3).max(63).regex(/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/),
    owner: z.uuid(),
    quotaBytes: z.optional(z.union([
        z.int().gte(1),
        z.null()
    ])),
    versioning: z.optional(z.nullable(z.enum([
        'enabled',
        'suspended'
    ]))),
    qos: z.optional(z.union([
        z.object({
            readOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            writeOps: z.optional(z.union([
                z.int().gte(1),
                z.null()
            ])),
            readBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ])),
            writeBytesPerSec: z.optional(z.union([
                z.int().gte(1048576),
                z.null()
            ]))
        }),
        z.null()
    ])),
    objectLock: z.optional(z.union([
        z.object({
            defaultRetention: z.optional(z.union([
                z.object({
                    mode: z.enum([
                        'governance',
                        'compliance'
                    ]),
                    days: z.optional(z.union([
                        z.int().gte(1).lte(36500),
                        z.null()
                    ])),
                    years: z.optional(z.union([
                        z.int().gte(1).lte(100),
                        z.null()
                    ]))
                }),
                z.null()
            ]))
        }),
        z.null()
    ]))
});

/**
 * An S3 ACCOUNT — the DATA-PLANE consumer identity of an ObjectStore (OBJ-1, 2026-08-04): never an operator, never a session (the two-plane doctrine — the NAS `fileUser` twin). The PLATFORM MINTS the key pair at creation (keystore kind `s3Account`, provenance-marked — `credentials.yaml`), propagates it to the engine — ceph: `radosgw-admin user create` with the minted uid (PROPOSAL 2026-08-04: `tvstor-<account8hex>`); multiscsi (OBJ-MS 2026-08-05 — PROPOSAL): the account converges into versitygw's internal file-backed IAM on the authority, rendered by the worker from the keystore replica (never argv, never logs — the secretfile discipline; NOTE the engine asymmetry, declared: versitygw's identity IS the access key id, there is no separate uid, spike §3) — and renders the secret EXACTLY ONCE in the create response — never again (M2g write-only discipline; the M5a join-token precedent). Accounts are SUBRESOURCES of their store — no scope/tags/notes; `name` unique per store. Config-class row (`s3_accounts`, replicated).
 *
 */
export const zS3AccountWritable = z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/)
});

/**
 * A named keystore entry (M2g, 2026-08-03). `kind` is IMMUTABLE after creation; exactly ONE per-kind block — matching `kind` — must be present (`422 validation` otherwise, on create and on PATCH alike). The block's PUBLIC fields render back on every read; its SECRET fields are WRITE-ONLY forever — never rendered, never logged, never on argv (the never-rendered discipline, stated 2026-08-03) — required at creation and replaceable ONLY through `actions/rotate` (a PATCH carrying a secret field is `422 validation` — TVStor deviation from TVirt's PATCH-rotation, dated 2026-08-03: secret material is sudo-gated, PATCH is not). The stored secret is envelope-encrypted on the cluster store (AES-256-GCM, per-secret random IV, AAD = credential id + kind; DEK wrapped by the node-local 0600 master key file — see the file header).
 *
 */
export const zCredentialWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'iscsiChap',
        'bmcAccount',
        'cephx',
        'luksKey',
        's3Account',
        'replicationToken',
        'fileUser',
        'smtpAccount',
        'webhookSecret',
        'tlsCertificate',
        'wireguardKey',
        'machineAccount',
        'directoryAccount',
        'cephfsMirrorBootstrap'
    ]),
    iscsiChap: z.optional(z.object({
        username: z.string().min(1).max(255),
        password: z.optional(z.string().min(12).max(255)),
        mutualUsername: z.optional(z.string().min(1).max(255)),
        mutualPassword: z.optional(z.string().min(12).max(255))
    })),
    bmcAccount: z.optional(z.object({
        username: z.string().min(1).max(255),
        certificateFingerprint: z.optional(z.string().regex(/^[0-9A-Fa-f]{2}(:?[0-9A-Fa-f]{2}){31}$/)),
        password: z.optional(z.string().min(1).max(255))
    })),
    cephx: z.optional(z.object({
        entity: z.string().min(1).max(255),
        key: z.optional(z.string().min(1).max(4096))
    })),
    luksKey: z.optional(z.object({
        passphrase: z.optional(z.string().min(12).max(512))
    })),
    s3Account: z.optional(z.object({
        accessKeyId: z.string().min(1).max(255),
        secretAccessKey: z.optional(z.string().min(1).max(512))
    })),
    replicationToken: z.optional(z.object({
        clusterName: z.string().min(1).max(63),
        token: z.optional(z.string().min(24).max(512).regex(/^tvsl_/))
    })),
    cephfsMirrorBootstrap: z.optional(z.object({
        peerCluster: z.string(),
        sourceFileSystem: z.uuid(),
        remoteFileSystem: z.string(),
        clientName: z.string()
    })),
    fileUser: z.optional(z.object({
        username: z.string().min(1).max(63).regex(/^[a-z_][a-z0-9._-]{0,62}$/),
        password: z.optional(z.string().min(8).max(255)),
        sshPublicKeys: z.optional(z.array(z.string().min(1).max(4096)))
    })),
    smtpAccount: z.optional(z.object({
        username: z.string().min(1).max(255),
        password: z.optional(z.string().min(1).max(512))
    })),
    webhookSecret: z.optional(z.object({
        secret: z.string().min(16).max(512)
    })),
    tlsCertificate: z.optional(z.object({
        certificatePem: z.string().min(1).max(65536),
        privateKeyPem: z.optional(z.string().min(1).max(65536))
    })),
    wireguardKey: z.optional(z.object({
        privateKey: z.optional(z.string().length(44))
    })),
    machineAccount: z.optional(z.object({
        realm: z.string().min(1).max(255),
        netbiosDomain: z.optional(z.string().min(1).max(15)),
        accountName: z.string().min(1).max(15),
        secretsTdb: z.optional(z.string().min(1).max(4194304)),
        keytab: z.optional(z.string().min(1).max(131072))
    })),
    directoryAccount: z.optional(z.object({
        username: z.string().min(1).max(1024),
        password: z.optional(z.string().min(1).max(1024))
    }))
}));

/**
 * Body of `actions/rotate` (M2g, 2026-08-03) — SECRET FIELDS ONLY, in the per-kind block matching the credential's kind (`422 validation` on a mismatched or missing block): rotate replaces the secret material, never the public fields (`username`/`entity` changes are ordinary PATCH). Mirrors the creation blocks' secret halves.
 *
 */
export const zCredentialRotateRequestWritable = z.object({
    iscsiChap: z.optional(z.object({
        password: z.string().min(12).max(255),
        mutualPassword: z.optional(z.string().min(12).max(255))
    })),
    bmcAccount: z.optional(z.object({
        password: z.string().min(1).max(255)
    })),
    cephx: z.optional(z.object({
        key: z.string().min(1).max(4096)
    })),
    luksKey: z.optional(z.object({
        passphrase: z.optional(z.string().min(12).max(512))
    })),
    s3Account: z.optional(z.object({
        secretAccessKey: z.string().min(1).max(512)
    })),
    replicationToken: z.optional(z.object({
        token: z.string().min(24).max(512).regex(/^tvsl_/)
    })),
    fileUser: z.optional(z.object({
        password: z.string().min(8).max(255)
    })),
    smtpAccount: z.optional(z.object({
        password: z.string().min(1).max(512)
    })),
    webhookSecret: z.optional(z.object({
        secret: z.string().min(16).max(512)
    })),
    tlsCertificate: z.optional(z.object({
        certificatePem: z.string().min(1).max(65536),
        privateKeyPem: z.string().min(1).max(65536)
    })),
    wireguardKey: z.optional(z.object({
        privateKey: z.optional(z.string().length(44))
    })),
    machineAccount: z.optional(z.object({
        secretsTdb: z.optional(z.string().min(1).max(4194304)),
        keytab: z.optional(z.string().min(1).max(131072))
    })),
    directoryAccount: z.optional(z.object({
        password: z.string().min(1).max(1024)
    }))
});

/**
 * A backup DESTINATION (M4b, 2026-08-04): where backup objects live and under which immutability policy. Pure config-class row — synchronous CRUD, no task; reachability problems surface on the backup tasks that use the target (a dated seam 2026-08-04: a `verify` action probing the destination may join later). `kind` is IMMUTABLE after creation (the credentials.yaml rationale, adopted: retyping is a NEW target); exactly the per-kind block matching `kind` must be present (`422 validation` otherwise, on create and PATCH alike).
 *
 */
export const zBackupTargetWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'local',
        's3',
        'tape'
    ]),
    local: z.optional(z.object({
        path: z.string().min(1).max(4096).regex(/^\//)
    })),
    s3: z.optional(z.object({
        endpoint: z.url().max(2048),
        bucket: z.string().min(3).max(63),
        region: z.optional(z.string().max(64)),
        prefix: z.optional(z.string().max(512)),
        forcePathStyle: z.optional(z.boolean()).default(true),
        credential: z.uuid()
    })),
    tape: z.optional(z.object({
        node: z.string().min(1),
        drive: z.string().min(1),
        changer: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        worm: z.optional(z.enum([
            'required',
            'any'
        ])),
        blockSize: z.optional(z.int().gte(512).lte(16777216))
    })),
    immutability: z.object({
        mode: z.enum([
            'off',
            'governance',
            'compliance'
        ]),
        defaultRetentionDays: z.optional(z.int().gte(1).lte(36500))
    }),
    timestamp: z.optional(z.union([
        z.object({
            url: z.url().max(2048),
            anchorPem: z.string().min(1).max(262144),
            endpointTlsPin: z.optional(z.string().regex(/^[0-9a-f]{64}$/)),
            policyOid: z.optional(z.string().max(128).regex(/^[0-9]+(\.[0-9]+)+$/)),
            hashAlg: z.optional(z.enum([
                'sha256',
                'sha384',
                'sha512'
            ])),
            verifyEvery: z.optional(z.int().gte(0).lte(36500)).default(30),
            qualified: z.optional(z.boolean()).default(false)
        }),
        z.null()
    ])),
    scrub: z.optional(z.union([
        z.object({
            everyDays: z.int().gte(1).lte(3650),
            windowHours: z.int().gte(1).lte(168)
        }),
        z.null()
    ]))
}));

/**
 * A point-in-time copy of a volume on a BackupTarget (M4b, 2026-08-04), created FROM A SNAPSHOT — never from the live head: the snapshot IS the point in time, and its crash-consistent semantics (§6.4 open question, `VolumeSnapshot`) carry over unchanged. NO `name` field (PROPOSAL 2026-08-04, deviation from the round-#105 named-kinds pattern): backups are machine-born rows — potentially thousands, schedule-born later — addressed by id and found by `?filter=` on `volume`/`target`/`phase`. The row OUTLIVES ITS VOLUME: deleting the volume leaves its backups restorable (`restore` with `newVolumeName`); it outlives its SNAPSHOT too — the snapshot reference is provenance, and a deleted base snapshot merely forces the next backup of that volume to be `full` (no hard pin, stated 2026-08-04). `retainUntil` and `immutabilityMode` are FROZEN COPIES of the target's policy at creation — the brochure's binding rule lives on this row, not on the target.
 *
 */
export const zBackupWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    volume: z.uuid(),
    snapshot: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    target: z.uuid(),
    kind: z.optional(z.enum([
        'full',
        'incremental'
    ])),
    scrub: z.optional(zBackupScrub)
}));

export const zBackupCopyWritable = z.object({
    scrub: zBackupScrub,
    notes: z.optional(z.string())
});

/**
 * An OUTBOUND asynchronous replication relationship to a second TVStor cluster (M4b, 2026-08-04). Config-class row; the per-volume watermarks (`volumeStates[]`) are state-class, composed onto the render.
 *
 */
export const zReplicationLinkWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    direction: z.optional(z.enum([
        'outbound'
    ])),
    kind: z.optional(z.enum([
        'block',
        'file'
    ])),
    remote: z.object({
        url: z.url().max(2048),
        clusterName: z.string().min(1).max(63),
        credential: z.uuid(),
        certFingerprint: z.optional(z.union([
            z.string().max(95),
            z.null()
        ]))
    }),
    volumes: z.array(z.uuid()).max(256),
    shares: z.optional(z.array(z.uuid()).max(256)).default([]),
    mirror: z.optional(z.union([
        z.object({
            retainSnapshots: z.optional(z.int().gte(1).lte(1000)).default(24)
        }),
        z.null()
    ])),
    schedule: z.optional(z.union([
        z.object({
            mode: z.optional(z.enum([
                'scheduled',
                'continuous'
            ])),
            everyMinutes: z.optional(z.int().gte(1).lte(10080))
        }),
        z.null()
    ])),
    enabled: z.optional(z.boolean()).default(true)
}));

/**
 * The registration, on the SERVING cluster (site B), of one remote sync volume's identity (#39 REP-SYNC-MS, 2026-08-22). Config-class row; `phase` is its whole lifecycle (`serving` → `promoted`, terminal).
 *
 */
export const zSyncLinkWritable = zResourceCommonWritable.and(z.object({
    name: z.string(),
    sourceCluster: z.string(),
    sourceVolume: z.uuid(),
    volumeName: z.string(),
    sizeGiB: z.int().gte(1),
    volume: z.optional(z.union([
        z.uuid(),
        z.null()
    ])),
    witnessEndpoints: z.optional(z.array(z.string())),
    phase: z.enum([
        'serving',
        'promoted'
    ])
}));

/**
 * A threshold rule over the fixed metric catalog (M5b, 2026-08-04) — config-class, replicated. The backend sweeper evaluates every enabled rule against the latest samples of every matching target: a breach sustained for `forMinutes` FIRES one Alert per (rule, target) pair — the dedup key: a still-burning breach never births a second row — and the value crossing back past `threshold ∓ clearMargin` RESOLVES it (hysteresis). PROPOSAL (2026-08-04): the whole rule shape is this round's proposal on the scope's sketch, pending ratification.
 *
 */
export const zAlertRuleWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    metric: z.enum([
        'node.cpuLoad1',
        'node.memUsedPercent',
        'node.fsUsedPercent',
        'node.netRxBytesPerSec',
        'node.netTxBytesPerSec',
        'node.netErrors',
        'node.netDrops',
        'node.replicaTunnelPeersDown',
        'node.configReplicaAgeSeconds',
        'node.crlAgeSeconds',
        'node.machineAccountDivergedSeconds',
        'node.daemonRestarts',
        'node.cephDaemonsMute',
        'node.auditWriteFailures',
        'node.replicationSpoolPercent',
        'node.sanPathsDown',
        'node.diskWearMaxPercent',
        'node.diskMediaErrors',
        'node.maintenanceHours',
        'cephcluster.capacityGiB',
        'cephcluster.usedGiB',
        'cephcluster.usedPercent',
        'cephcluster.daysToFull',
        'cephcluster.osdsDown',
        'cephcluster.pgsInactive',
        'multiscsicluster.capacityGiB',
        'multiscsicluster.usedGiB',
        'multiscsicluster.usedPercent',
        'multiscsicluster.daysToFull',
        'multiscsicluster.missingPvs',
        'multiscsicluster.scrubMismatches',
        'multiscsicluster.scrubAgeDays',
        'volume.health',
        'volume.backupAgeHours',
        'volume.replicaLagSeconds',
        'volume.syncDegraded',
        'volume.resyncPercent',
        'volume.readBytesPerSec',
        'volume.writeBytesPerSec',
        'volume.readIops',
        'volume.writeIops',
        'volume.ioAwaitMs',
        'share.health',
        'share.clients',
        'share.fsUsedPercent',
        'share.daysToFull',
        'share.daemonsNotServing',
        'share.daemonRestarts',
        'share.smbLeaseUnrenewedSeconds',
        'share.opsPerSec',
        'share.bytesPerSec',
        'share.deleteOps',
        'share.authFailures',
        'share.accessDenied',
        'store.health',
        'store.lagSeconds',
        'objectstore.health',
        'objectstore.requestsPerSec',
        'objectstore.bytesPerSec',
        'objectstore.deleteOps',
        'objectstore.authFailures',
        'objectstore.accessDenied',
        'backuptarget.timestampUnreachable',
        'backuptarget.timestampRefused',
        'backuptarget.timestampUnverifiable',
        'backuptarget.timestampInvalid',
        'backuptarget.timestampTampered',
        'backuptarget.timestampAgeDays'
    ]),
    targetKind: z.enum([
        'node',
        'cephcluster',
        'multiscsicluster',
        'volume',
        'share',
        'store',
        'objectstore',
        'backuptarget'
    ]),
    targetSelector: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    operator: z.enum([
        'gt',
        'lt',
        'anomaly'
    ]),
    threshold: z.number(),
    forMinutes: z.optional(z.int().gte(0).lte(1440)).default(0),
    severity: z.enum([
        'info',
        'warning',
        'critical'
    ]),
    clearMargin: z.optional(z.number().gte(0)).default(0),
    enabled: z.optional(z.boolean()).default(true)
}));

/**
 * A notification DESTINATION (M5b, 2026-08-04) — config-class, replicated. On every alert transition (fire AND resolve) the sweeper births one `alert.deliver` task per ENABLED sink; the task engine's retry/backoff and log are the delivery record — a sink outage delays notifications, never evaluation. `kind` is IMMUTABLE after creation (the credentials.yaml rationale: retyping is a NEW sink); exactly the per-kind block matching `kind` must be present (`422 validation`, on create and PATCH alike). Mutations are SUDO-GATED (PROPOSAL 2026-08-04): a sink names WHERE alert traffic goes — re-pointing it is an exfiltration primitive, the danger-ladder argument without a secret on the row.
 *
 */
export const zAlertSinkWritable = zResourceCommonWritable.and(zRenderedResourceWritable).and(z.object({
    name: z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/),
    kind: z.enum([
        'email',
        'webhook',
        'syslog'
    ]),
    enabled: z.optional(z.boolean()).default(true),
    email: z.optional(z.object({
        host: z.string().min(1).max(255),
        port: z.optional(z.int().gte(1).lte(65535)).default(587),
        tls: z.optional(z.enum([
            'starttls',
            'implicit',
            'none'
        ])),
        from: z.string().min(3).max(255),
        to: z.array(z.string().min(3).max(255)).min(1).max(32),
        credential: z.optional(z.union([
            z.uuid(),
            z.null()
        ]))
    })),
    webhook: z.optional(z.object({
        url: z.url().max(2048).regex(/^https:\/\//),
        format: z.optional(z.enum([
            'alert',
            'callhome'
        ])),
        secret: z.optional(z.union([
            z.uuid(),
            z.null()
        ]))
    })),
    syslog: z.optional(z.object({
        host: z.string().min(1).max(255),
        port: z.optional(z.int().gte(1).lte(65535)).default(6514),
        transport: z.optional(z.enum([
            'tls',
            'tcp'
        ])),
        tls: z.optional(z.object({
            caPem: z.optional(z.union([
                z.string().max(65536),
                z.null()
            ])),
            fingerprintSha256: z.optional(z.union([
                z.string(),
                z.null()
            ])),
            clientCertificate: z.optional(z.union([
                z.uuid(),
                z.null()
            ])),
            serverName: z.optional(z.union([
                z.string().max(255),
                z.null()
            ]))
        })),
        facility: z.optional(z.enum([
            'kern',
            'user',
            'mail',
            'daemon',
            'auth',
            'syslog',
            'lpr',
            'news',
            'uucp',
            'cron',
            'authpriv',
            'ftp',
            'local0',
            'local1',
            'local2',
            'local3',
            'local4',
            'local5',
            'local6',
            'local7'
        ])),
        appName: z.optional(z.string().max(48)).default('tvstor'),
        forward: z.optional(z.object({
            alerts: z.optional(z.boolean()).default(true),
            journal: z.optional(z.boolean()).default(true),
            daemonLogs: z.optional(z.boolean()).default(true)
        }))
    }))
}));

export const zAlertListWritable = z.object({
    items: z.array(z.unknown()),
    nextCursor: z.optional(z.string())
});

/**
 * The atom of execution — a single idempotent operation with an owner, an executor, a structured log, progress and a terminal outcome. Flat, server-created and entirely read-only. A task may own CHILDREN (TVirt round #128 model): the children are its ordered steps, each gated on the success of the previous, and the parent itself executes no code — its phase derives from the children (running while one runs, failed when one fails, cancelled when the family's cancellation was requested, succeeded when all closed).
 *
 */
export const zTaskWritable = z.object({
    id: z.string(),
    type: z.string(),
    target: z.optional(zTaskTarget),
    phase: z.enum([
        'queued',
        'running',
        'succeeded',
        'failed',
        'cancelled',
        'aborted'
    ]),
    progressPercent: z.optional(z.number().gte(0).lte(100)),
    message: z.optional(z.string()),
    startedAt: z.optional(z.iso.datetime()),
    finishedAt: z.optional(z.iso.datetime()),
    error: z.optional(zProblem),
    node: z.optional(z.string())
});

/**
 * Page size.
 */
export const zLimit = z.int().gte(1).lte(500).default(100);

/**
 * Opaque pagination cursor from a previous response.
 */
export const zCursor = z.string();

/**
 * Scope selector: `/cluster1/tenant1` selects exactly that scope, `/cluster1/tenant1*` selects the scope and every sub-scope. A Scope `id` is accepted in place of the string, with the same optional `*` suffix. SINGLE TREE (TVirt cluster-first heritage, adopted 2026-08-02): there is one scope tree per cluster and one authority — the default `*` spans the whole tree everywhere.
 *
 */
export const zScopeFilter = z.string().default('/*');

/**
 * Uniform, repeatable predicate `<field>:<op>:<value>`. `<field>` is the dotted JSON path of a resource field (`enabled`, `tag.team`). Operators — negation is the `!` prefix — are gated by the field's type: `eq`/`!eq` and `in`/`!in` on any scalar; `gt`/`gte`/`lt`/`lte` on numbers and timestamps; `contains`/`!contains` on free-text strings (substring) and arrays (membership); `startsWith`/`endsWith` and their `!` forms on free-text strings; `exists` only on optional fields such as tag names, with a JSON boolean value (`filter=tag.team:exists:true` / `:exists:false` — no `!` form). Identity-like strings — `id`/UUID references and enum fields such as `phase` — accept only eq/!eq/in/!in (e.g. `phase:in:["running","queued"]`). TAG PREDICATES (`tag.<name>:<op>:<value>`, TVirt Annex T model) match the resource's assignments: `<name>` is the tag's BARE name when it is unambiguous among the tags visible to the caller, and the qualified `/scope/name` form otherwise — an ambiguous bare name is `400 invalid_argument` listing the candidates. `!eq` matches resources that carry a different value AND those that carry the tag not at all. `<value>` is a JSON value (strings quoted, arrays for in/!in — percent-encode in real URLs). `eq`/`!eq` also accept JSON `null` for absence (`parent:eq:null` selects top-level tasks): `eq null` matches where the field is absent, `!eq null` where it is present. Multiple filters AND together. Accepted fields are declared per collection via `x-tvstor-filterable`; violations are `400 invalid_argument` naming the offending filter in `errors[]`.
 *
 */
export const zFilter = z.array(z.string());

/**
 * Sort key on the same field paths as `filter`, `-` prefix for descending: `name`, `-updatedAt`.
 */
export const zSort = z.string();

/**
 * Full admission (schema, invariants, references, RBAC, quotas) without persisting or scheduling anything; returns what would happen. DRYRUN-EXT (2026-08-27) — i confini dichiarati dell'estensione a delete e actions: (1) sui DELETE tutte le guardie corrono (404, 409 in_use coi nomi, retention_locked, immutabilita') e la cancellazione — task di teardown inclusi — rolla indietro; la risposta e' quella vera MA SENZA l'header `X-TVStor-Task` (il task del dry-run non esiste: nominarlo sarebbe una bugia). (2) Sulle actions promosse (expand/revert del volume, addMember dei due cluster, expand del vdisk) l'ammissione corre tutta e il task non nasce (202 senza header). (3) Confine confirm-typed: il dry-run valida TUTTO TRANNE il `confirm` — il gesto digitato non si consuma in prova, cosi' un preflight verifica le guardie senza scriversi il nome (la sudo elevation invece resta pretesa: e' autorizzazione, non gesto). (4) Restano fuori, con ragione: i verbi del piano auth (login/sudo/sessioni/totp — il gesto E' l'effetto), i receive interni di replica e join (machine-to-machine), il piano dati /fs e WOPI, le actions il cui unico valore e' l'esecuzione (syncNow, test del sink, deepScrub, task abort/cancel/resume, ack/resolve degli allarmi) e i gesti di failover/promozione (la prova e' il mestiere dell'arbitro, non di un preflight). (5) CONFINE DEI BATCH (caccia 6 #5, 2026-08-28): sui batch endpoint OGNI VOCE e' giudicata contro lo stato IMMUTATO — ogni cancellazione di prova rolla indietro, quindi la voce N+1 non vede gli effetti delle precedenti. Il caso parlante e VERO (scopes): batch-delete di [figlio, padre] — il run vero cancella il figlio e POI il padre (ormai vuoto): entrambi deleted; il dry-run giudica il padre contro lo stato immutato (il figlio ancora vivo) e lo RIFIUTA not-empty. Il dry-run dei batch e' l'ammissione DI OGGI voce per voce, mai la simulazione della sequenza — dichiarato, non simulato.
 *
 */
export const zDryRun = z.boolean().default(false);

/**
 * Client-generated UUID for action invocations: stored with the resulting Task for 24 h; retries replay the same response instead of re-executing. The key is BOUND to a fingerprint of the request (method + path + body, #212 2026-08-24): a replay is honoured only for the SAME request repeated unchanged — reusing a key with a different body answers `422 validation` naming the header, because replaying the old outcome would silently discard the new request. Mint a fresh key for every new gesture; reuse one only to retry that exact gesture.
 *
 */
export const zIdempotencyKey = z.uuid();

/**
 * Sudo-mode elevation token from `POST /api/v1/auth/sudo`.
 */
export const zSudoToken = z.string();

/**
 * DECLARED AUTH EXCEPTION (NAS-WebExplorer #30, dettatura Luca 2026-08-05): on the fs STREAM GETs ONLY (`content`, `archive` — never the listing, never any mutation) the session bearer token MAY ride the query string instead of the `Authorization` header. MOTIVATION: browser-native downloads (an anchor `href`, `window.open`) cannot set headers, and a download that only works through fetch+blob would buffer whole files in memory — unacceptable for share-sized archives. SAME token, SAME session checks, SAME RBAC as the header form; when both are present the header WINS. The credential lands in URLs (history, proxy logs) — accepted and bounded: session tokens are short-lived and revocable, transport is TLS-only, and the parameter is honored on exactly these GETs, nowhere else.
 *
 */
export const zFsAccessToken = z.string();

/**
 * THE WOPI AUTHENTICATION (WEBEXP-EDIT #63, 2026-08-05 — the standard WOPI parameter name, declared exactly like the round #30 `FsAccessToken` exception): on the `/api/v1/wopi/...` routes ONLY, the `tvse_` EDITOR TOKEN in the query string IS the authentication — there is no session, no RBAC evaluation (the mint already ran both; the token is bound to one file, one mode, one user, hard expiry). SPIKE FINDING 3 (binding, docs/onlyoffice-spike-2026-08-05.md): the editor server attaches its OWN `Authorization: Bearer <JWT>` header to these calls — a foreign Bearer MUST be IGNORED on the WOPI routes, never allowed to fail a request whose `access_token` is valid. Landing in third-party logs is accepted BECAUSE the token is dedicated, short-lived and file-scoped (finding 1 — never the session token).
 *
 */
export const zWopiAccessToken = z.string();

export const zGetSystemHealthData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The component is alive.
 */
export const zGetSystemHealthResponse = zSystemHealth;

export const zGetOpenapiData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * OpenAPI 3.1 document.
 */
export const zGetOpenapiResponse = z.record(z.string(), z.unknown());

export const zIdentifyLoginData = z.object({
    body: zIdentifyRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * How to authenticate this identifier.
 */
export const zIdentifyLoginResponse = zIdentifyResult;

export const zListSessionsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        realm: z.optional(z.string())
    }))
});

/**
 * Session page.
 */
export const zListSessionsResponse = zSessionList;

export const zCreateSessionData = z.object({
    body: z.object({
        username: z.string(),
        password: z.string(),
        realm: z.optional(z.string()),
        otp: z.optional(z.string().max(64))
    }),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Session created; the body carries the `tvss_` bearer token (shown exactly once) and the session details.
 *
 */
export const zCreateSessionResponse = zSessionCreated;

export const zDeleteCurrentSessionData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Logged out.
 */
export const zDeleteCurrentSessionResponse = z.void();

export const zGetCurrentSessionData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The current session.
 */
export const zGetCurrentSessionResponse = zSession;

export const zDeleteSessionData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        session: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * Revoked.
 */
export const zDeleteSessionResponse = z.void();

export const zGetSessionData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        session: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The session.
 */
export const zGetSessionResponse = zSession;

export const zElevateSudoData = z.object({
    body: z.object({
        password: z.string()
    }),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Elevation granted.
 */
export const zElevateSudoResponse = zSudoGrant;

export const zListMyPermissionsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.object({
        scope: z.string().max(257)
    })
});

/**
 * The held subset of the catalog, sorted by name. Reuses the catalog entry shape of `GET /rbac/permissions`.
 *
 */
export const zListMyPermissionsResponse = zPermissionList;

export const zListUsersData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * User page.
 */
export const zListUsersResponse = zUserList;

export const zCreateUserData = z.object({
    body: zUserWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateUserResponse = zUser;

export const zGetMyselfData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Your account.
 */
export const zGetMyselfResponse = zUser;

export const zPatchMyselfData = z.object({
    body: zUserWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchMyselfResponse = zUser;

export const zReplaceMyPasswordData = z.object({
    body: z.object({
        currentPassword: z.string(),
        newPassword: z.string()
    }),
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Password changed.
 */
export const zReplaceMyPasswordResponse = z.void();

export const zBatchDeleteUsersData = z.object({
    body: zBatchDeleteRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Per-item outcomes.
 */
export const zBatchDeleteUsersResponse = zBatchDeleteResult;

export const zDeleteUserData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        user: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteUserResponse = z.void();

export const zGetUserData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        user: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The user.
 */
export const zGetUserResponse = zUser;

export const zPatchUserData = z.object({
    body: zUserWritable,
    path: z.object({
        user: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Updated.
 */
export const zPatchUserResponse = zUser;

export const zReplaceUserPasswordData = z.object({
    body: z.object({
        newPassword: z.string(),
        mustChangePassword: z.optional(z.boolean()).default(true)
    }),
    path: z.object({
        user: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Password set.
 */
export const zReplaceUserPasswordResponse = z.void();

export const zUnlockUserData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        user: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Unlocked.
 */
export const zUnlockUserResponse = zUser;

export const zRevokeUserSessionsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        user: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Revocation counts (TVirt shape, adopted 2026-08-02 — minus the app-token count, which joins with its milestone).
 *
 */
export const zRevokeUserSessionsResponse = z.object({
    revokedSessions: z.optional(z.int()),
    revokedEditorTokens: z.optional(z.int())
});

export const zEnrollTotpData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The enrolment material, shown exactly once.
 */
export const zEnrollTotpResponse = zTotpEnrollment;

export const zConfirmTotpData = z.object({
    body: zTotpConfirmRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Enrolment confirmed; the render now carries `mfaEnrolled: true`.
 *
 */
export const zConfirmTotpResponse = zUser;

export const zDisenrollMyTotpData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The account, `mfaEnrolled: false`.
 *
 */
export const zDisenrollMyTotpResponse = zUser;

export const zDisenrollUserTotpData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        user: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The user, `mfaEnrolled: false`.
 *
 */
export const zDisenrollUserTotpResponse = zUser;

export const zListScopesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Scope page.
 */
export const zListScopesResponse = zScopeList;

export const zCreateScopeData = z.object({
    body: zScopeWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateScopeResponse = zScope;

export const zBatchDeleteScopesData = z.object({
    body: zBatchDeleteRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Per-item outcomes.
 */
export const zBatchDeleteScopesResponse = zBatchDeleteResult;

export const zDeleteScopeData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        scope: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteScopeResponse = z.void();

export const zGetScopeData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        scope: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The scope.
 */
export const zGetScopeResponse = zScope;

export const zPatchScopeData = z.object({
    body: zScopeWritable,
    path: z.object({
        scope: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchScopeResponse = zScope;

export const zGetScopeReportData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        scope: z.uuid()
    }),
    query: z.optional(z.object({
        format: z.optional(z.enum([
            'json',
            'csv',
            'jsonl'
        ]))
    }))
});

/**
 * The report (JSON), or its rows streamed (CSV/JSONL).
 */
export const zGetScopeReportResponse = zScopeReport;

export const zListRbacPermissionsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The catalog.
 */
export const zListRbacPermissionsResponse = zPermissionList;

export const zListRbacRolesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Role page.
 */
export const zListRbacRolesResponse = zRoleList;

export const zCreateRbacRoleData = z.object({
    body: zRole,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateRbacRoleResponse = zRole;

export const zBatchDeleteRolesData = z.object({
    body: zBatchDeleteRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Per-item outcomes.
 */
export const zBatchDeleteRolesResponse = zBatchDeleteResult;

export const zDeleteRbacRoleData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        role: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteRbacRoleResponse = z.void();

export const zGetRbacRoleData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        role: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The role.
 */
export const zGetRbacRoleResponse = zRole;

export const zPatchRbacRoleData = z.object({
    body: zRole,
    path: z.object({
        role: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchRbacRoleResponse = zRole;

export const zListRbacGrantsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Grant page.
 */
export const zListRbacGrantsResponse = zGrantList;

export const zCreateRbacGrantData = z.object({
    body: zGrantWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateRbacGrantResponse = zGrant;

export const zBatchDeleteGrantsData = z.object({
    body: zBatchDeleteRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Per-item outcomes.
 */
export const zBatchDeleteGrantsResponse = zBatchDeleteResult;

export const zDeleteRbacGrantData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        grant: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Revoked.
 */
export const zDeleteRbacGrantResponse = z.void();

export const zGetRbacGrantData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        grant: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The grant.
 */
export const zGetRbacGrantResponse = zGrant;

export const zGetClusterData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The singleton.
 */
export const zGetClusterResponse = zCluster;

export const zPatchClusterData = z.object({
    body: zClusterWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Updated.
 */
export const zPatchClusterResponse = zCluster;

export const zPromoteStoreData = z.object({
    body: zClusterPromoteStoreRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zSwitchoverStoreData = z.object({
    body: zClusterSwitchoverStoreRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zAddStoreMemberData = z.object({
    body: zClusterStoreMemberRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zRemoveStoreMemberData = z.object({
    body: zClusterStoreMemberRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zStartRollingUpdateData = z.object({
    body: zRollingUpdateRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zPauseRollingUpdateData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Paused; the current state.
 */
export const zPauseRollingUpdateResponse = zRollingUpdateState;

export const zResumeRollingUpdateData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Resumed; the current state.
 */
export const zResumeRollingUpdateResponse = zRollingUpdateState;

export const zAbortRollingUpdateData = z.object({
    body: zRollingUpdateAbortRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Aborted; the final state.
 */
export const zAbortRollingUpdateResponse = zRollingUpdateState;

export const zListAuditEventsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * One page, newest first unless `sort` says otherwise.
 */
export const zListAuditEventsResponse = zAuditEventList;

export const zVerifyAuditChainData = z.object({
    body: z.optional(zAuditVerifyRequest),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The verdicts.
 */
export const zVerifyAuditChainResponse = zAuditChainVerification;

export const zListClusterDisksData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The fleet-wide disk census.
 */
export const zListClusterDisksResponse = zClusterDiskList;

export const zListClusterNodesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * One page.
 */
export const zListClusterNodesResponse = zClusterNodeList;

export const zCreateClusterNodeData = z.object({
    body: zClusterNodeCreateRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Registered; the body carries the join token — the ONLY render it will ever get.
 *
 */
export const zCreateClusterNodeResponse = zClusterNodeRegistration;

export const zDeleteClusterNodeData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Registration revoked; the token is dead.
 */
export const zDeleteClusterNodeResponse = z.void();

export const zGetClusterNodeData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The node.
 */
export const zGetClusterNodeResponse = zClusterNode;

export const zPatchClusterNodeData = z.object({
    body: zClusterNodeWritable,
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Updated.
 */
export const zPatchClusterNodeResponse = zClusterNode;

export const zRevokeClusterNodeCertificatesData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The certificates are revoked and the list re-issued.
 */
export const zRevokeClusterNodeCertificatesResponse = zClusterNodeRevocation;

export const zReinstateClusterNodeCertificatesData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The node may obtain certificates again.
 */
export const zReinstateClusterNodeCertificatesResponse = zClusterNodeReinstatement;

export const zDrainClusterNodeData = z.object({
    body: zNodeDrainRequest,
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zUndrainClusterNodeData = z.object({
    body: zNodeDrainRequest,
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zEnrollClusterNodeWrappingKeyData = z.object({
    body: zClusterNodeWrappingKeyEnrollRequest,
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The wrapping key is enrolled.
 */
export const zEnrollClusterNodeWrappingKeyResponse = zClusterNodeWrappingKeyEnrollment;

export const zRevokeClusterNodeWrappingKeyData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The wrapping key is revoked and the envelopes are dead.
 */
export const zRevokeClusterNodeWrappingKeyResponse = zClusterNodeWrappingKeyRevocation;

export const zListNodeDiskChunksData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The census.
 */
export const zListNodeDiskChunksResponse = zNodeDiskChunksList;

export const zCreateNodeChunksData = z.object({
    body: zNodeChunksCreateRequest,
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zDeleteNodeChunksData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid(),
        disk: z.string().min(1)
    }),
    query: z.object({
        dryRun: z.optional(z.boolean()).default(false),
        confirm: z.string()
    })
});

export const zListNodeRaidsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Raid page.
 */
export const zListNodeRaidsResponse = zNodeRaidList;

export const zCreateNodeRaidData = z.object({
    body: zNodeRaidCreateRequest,
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the node.raid.make task is running.
 */
export const zCreateNodeRaidResponse = zNodeRaid;

export const zDeleteNodeRaidData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid(),
        raid: z.uuid()
    }),
    query: z.object({
        dryRun: z.optional(z.boolean()).default(false),
        confirm: z.string()
    })
});

export const zGetNodeRaidData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid(),
        raid: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The raid.
 */
export const zGetNodeRaidResponse = zNodeRaid;

export const zListNodeVdisksData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Vdisk page.
 */
export const zListNodeVdisksResponse = zNodeVdiskList;

export const zCreateNodeVdiskData = z.object({
    body: zNodeVdiskCreateRequest,
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the node.vdisk.make task is running.
 */
export const zCreateNodeVdiskResponse = zNodeVdisk;

export const zDeleteNodeVdiskData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    }),
    query: z.object({
        dryRun: z.optional(z.boolean()).default(false),
        confirm: z.string()
    })
});

export const zGetNodeVdiskData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The vdisk.
 */
export const zGetNodeVdiskResponse = zNodeVdisk;

export const zExpandNodeVdiskData = z.object({
    body: zNodeVdiskExpandRequest,
    path: z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zMigrateNodeVdiskData = z.object({
    body: zNodeVdiskMigrateRequest,
    path: z.object({
        node: z.uuid(),
        vdisk: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zGetClusterNodeMountsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        node: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The node's mount census.
 */
export const zGetClusterNodeMountsResponse = zNodeMountCensus;

export const zListNetworksData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Network page.
 */
export const zListNetworksResponse = zNetworkList;

export const zCreateNetworkData = z.object({
    body: zNetworkCreateRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Declared (no bindings yet — bind nodes one by one).
 */
export const zCreateNetworkResponse = zNetwork;

export const zDeleteNetworkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        network: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Deleted.
 */
export const zDeleteNetworkResponse = z.void();

export const zGetNetworkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        network: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The network.
 */
export const zGetNetworkResponse = zNetwork;

export const zPatchNetworkData = z.object({
    body: zNetworkWritable,
    path: z.object({
        network: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Updated.
 */
export const zPatchNetworkResponse = zNetwork;

export const zListNetworkBindingsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        network: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The bindings.
 */
export const zListNetworkBindingsResponse = zNetworkNodeBindingList;

export const zDeleteNetworkBindingData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        network: z.uuid(),
        node: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Removed; the node converges back onto the seams.
 */
export const zDeleteNetworkBindingResponse = z.void();

export const zGetNetworkBindingData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        network: z.uuid(),
        node: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The binding.
 */
export const zGetNetworkBindingResponse = zNetworkNodeBinding;

export const zPutNetworkBindingData = z.object({
    body: zNetworkNodeBindingWritable,
    path: z.object({
        network: z.uuid(),
        node: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Replaced; the reconcile pass converges the node.
 */
export const zPutNetworkBindingResponse = zNetworkNodeBinding;

export const zInternalJoinAttestData = z.object({
    body: zInternalJoinAttestRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The attestation — public material and a signature over it; nothing here is a secret.
 *
 */
export const zInternalJoinAttestResponse2 = zInternalJoinAttestResponse;

export const zInternalPkiSignData = z.object({
    body: zInternalPkiSignRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The issued leaf certificate and the CA that signed it — public material.
 */
export const zInternalPkiSignResponse2 = zInternalPkiSignResponse;

export const zInternalJoinData = z.object({
    body: zInternalJoinRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Joined (first half): the token is consumed, the registry row is `joinPending`, and the body seeds the new node — the joiner's confirm commits the flip to `member`.
 *
 */
export const zInternalJoinResponse2 = zInternalJoinResponse;

export const zInternalJoinConfirmData = z.object({
    body: zInternalJoinConfirmRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The row is `member` — committed now, or found already committed (the idempotent replay).
 *
 */
export const zInternalJoinConfirmResponse2 = zInternalJoinConfirmResponse;

export const zInternalNasIdmapAllocateData = z.object({
    body: zInternalNasIdmapRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * I numeri incisi per questo soggetto — nuovi o gia' esistenti, la risposta e' la stessa.
 *
 */
export const zInternalNasIdmapAllocateResponse = zInternalNasIdmapResponse;

export const zInternalReplicationReceiveData = z.object({
    body: z.string(),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Stream fully received, verified (sha-256 trailer) and committed — the replica stands at `toStamp`.
 *
 */
export const zInternalReplicationReceiveResponse = zReplicationReceiveResult;

export const zInternalReplicationPromoteData = z.object({
    body: z.object({
        sourceVolume: z.uuid(),
        toStamp: z.string().regex(/^[0-9a-f]{8}$/),
        name: z.optional(z.string().min(1).max(63).regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/))
    }),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Promotion admitted (or already recorded — replay).
 */
export const zInternalReplicationPromoteResponse = zReplicationPromoteResult;

export const zInternalReplicationMirrorPeerData = z.object({
    body: zReplicationMirrorPeerRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * Offer admitted; poll it.
 */
export const zInternalReplicationMirrorPeerResponse = zReplicationMirrorPeerOffer;

export const zInternalReplicationMirrorPeerOfferData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        offer: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The token, once.
 */
export const zInternalReplicationMirrorPeerOfferResponse = zReplicationMirrorPeerToken;

export const zListCephClustersData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * CephCluster page.
 */
export const zListCephClustersResponse = zCephClusterList;

export const zCreateCephClusterData = z.object({
    body: zCephClusterWritable,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Created; the ceph.deploy parent is running (phase `initializing`).
 */
export const zCreateCephClusterResponse = zCephCluster;

export const zDeleteCephClusterData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never())
});

export const zGetCephClusterData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The ceph cluster.
 */
export const zGetCephClusterResponse = zCephCluster;

export const zPatchCephClusterData = z.object({
    body: zCephClusterWritable,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchCephClusterResponse = zCephCluster;

export const zAddCephClusterMemberData = z.object({
    body: zCephClusterAddMemberRequest,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zDestroyCephClusterData = z.object({
    body: zCephClusterDestroyRequest,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zMigrateCephClusterSecureData = z.object({
    body: zCephClusterMigrateSecureRequest,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zGetCephClusterHealthData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The explorer view.
 */
export const zGetCephClusterHealthResponse = zCephHealthReport;

export const zListCephClusterPoolsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The explorer view.
 */
export const zListCephClusterPoolsResponse = zCephPoolExplorerList;

export const zGetCephClusterPoolData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid(),
        pool: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The explorer view.
 */
export const zGetCephClusterPoolResponse = zCephPoolExplorerEntry;

export const zListCephClusterPoolObjectsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid(),
        pool: z.string()
    }),
    query: z.optional(z.object({
        prefix: z.optional(z.string()),
        cursor: z.optional(z.string())
    }))
});

/**
 * The explorer view.
 */
export const zListCephClusterPoolObjectsResponse = zCephPoolObjectsPage;

export const zGetCephClusterCrushMapData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The explorer view.
 */
export const zGetCephClusterCrushMapResponse = zCephCrushMap;

export const zListCephClusterOsdsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The explorer view.
 */
export const zListCephClusterOsdsResponse = zCephOsdExplorerList;

export const zSetCephClusterOsdFlagsData = z.object({
    body: zCephSetOsdFlagsRequest,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zOutCephClusterOsdData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid(),
        osdId: z.int()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zInCephClusterOsdData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid(),
        osdId: z.int()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zDeepScrubCephClusterPoolData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid(),
        pool: z.string()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zListMultiscsiClustersData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * MultiscsiCluster page.
 */
export const zListMultiscsiClustersResponse = zMultiscsiClusterList;

export const zCreateMultiscsiClusterData = z.object({
    body: zMultiscsiClusterWritable,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Created; the multiscsicluster.init parent is running (phase `initializing`).
 */
export const zCreateMultiscsiClusterResponse = zMultiscsiCluster;

export const zDeleteMultiscsiClusterData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetMultiscsiClusterData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The multiscsi cluster.
 */
export const zGetMultiscsiClusterResponse = zMultiscsiCluster;

export const zPatchMultiscsiClusterData = z.object({
    body: zMultiscsiClusterWritable,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchMultiscsiClusterResponse = zMultiscsiCluster;

export const zAddMultiscsiClusterMemberData = z.object({
    body: zMultiscsiClusterAddMemberRequest,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zScrubMultiscsiClusterData = z.object({
    body: zMultiscsiClusterScrubRequest,
    path: z.object({
        cluster: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zListVolumesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Volume page.
 */
export const zListVolumesResponse = zVolumeList;

export const zCreateVolumeData = z.object({
    body: zVolumeCreateRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Declared (plain create — synchronous, no task, `X-TVStor-Task` ABSENT) or clone materializing (`X-TVStor-Task` names the clone task).
 *
 */
export const zCreateVolumeResponse = zVolume;

export const zDeleteVolumeData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zDeleteVolumeResponse = z.union([
    z.unknown(),
    z.void()
]);

export const zGetVolumeData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The volume.
 */
export const zGetVolumeResponse = zVolume;

export const zPatchVolumeData = z.object({
    body: zVolumeWritable,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated. When the patch changed `qos` (ceph datapools only), `X-TVStor-Task` names the `ceph.volume.qos.apply` task the write birthed (M5d, 2026-08-04); absent on a name/notes-only patch — nothing physical to converge.
 *
 */
export const zPatchVolumeResponse = zVolume;

export const zListVolumeSnapshotsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Snapshot page.
 */
export const zListVolumeSnapshotsResponse = zVolumeSnapshotList;

export const zCreateVolumeSnapshotData = z.object({
    body: zVolumeSnapshotWritable,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the volume.snapshot.create task is running.
 */
export const zCreateVolumeSnapshotResponse = zVolumeSnapshot;

export const zDeleteVolumeSnapshotData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        volume: z.uuid(),
        snapshot: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetVolumeSnapshotData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        volume: z.uuid(),
        snapshot: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The snapshot.
 */
export const zGetVolumeSnapshotResponse = zVolumeSnapshot;

export const zUpdateVolumeSnapshotData = z.object({
    body: z.object({
        retention: zSnapshotRetention
    }),
    path: z.object({
        volume: z.uuid(),
        snapshot: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * The snapshot with its retention.
 */
export const zUpdateVolumeSnapshotResponse = zVolumeSnapshot;

export const zRevertVolumeData = z.object({
    body: zVolumeRevertRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zFailoverVolumeData = z.object({
    body: z.optional(zVolumeFailoverRequest),
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zForcePromoteVolumeLegData = z.object({
    body: zVolumeForcePromoteRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zPromoteVolumeReplicaData = z.object({
    body: zVolumePromoteRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zSyncDegradeVolumeData = z.object({
    body: zVolumeSyncDegradeRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The updated volume, degrade opt-in recorded.
 */
export const zSyncDegradeVolumeResponse = zVolume;

export const zSyncAttachVolumeData = z.object({
    body: zVolumeSyncAttachRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zExpandVolumeData = z.object({
    body: zVolumeExpandRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zExpandVolumeResponse = z.union([
    zVolume,
    z.unknown()
]);

export const zMigrateVolumeToSharedFilesystemData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Migration task scheduled; the volume's shares are OFFLINE (system lockdown) until the task flips the placement.
 *
 */
export const zMigrateVolumeToSharedFilesystemResponse = z.object({
    task: z.string(),
    shares: z.array(z.uuid()),
    estimatedBytes: z.int()
});

export const zShrinkVolumeData = z.object({
    body: zVolumeShrinkRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zShrinkVolumeResponse = z.union([
    zVolume,
    z.unknown()
]);

export const zDiffVolumeData = z.object({
    body: zVolumeDiffRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zAttachVolumeToIoServerPairData = z.object({
    body: zVolumeAttachIoServerPairRequest,
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zDetachVolumeFromIoServerPairData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        volume: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zListHostsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Host page.
 */
export const zListHostsResponse = zHostList;

export const zCreateHostData = z.object({
    body: zHostWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateHostResponse = zHost;

export const zDeleteHostData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        host: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteHostResponse = z.void();

export const zGetHostData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        host: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The host.
 */
export const zGetHostResponse = zHost;

export const zPatchHostData = z.object({
    body: zHostWritable,
    path: z.object({
        host: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchHostResponse = zHost;

export const zListTargetsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Target page.
 */
export const zListTargetsResponse = zTargetList;

export const zCreateTargetData = z.object({
    body: zTargetCreateRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the target.apply task is running.
 */
export const zCreateTargetResponse = zTarget;

export const zDeleteTargetData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        target: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetTargetData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        target: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The target.
 */
export const zGetTargetResponse = zTarget;

export const zPatchTargetData = z.object({
    body: zTargetWritable,
    path: z.object({
        target: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated; a hosts or chap change runs a target.apply task.
 */
export const zPatchTargetResponse = zTarget;

export const zListIoServerPairsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * I/O server pair page.
 */
export const zListIoServerPairsResponse = zIoServerPairList;

export const zCreateIoServerPairData = z.object({
    body: zIoServerPairWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Admitted; the apply task mints the identity.
 */
export const zCreateIoServerPairResponse = zIoServerPair;

export const zDeleteIoServerPairData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        pair: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetIoServerPairData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        pair: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The pair, with its volumes and the observed holders.
 */
export const zGetIoServerPairResponse = zIoServerPair;

export const zPatchIoServerPairData = z.object({
    body: zIoServerPairWritable,
    path: z.object({
        pair: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchIoServerPairResponse = zIoServerPair;

export const zGetIoServerPairCredentialData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        pair: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The identity, keyring and ceph.conf texts.
 */
export const zGetIoServerPairCredentialResponse = zIoServerPairCredential;

export const zRotateIoServerPairCredentialData = z.object({
    body: zIoServerPairRotateRequest,
    path: z.object({
        pair: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zFenceIoServerPairMemberData = z.object({
    body: zIoServerPairFenceRequest,
    path: z.object({
        pair: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zUnfenceIoServerPairMemberData = z.object({
    body: zIoServerPairFenceRequest,
    path: z.object({
        pair: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zListCatalogSourcesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        node: z.optional(z.string().max(63)),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Catalog source page.
 */
export const zListCatalogSourcesResponse = zCatalogSourceList;

export const zCreateCatalogSourceData = z.object({
    body: zCatalogSourceCreate,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Created.
 */
export const zCreateCatalogSourceResponse = zCatalogSource;

export const zDeleteCatalogSourceData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteCatalogSourceResponse = z.void();

export const zGetCatalogSourceData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The source.
 */
export const zGetCatalogSourceResponse = zCatalogSource;

export const zPatchCatalogSourceData = z.object({
    body: zCatalogSourceUpdate,
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchCatalogSourceResponse = zCatalogSource;

export const zListCatalogSourceCatalogData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.object({
        pathPrefix: z.optional(z.string().max(4096)),
        extension: z.optional(z.string().max(32)),
        type: z.optional(z.enum([
            'file',
            'dir',
            'symlink',
            'other'
        ])),
        sizeMin: z.optional(z.int().gte(0)),
        sizeMax: z.optional(z.int().gte(0)),
        mtimeFrom: z.optional(z.iso.datetime()),
        mtimeTo: z.optional(z.iso.datetime()),
        uid: z.optional(z.int().gte(0)),
        gid: z.optional(z.int().gte(0)),
        nameContains: z.optional(z.string().max(255)),
        sort: z.optional(z.enum([
            'path',
            '-path',
            'name',
            '-name',
            'size',
            '-size',
            'mtime',
            '-mtime',
            'ctime',
            '-ctime'
        ])),
        limit: z.optional(z.int().gte(1).lte(1000)).default(100),
        cursor: z.optional(z.string())
    }))
});

/**
 * A page of catalog entries.
 */
export const zListCatalogSourceCatalogResponse = zCatalogEntryList;

export const zGetCatalogSourceCatalogStatusData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The catalog status.
 */
export const zGetCatalogSourceCatalogStatusResponse = zCatalogStatus;

export const zGetCatalogSourceCatalogSummaryData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The breakdown.
 */
export const zGetCatalogSourceCatalogSummaryResponse = zCatalogSummary;

export const zExportCatalogSourceCatalogData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        source: z.uuid()
    }),
    query: z.object({
        format: z.enum([
            'csv',
            'jsonl'
        ]),
        pathPrefix: z.optional(z.string().max(4096)),
        extension: z.optional(z.string().max(32)),
        type: z.optional(z.enum([
            'file',
            'dir',
            'symlink',
            'other'
        ])),
        sizeMin: z.optional(z.int().gte(0)),
        sizeMax: z.optional(z.int().gte(0)),
        mtimeFrom: z.optional(z.iso.datetime()),
        mtimeTo: z.optional(z.iso.datetime()),
        uid: z.optional(z.int().gte(0)),
        gid: z.optional(z.int().gte(0)),
        nameContains: z.optional(z.string().max(255)),
        sort: z.optional(z.enum([
            'path',
            '-path',
            'name',
            '-name',
            'size',
            '-size',
            'mtime',
            '-mtime',
            'ctime',
            '-ctime'
        ]))
    })
});

/**
 * The export, streamed.
 */
export const zExportCatalogSourceCatalogResponse = z.string();

export const zScanCatalogSourceCatalogData = z.object({
    body: z.optional(zCatalogScanRequest),
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zGetCatalogSourceCatalogCredentialsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        source: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The credential.
 */
export const zGetCatalogSourceCatalogCredentialsResponse = zCatalogCredentials;

export const zListEnclosuresData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Enclosure page.
 */
export const zListEnclosuresResponse = zEnclosureList;

export const zCreateEnclosureData = z.object({
    body: zEnclosureCreateRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Declared.
 */
export const zCreateEnclosureResponse = zEnclosure;

export const zDeleteEnclosureData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        enclosure: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteEnclosureResponse = z.void();

export const zGetEnclosureData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        enclosure: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The enclosure.
 */
export const zGetEnclosureResponse = zEnclosure;

export const zFailoverEnclosureData = z.object({
    body: zEnclosureFailoverRequest,
    path: z.object({
        enclosure: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zFailbackEnclosureData = z.object({
    body: zEnclosureFailbackRequest,
    path: z.object({
        enclosure: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zListFileSharesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * File share page.
 */
export const zListFileSharesResponse = zFileShareList;

export const zCreateFileShareData = z.object({
    body: zFileShareWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the create task is running.
 */
export const zCreateFileShareResponse = zFileShare;

export const zDeleteFileShareData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.object({
        dryRun: z.optional(z.boolean()).default(false),
        destroyData: z.boolean()
    })
});

export const zGetFileShareData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The file share.
 */
export const zGetFileShareResponse = zFileShare;

export const zPatchFileShareData = z.object({
    body: zFileShareWritable,
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchFileShareResponse = zFileShare;

export const zRemountFileShareData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zResizeFileShareData = z.object({
    body: zFileShareResizeRequest,
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zResizeFileShareResponse = z.union([
    zFileShare,
    z.unknown()
]);

export const zLockdownFileShareData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string()),
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * The share is in lockdown; counts of what fell.
 */
export const zLockdownFileShareResponse = zFileShareLockdownResult;

export const zUnlockFileShareData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string()),
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * The share (lockdown null — serving converges back).
 */
export const zUnlockFileShareResponse = zFileShare;

export const zPromoteFileShareReplicaData = z.object({
    body: zFileSharePromoteRequest,
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zListFileShareSnapshotsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * A page of snapshots.
 */
export const zListFileShareSnapshotsResponse = zFileShareSnapshotList;

export const zCreateFileShareSnapshotData = z.object({
    body: zFileShareSnapshotWritable,
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Created; the ceph.fileshare.snapshot task is running.
 */
export const zCreateFileShareSnapshotResponse = zFileShareSnapshot;

export const zDeleteFileShareSnapshotData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string(),
        snapshot: z.string()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetFileShareSnapshotData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string(),
        snapshot: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The snapshot.
 */
export const zGetFileShareSnapshotResponse = zFileShareSnapshot;

export const zUpdateFileShareSnapshotData = z.object({
    body: z.object({
        retention: zSnapshotRetention
    }),
    path: z.object({
        fileShare: z.string(),
        snapshot: z.string()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * The snapshot with its retention.
 */
export const zUpdateFileShareSnapshotResponse = zFileShareSnapshot;

export const zGetFileShareAuditTailData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.object({
        tail: z.optional(z.int().gte(1).lte(1000)).default(100)
    }))
});

/**
 * The newest events, oldest first.
 */
export const zGetFileShareAuditTailResponse = zClientAuditTail;

export const zListFileShareCatalogData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.object({
        pathPrefix: z.optional(z.string().max(4096)),
        extension: z.optional(z.string().max(32)),
        type: z.optional(z.enum([
            'file',
            'dir',
            'symlink',
            'other'
        ])),
        sizeMin: z.optional(z.int().gte(0)),
        sizeMax: z.optional(z.int().gte(0)),
        mtimeFrom: z.optional(z.iso.datetime()),
        mtimeTo: z.optional(z.iso.datetime()),
        uid: z.optional(z.int().gte(0)),
        gid: z.optional(z.int().gte(0)),
        nameContains: z.optional(z.string().max(255)),
        sort: z.optional(z.enum([
            'path',
            '-path',
            'name',
            '-name',
            'size',
            '-size',
            'mtime',
            '-mtime',
            'ctime',
            '-ctime'
        ])),
        limit: z.optional(z.int().gte(1).lte(1000)).default(100),
        cursor: z.optional(z.string())
    }))
});

/**
 * A page of catalog entries.
 */
export const zListFileShareCatalogResponse = zCatalogEntryList;

export const zGetFileShareCatalogStatusData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The catalog status.
 */
export const zGetFileShareCatalogStatusResponse = zCatalogStatus;

export const zGetFileShareCatalogSummaryData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The breakdown.
 */
export const zGetFileShareCatalogSummaryResponse = zCatalogSummary;

export const zExportFileShareCatalogData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.object({
        format: z.enum([
            'csv',
            'jsonl'
        ]),
        pathPrefix: z.optional(z.string().max(4096)),
        extension: z.optional(z.string().max(32)),
        type: z.optional(z.enum([
            'file',
            'dir',
            'symlink',
            'other'
        ])),
        sizeMin: z.optional(z.int().gte(0)),
        sizeMax: z.optional(z.int().gte(0)),
        mtimeFrom: z.optional(z.iso.datetime()),
        mtimeTo: z.optional(z.iso.datetime()),
        uid: z.optional(z.int().gte(0)),
        gid: z.optional(z.int().gte(0)),
        nameContains: z.optional(z.string().max(255)),
        sort: z.optional(z.enum([
            'path',
            '-path',
            'name',
            '-name',
            'size',
            '-size',
            'mtime',
            '-mtime',
            'ctime',
            '-ctime'
        ]))
    })
});

/**
 * The export, streamed.
 */
export const zExportFileShareCatalogResponse = z.string();

export const zScanFileShareCatalogData = z.object({
    body: z.optional(zCatalogScanRequest),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zGetFileShareCatalogCredentialsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.string()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The credential.
 */
export const zGetFileShareCatalogCredentialsResponse = zCatalogCredentials;

export const zListFileShareFsEntriesData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.object({
        path: z.optional(z.string().max(4096)),
        limit: z.optional(z.int().gte(1).lte(2000)).default(500),
        cursor: z.optional(z.string())
    }))
});

/**
 * One listing page.
 */
export const zListFileShareFsEntriesResponse = zFsListing;

export const zGetFileShareFsAclData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.object({
        path: z.optional(z.string().max(4096))
    }))
});

/**
 * The ACL.
 */
export const zGetFileShareFsAclResponse = zFsAcl;

export const zPutFileShareFsAclData = z.object({
    body: z.object({
        path: z.optional(z.string().max(4096)),
        model: z.enum([
            'posix',
            'nt'
        ]),
        owner: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        group: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        entries: z.array(zFsAclEntry)
    }),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The ACL as it now stands, read back.
 */
export const zPutFileShareFsAclResponse = zFsAcl;

export const zCreateFileShareFsFolderData = z.object({
    body: z.object({
        path: z.string().min(1).max(4096)
    }),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * Folder created.
 */
export const zCreateFileShareFsFolderResponse = z.void();

export const zRenameFileShareFsEntryData = z.object({
    body: z.object({
        path: z.string().min(1).max(4096),
        newName: z.string().min(1).max(255)
    }),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * Renamed.
 */
export const zRenameFileShareFsEntryResponse = z.void();

export const zDeleteFileShareFsEntriesData = z.object({
    body: z.object({
        paths: z.array(z.string().min(1).max(4096)).min(1).max(1000)
    }),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * All listed entries deleted.
 */
export const zDeleteFileShareFsEntriesResponse = z.void();

export const zGetFileShareFsContentData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.object({
        path: z.string().min(1).max(4096),
        inline: z.optional(z.boolean()).default(false),
        access_token: z.optional(z.string())
    })
});

/**
 * The file's bytes, streamed.
 */
export const zGetFileShareFsContentResponse = z.string();

export const zPutFileShareFsContentData = z.object({
    body: z.string(),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.object({
        path: z.string().min(1).max(4096)
    })
});

/**
 * Uploaded — the file stands whole at `path`.
 */
export const zPutFileShareFsContentResponse = z.void();

export const zGetFileShareFsArchiveData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.object({
        path: z.optional(z.string().max(4096)),
        access_token: z.optional(z.string())
    }))
});

/**
 * The ZIP stream.
 */
export const zGetFileShareFsArchiveResponse = z.string();

export const zCreateFileShareFsEditorSessionData = z.object({
    body: z.object({
        path: z.string().min(1).max(4096),
        mode: z.enum([
            'view',
            'edit'
        ])
    }),
    path: z.object({
        fileShare: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * Editor session minted — the token renders exactly once.
 */
export const zCreateFileShareFsEditorSessionResponse = zFsEditorSession;

export const zWopiCheckFileInfoData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileId: z.uuid()
    }),
    query: z.object({
        access_token: z.string()
    })
});

/**
 * The CheckFileInfo document. `LastModifiedTime` is the ISO mtime the PutFile conflict check compares against; `UserCanWrite` renders the token's mode.
 *
 */
export const zWopiCheckFileInfoResponse = z.object({
    BaseFileName: z.string(),
    Size: z.number(),
    UserId: z.string(),
    UserFriendlyName: z.string(),
    UserCanWrite: z.boolean(),
    LastModifiedTime: z.string(),
    PostMessageOrigin: z.optional(z.string())
});

export const zWopiGetFileData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        fileId: z.uuid()
    }),
    query: z.object({
        access_token: z.string()
    })
});

/**
 * The document's bytes, streamed.
 */
export const zWopiGetFileResponse = z.string();

export const zWopiPutFileData = z.object({
    body: z.string(),
    path: z.object({
        fileId: z.uuid()
    }),
    query: z.object({
        access_token: z.string()
    }),
    headers: z.optional(z.object({
        'X-COOL-WOPI-Timestamp': z.optional(z.string())
    }))
});

/**
 * Saved — the file stands whole at the token's path.
 */
export const zWopiPutFileResponse = z.object({
    LastModifiedTime: z.string()
});

export const zListDhcpServicesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * DHCP service page.
 */
export const zListDhcpServicesResponse = zDhcpServiceList;

export const zCreateDhcpServiceData = z.object({
    body: zDhcpServiceWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the daemon converges via the reconcile pass.
 */
export const zCreateDhcpServiceResponse = zDhcpService;

export const zDeleteDhcpServiceData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        dhcpService: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted; the daemon stops via the reconcile pass.
 */
export const zDeleteDhcpServiceResponse = z.void();

export const zGetDhcpServiceData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        dhcpService: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The DHCP service.
 */
export const zGetDhcpServiceResponse = zDhcpService;

export const zPatchDhcpServiceData = z.object({
    body: zDhcpServiceWritable,
    path: z.object({
        dhcpService: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchDhcpServiceResponse = zDhcpService;

export const zListDirectoryRealmsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Directory realm page. `uidRange` and `allocatedIdentities` are composed from the cluster allocator (`id_realms`/`id_allocations`), which is the one authority on those numbers.
 *
 */
export const zListDirectoryRealmsResponse = zDirectoryRealmList;

export const zCreateDirectoryRealmData = z.object({
    body: zDirectoryRealmWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Declared; the uid range is engraved with it.
 */
export const zCreateDirectoryRealmResponse = zDirectoryRealm;

export const zDeleteDirectoryRealmData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        directoryRealm: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Deleted; the uid range survives as a tombstone.
 */
export const zDeleteDirectoryRealmResponse = z.void();

export const zGetDirectoryRealmData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        directoryRealm: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The directory realm.
 */
export const zGetDirectoryRealmResponse = zDirectoryRealm;

export const zPatchDirectoryRealmData = z.object({
    body: zDirectoryRealmWritable,
    path: z.object({
        directoryRealm: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Updated.
 */
export const zPatchDirectoryRealmResponse = zDirectoryRealm;

export const zPreflightDirectoryRealmData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        directoryRealm: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * The preflight report.
 */
export const zPreflightDirectoryRealmResponse = zDirectoryRealmPreflight;

export const zRevokeDirectoryRealmSessionsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        directoryRealm: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Revoked.
 */
export const zRevokeDirectoryRealmSessionsResponse = z.object({
    revokedSessions: z.optional(z.int()),
    revokedEditorTokens: z.optional(z.int())
});

export const zRefoundDirectoryRealmData = z.object({
    body: zDirectoryRealmRefoundRequest,
    path: z.object({
        directoryRealm: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Rebound. The body says what was kept and what it now means.
 *
 */
export const zRefoundDirectoryRealmResponse = zDirectoryRealmRefoundResult;

export const zListObjectStoresData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Object store page.
 */
export const zListObjectStoresResponse = zObjectStoreList;

export const zCreateObjectStoreData = z.object({
    body: zObjectStoreWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the create task is running.
 */
export const zCreateObjectStoreResponse = zObjectStore;

export const zDeleteObjectStoreData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetObjectStoreData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The object store.
 */
export const zGetObjectStoreResponse = zObjectStore;

export const zPatchObjectStoreData = z.object({
    body: zObjectStoreWritable,
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchObjectStoreResponse = zObjectStore;

export const zGetObjectStoreAuditTailData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.object({
        tail: z.optional(z.int().gte(1).lte(1000)).default(100)
    }))
});

/**
 * The newest events, oldest first.
 */
export const zGetObjectStoreAuditTailResponse = zClientAuditTail;

export const zListBucketsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Bucket page.
 */
export const zListBucketsResponse = zBucketList;

export const zCreateBucketData = z.object({
    body: zBucketWritable,
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created; the bucket.create task is running.
 */
export const zCreateBucketResponse = zBucket;

export const zDeleteBucketData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid(),
        bucket: z.uuid()
    }),
    query: z.object({
        dryRun: z.optional(z.boolean()).default(false),
        deleteObjects: z.boolean()
    })
});

export const zGetBucketData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid(),
        bucket: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The bucket.
 */
export const zGetBucketResponse = zBucket;

export const zPatchBucketData = z.object({
    body: zBucketWritable,
    path: z.object({
        objectStore: z.uuid(),
        bucket: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated. `X-TVStor-Task` present exactly when an engine-visible leaf changed (the bucket.apply convergence task, GAP-5 2026-08-06).
 *
 */
export const zPatchBucketResponse = zBucket;

export const zListS3AccountsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * S3 account page.
 */
export const zListS3AccountsResponse = zS3AccountList;

export const zCreateS3AccountData = z.object({
    body: zS3AccountWritable,
    path: z.object({
        objectStore: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Created — the secret half of the pair exists ONLY in this response body; the s3account.create task is propagating it to RGW.
 *
 */
export const zCreateS3AccountResponse = zS3AccountWithSecret;

export const zDeleteS3AccountData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid(),
        s3Account: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetS3AccountData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        objectStore: z.uuid(),
        s3Account: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The S3 account.
 */
export const zGetS3AccountResponse = zS3Account;

export const zRotateS3AccountData = z.object({
    body: zS3AccountRotateRequest,
    path: z.object({
        objectStore: z.uuid(),
        s3Account: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string()),
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Rotated — the new secret exists ONLY in this response body; the s3account.rotate task is propagating it to RGW.
 *
 */
export const zRotateS3AccountResponse = zS3AccountWithSecret;

export const zListCredentialsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Credential page — secrets never ride the render.
 */
export const zListCredentialsResponse = zCredentialList;

export const zCreateCredentialData = z.object({
    body: zCredentialWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Created — the secret never rides the render.
 */
export const zCreateCredentialResponse = zCredential;

export const zDeleteCredentialData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        credential: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteCredentialResponse = z.void();

export const zGetCredentialData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        credential: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The credential — the secret never rides the render.
 */
export const zGetCredentialResponse = zCredential;

export const zPatchCredentialData = z.object({
    body: zCredentialWritable,
    path: z.object({
        credential: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated — the secret never rides the render.
 */
export const zPatchCredentialResponse = zCredential;

export const zRotateCredentialData = z.object({
    body: zCredentialRotateRequestWritable,
    path: z.object({
        credential: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string()),
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Rotated — the secret never rides the render; `X-TVStor-Task` present when a consumer re-apply was scheduled.
 *
 */
export const zRotateCredentialResponse = zCredential;

export const zRotateMachineAccountNowData = z.object({
    body: zMachineAccountRotationRequest,
    path: z.object({
        credential: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string()),
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * The request is recorded; the elected rotator performs it.
 */
export const zRotateMachineAccountNowResponse = zMachineAccountRotationDispatch;

export const zRevokeCredentialSessionsData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        credential: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Revoked.
 */
export const zRevokeCredentialSessionsResponse = z.object({
    revokedSessions: z.optional(z.int()),
    revokedEditorTokens: z.optional(z.int())
});

export const zExportRecoveryKeysData = z.object({
    body: zRecoveryKeysExportRequest,
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The recovery material — shown ONCE; the caller owns getting it into the safe.
 *
 */
export const zExportRecoveryKeysResponse = zRecoveryKeysExport;

export const zListBackupTargetsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Backup target page.
 */
export const zListBackupTargetsResponse = zBackupTargetList;

export const zCreateBackupTargetData = z.object({
    body: zBackupTargetWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateBackupTargetResponse = zBackupTarget;

export const zDeleteBackupTargetData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backupTarget: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteBackupTargetResponse = z.void();

export const zGetBackupTargetData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backupTarget: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The backup target.
 */
export const zGetBackupTargetResponse = zBackupTarget;

export const zPatchBackupTargetData = z.object({
    body: zBackupTargetWritable,
    path: z.object({
        backupTarget: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchBackupTargetResponse = zBackupTarget;

export const zListBackupsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Backup page.
 */
export const zListBackupsResponse = zBackupList;

export const zCreateBackupData = z.object({
    body: zBackupWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Backup admitted (`phase: pending`) — the row rides the body, `Location` points at it, `X-TVStor-Task` at the `backup.create` task.
 *
 */
export const zCreateBackupResponse = zBackup;

export const zDeleteBackupData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

export const zGetBackupData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The backup.
 */
export const zGetBackupResponse = zBackup;

export const zRestoreBackupData = z.object({
    body: zBackupRestoreRequest,
    path: z.object({
        backup: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zVerifyBackupTimestampData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zArchiveBackupData = z.object({
    body: zBackupArchiveRequest,
    path: z.object({
        backup: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The copy is born `pending`; the task reference rides `X-TVStor-Task`.
 */
export const zArchiveBackupResponse = zBackupCopy;

export const zScrubBackupData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid()
    }),
    query: z.optional(z.never())
});

export const zListBackupCopiesData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The copies.
 */
export const zListBackupCopiesResponse = zBackupCopyList;

export const zGetBackupCopyData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid(),
        copy: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The copy.
 */
export const zGetBackupCopyResponse = zBackupCopy;

export const zEjectBackupCopyData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid(),
        copy: z.uuid()
    }),
    query: z.optional(z.never())
});

export const zScrubBackupCopyData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        backup: z.uuid(),
        copy: z.uuid()
    }),
    query: z.optional(z.never())
});

export const zForgetBackupCopyData = z.object({
    body: zBackupCopyForgetRequest,
    path: z.object({
        backup: z.uuid(),
        copy: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The copy, medium `lost`.
 */
export const zForgetBackupCopyResponse = zBackupCopy;

export const zListReplicationLinksData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Replication link page.
 */
export const zListReplicationLinksResponse = zReplicationLinkList;

export const zCreateReplicationLinkData = z.object({
    body: zReplicationLinkWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateReplicationLinkResponse = zReplicationLink;

export const zDeleteReplicationLinkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted; remote replicas remain.
 */
export const zDeleteReplicationLinkResponse = z.void();

export const zGetReplicationLinkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The replication link.
 */
export const zGetReplicationLinkResponse = zReplicationLink;

export const zPatchReplicationLinkData = z.object({
    body: zReplicationLinkWritable,
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchReplicationLinkResponse = zReplicationLink;

export const zSyncReplicationLinkNowData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zPromoteReplicationLinkData = z.object({
    body: zReplicationLinkPromoteRequest,
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zDemoteReplicationLinkData = z.object({
    body: zReplicationLinkDemoteRequest,
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Demoted; the link is consumed.
 */
export const zDemoteReplicationLinkResponse = zReplicationLinkDemoteResult;

export const zListSyncLinksData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Sync link page.
 */
export const zListSyncLinksResponse = zSyncLinkList;

export const zCreateSyncLinkData = z.object({
    body: zSyncLinkCreateRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateSyncLinkResponse = zSyncLink;

export const zDeleteSyncLinkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteSyncLinkResponse = z.void();

export const zGetSyncLinkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The sync link.
 */
export const zGetSyncLinkResponse = zSyncLink;

export const zPromoteSyncLinkData = z.object({
    body: zSyncLinkPromoteRequest,
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

export const zAdoptSyncLinkData = z.object({
    body: zSyncLinkAdoptRequest,
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zDemoteSyncLinkData = z.object({
    body: zSyncLinkDemoteRequest,
    path: z.object({
        link: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid()),
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Demoted; the registration is consumed.
 */
export const zDemoteSyncLinkResponse = zSyncLinkDemoteResult;

export const zGetMonitoringOverviewData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The overview snapshot.
 */
export const zGetMonitoringOverviewResponse = zMonitoringOverview;

export const zGetMonitoringTrendsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.object({
        targetKind: z.enum([
            'node',
            'cephcluster',
            'multiscsicluster',
            'volume',
            'share',
            'store',
            'objectstore',
            'backuptarget'
        ]),
        targetId: z.optional(z.string()),
        metric: z.enum([
            'node.cpuLoad1',
            'node.memUsedPercent',
            'node.fsUsedPercent',
            'node.netRxBytesPerSec',
            'node.netTxBytesPerSec',
            'node.netErrors',
            'node.netDrops',
            'node.replicaTunnelPeersDown',
            'node.configReplicaAgeSeconds',
            'node.crlAgeSeconds',
            'node.machineAccountDivergedSeconds',
            'node.daemonRestarts',
            'node.cephDaemonsMute',
            'node.auditWriteFailures',
            'node.replicationSpoolPercent',
            'node.sanPathsDown',
            'node.diskWearMaxPercent',
            'node.diskMediaErrors',
            'node.maintenanceHours',
            'cephcluster.capacityGiB',
            'cephcluster.usedGiB',
            'cephcluster.usedPercent',
            'cephcluster.daysToFull',
            'cephcluster.osdsDown',
            'cephcluster.pgsInactive',
            'multiscsicluster.capacityGiB',
            'multiscsicluster.usedGiB',
            'multiscsicluster.usedPercent',
            'multiscsicluster.daysToFull',
            'multiscsicluster.missingPvs',
            'multiscsicluster.scrubMismatches',
            'multiscsicluster.scrubAgeDays',
            'volume.health',
            'volume.backupAgeHours',
            'volume.replicaLagSeconds',
            'volume.syncDegraded',
            'volume.resyncPercent',
            'volume.readBytesPerSec',
            'volume.writeBytesPerSec',
            'volume.readIops',
            'volume.writeIops',
            'volume.ioAwaitMs',
            'share.health',
            'share.clients',
            'share.fsUsedPercent',
            'share.daysToFull',
            'share.daemonsNotServing',
            'share.daemonRestarts',
            'share.smbLeaseUnrenewedSeconds',
            'share.opsPerSec',
            'share.bytesPerSec',
            'share.deleteOps',
            'share.authFailures',
            'share.accessDenied',
            'store.health',
            'store.lagSeconds',
            'objectstore.health',
            'objectstore.requestsPerSec',
            'objectstore.bytesPerSec',
            'objectstore.deleteOps',
            'objectstore.authFailures',
            'objectstore.accessDenied',
            'backuptarget.timestampUnreachable',
            'backuptarget.timestampRefused',
            'backuptarget.timestampUnverifiable',
            'backuptarget.timestampInvalid',
            'backuptarget.timestampTampered',
            'backuptarget.timestampAgeDays'
        ]),
        range: z.optional(z.enum([
            '1h',
            '6h',
            '24h',
            '7d',
            '30d',
            '90d'
        ])),
        step: z.optional(z.int().gte(300))
    })
});

/**
 * The series (possibly empty).
 */
export const zGetMonitoringTrendsResponse = zMonitoringTrendSeries;

export const zListAlertRulesData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Alert rule page.
 */
export const zListAlertRulesResponse = zAlertRuleList;

export const zCreateAlertRuleData = z.object({
    body: zAlertRuleWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Created.
 */
export const zCreateAlertRuleResponse = zAlertRule;

export const zDeleteAlertRuleData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alertRule: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Deleted.
 */
export const zDeleteAlertRuleResponse = z.void();

export const zGetAlertRuleData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alertRule: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The alert rule.
 */
export const zGetAlertRuleResponse = zAlertRule;

export const zPatchAlertRuleData = z.object({
    body: zAlertRuleWritable,
    path: z.object({
        alertRule: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Updated.
 */
export const zPatchAlertRuleResponse = zAlertRule;

export const zListAlertSinksData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Alert sink page.
 */
export const zListAlertSinksResponse = zAlertSinkList;

export const zCreateAlertSinkData = z.object({
    body: zAlertSinkWritable,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Created.
 */
export const zCreateAlertSinkResponse = zAlertSink;

export const zDeleteAlertSinkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alertSink: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Deleted.
 */
export const zDeleteAlertSinkResponse = z.void();

export const zGetAlertSinkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alertSink: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The alert sink.
 */
export const zGetAlertSinkResponse = zAlertSink;

export const zPatchAlertSinkData = z.object({
    body: zAlertSinkWritable,
    path: z.object({
        alertSink: z.uuid()
    }),
    query: z.optional(z.object({
        dryRun: z.optional(z.boolean()).default(false)
    })),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Updated.
 */
export const zPatchAlertSinkResponse = zAlertSink;

export const zTestAlertSinkData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alertSink: z.uuid()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

export const zRevokeMetricsTokenData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * Revoked (or nothing to revoke).
 */
export const zRevokeMetricsTokenResponse = z.void();

export const zIssueMetricsTokenData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'X-TVStor-Sudo': z.optional(z.string())
    }))
});

/**
 * The new token, shown once.
 */
export const zIssueMetricsTokenResponse = zMetricsToken;

export const zExportMetricsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.never())
});

/**
 * The exposition.
 */
export const zExportMetricsResponse = z.string();

export const zListAlertsData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Alert page.
 */
export const zListAlertsResponse = zAlertList;

export const zGetAlertData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alert: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * The alert.
 */
export const zGetAlertResponse = zAlert;

export const zAckAlertData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alert: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * Acknowledged — the updated alert.
 */
export const zAckAlertResponse = zAlert;

export const zResolveAlertData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        alert: z.uuid()
    }),
    query: z.optional(z.never())
});

/**
 * Resolved — the updated alert.
 */
export const zResolveAlertResponse = zAlert;

export const zListTasksData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(500)).default(100),
        cursor: z.optional(z.string()),
        scope: z.optional(z.string()).default('/*'),
        filter: z.optional(z.array(z.string())),
        sort: z.optional(z.string())
    }))
});

/**
 * Task page.
 */
export const zListTasksResponse = zTaskList;

export const zGetTaskData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        task: z.string()
    }),
    query: z.optional(z.never())
});

/**
 * The task.
 */
export const zGetTaskResponse = zTask;

export const zGetTaskLogData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        task: z.string()
    }),
    query: z.optional(z.object({
        follow: z.optional(z.boolean()).default(false)
    }))
});

/**
 * Structured log frames.
 */
export const zGetTaskLogResponse = z.string();

export const zCancelTaskData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        task: z.string()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Cancellation requested.
 */
export const zCancelTaskResponse = zTask;

export const zResumeTaskData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        task: z.string()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Resumed.
 */
export const zResumeTaskResponse = zTask;

export const zAbortTaskData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        task: z.string()
    }),
    query: z.optional(z.never()),
    headers: z.optional(z.object({
        'Idempotency-Key': z.optional(z.uuid())
    }))
});

/**
 * Abort started; the response enumerates what is undone vs merely reported.
 */
export const zAbortTaskResponse = zAbortResult;
